import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useProject } from '../../contexts/ProjectContext';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Search,
  Monitor,
  Youtube,
  ShoppingBag,
  Image,
  Film,
  Layers,
  Eye,
  MousePointer,
  MessageSquare,
  MessageCircle,
  Users,
  ShoppingCart,
  Target,
  Crosshair,
  DollarSign,
  Calendar,
  MapPin,
  Pin,
  Sparkles,
  Loader2,
  CheckCircle,
  ChevronRight,
  X,
  Info,
  Plus,
  Trash2,
  Upload,
  Link,
  Type,
  FileText,
  Play,
  ExternalLink,
  AlertCircle,
  FolderOpen,
  Save,
  Wand2,
  Globe,
  UserPlus,
  TrendingUp,
  Edit2,
  RefreshCw,
  Building2,
  Instagram,
  Settings as SettingsIcon
} from 'lucide-react';
import { campaignService, searchLocations, PlatformPixels, PlatformPixel, estimateMetaAudience, MetaAudienceEstimate } from '../../services/campaignService';
import audienceService, { Audience, LandingPageOption } from '../../services/audienceService';
import api from '../../services/api';
import { isHandledByInterceptor } from '../../utils/apiError';
import { pollJobUntilComplete } from '../../services/aiJobService';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import CreativeLibraryModal from '../../components/campaigns/CreativeLibraryModal';
import LocationMap from '../../components/campaigns/LocationMap';
import MetaInterestsInput from '../../components/campaigns/MetaInterestsInput';
import LinkedInTargetingInput, { LinkedInTargeting } from '../../components/campaigns/LinkedInTargetingInput';
import DeviceTargetingInput, { DevicesTargeting, DEFAULT_DEVICES } from '../../components/campaigns/DeviceTargetingInput';
import AdScheduleInput, { AdScheduleConfig, DEFAULT_AD_SCHEDULE } from '../../components/campaigns/AdScheduleInput';
import { creativeService, Creative as LibraryCreative } from '../../services/creativeService';
import ImageEditorModal from '../../components/image-studio/ImageEditorModal';
import { imageStudioService } from '../../services/imageStudioService';
import socialService from '../../services/socialService';
import onboardingService from '../../services/onboardingService';
import MediaPicker from '../../components/shared/MediaPicker';
import GoogleLanguagesInput, { DEFAULT_GOOGLE_LANGUAGES, useGoogleLanguageLabel } from '../../components/campaigns/rmf/wizard/GoogleLanguagesInput';
import GooglePortfolioStrategyPicker from '../../components/campaigns/rmf/wizard/GooglePortfolioStrategyPicker';
import AiCostBadge from '../../components/ai/AiCostBadge';

// Platform icons
const GoogleAdsIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" fill="#4285F4"/>
  </svg>
);

const MetaAdsIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <img src="/logo_meta.png" alt="Meta" className={className} />
);

const LinkedInAdsIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/>
  </svg>
);

// Channel type for type-safety
type IconComponent = React.ComponentType<{ className?: string }>;

interface Channel {
  id: string;
  name: string;
  description: string;
  icon: IconComponent;
  platform: string;
}

const FacebookIcon: IconComponent = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon: IconComponent = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

// Google Channels - descriptions use translation keys via getChannelDescription
const GOOGLE_CHANNELS: Channel[] = [
  { id: 'google_search', name: 'Google Search', description: 'campaigns.channels.googleSearch', icon: Search, platform: 'google' },
  { id: 'google_display', name: 'Google Display', description: 'campaigns.channels.googleDisplay', icon: Monitor, platform: 'google' },
  { id: 'youtube', name: 'YouTube', description: 'campaigns.channels.youtube', icon: Youtube, platform: 'google' },
  { id: 'google_shopping', name: 'Google Shopping', description: 'campaigns.channels.googleShopping', icon: ShoppingBag, platform: 'google' },
];

// Só a Pesquisa do Google funciona de ponta a ponta (RMF). Display, YouTube e Shopping
// ficam "em breve" em campanhas novas; campanhas já existentes com eles seguem editáveis.
const GOOGLE_COMING_SOON_CHANNELS = new Set(['google_display', 'youtube', 'google_shopping']);

// Meta Channels
const META_CHANNELS: Channel[] = [
  { id: 'facebook_feed', name: 'Facebook Feed', description: 'campaigns.channels.facebookFeed', icon: FacebookIcon, platform: 'meta' },
  { id: 'facebook_stories', name: 'Facebook Stories', description: 'campaigns.channels.facebookStories', icon: Layers, platform: 'meta' },
  { id: 'instagram_feed', name: 'Instagram Feed', description: 'campaigns.channels.instagramFeed', icon: InstagramIcon, platform: 'meta' },
  { id: 'instagram_stories', name: 'Instagram Stories', description: 'campaigns.channels.instagramStories', icon: Layers, platform: 'meta' },
  { id: 'instagram_reels', name: 'Instagram Reels', description: 'campaigns.channels.instagramReels', icon: Film, platform: 'meta' },
];

// LinkedIn Channels
const LINKEDIN_CHANNELS: Channel[] = [
  { id: 'linkedin_sponsored_content', name: 'Sponsored Content', description: 'campaigns.channels.linkedinSponsored', icon: FileText, platform: 'linkedin' },
  { id: 'linkedin_inmail', name: 'Sponsored InMail', description: 'campaigns.channels.linkedinInmail', icon: MessageSquare, platform: 'linkedin' },
  { id: 'linkedin_text_ads', name: 'Text Ads', description: 'campaigns.channels.linkedinTextAds', icon: Type, platform: 'linkedin' },
];

// ===========================================
// PLATFORM SPECIFICATIONS
// ===========================================

interface PlatformSpec {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  headlines: { min: number; max: number; charLimit: number; label: string };
  descriptions: { min: number; max: number; charLimit: number; label: string };
  image: { required: boolean; recommendedSize: string; formats: string[] };
  video: { supported: boolean; recommendedSize?: string };
  ctas: { value: string; label: string }[];
  keywords: { supported: boolean; required: boolean };
  /** Recommended/max number of ads per campaign (per ad group for Google, per ad set for Meta). */
  maxAdsPerCampaign: number;
  extraFields?: string[];
}

const PLATFORM_SPECS: Record<string, PlatformSpec> = {
  google: {
    name: 'Google Ads',
    icon: GoogleAdsIcon,
    headlines: { min: 3, max: 15, charLimit: 30, label: 'campaigns.new.labels.headlines' },
    descriptions: { min: 2, max: 4, charLimit: 90, label: 'campaigns.new.labels.descriptions' },
    image: { required: false, recommendedSize: '1200x628', formats: ['jpg', 'png', 'gif'] },
    video: { supported: true, recommendedSize: '1920x1080' },
    ctas: [
      { value: 'learn_more', label: 'campaigns.cta.learnMore' },
      { value: 'shop_now', label: 'campaigns.cta.shopNow' },
      { value: 'sign_up', label: 'campaigns.cta.signUp' },
      { value: 'contact_us', label: 'campaigns.cta.contactUs' },
      { value: 'get_quote', label: 'campaigns.cta.getQuote' },
      { value: 'apply_now', label: 'campaigns.cta.applyNow' },
      { value: 'book_now', label: 'campaigns.cta.bookNow' },
      { value: 'download', label: 'campaigns.cta.download' },
    ],
    keywords: { supported: true, required: false },
    maxAdsPerCampaign: 50, // Hard limit da Google API. Google recomenda 3 RSAs/ad group pra ML, mas é só recomendação.
    extraFields: ['display_url', 'path1', 'path2'],
  },
  meta: {
    name: 'Meta Ads',
    icon: MetaAdsIcon,
    headlines: { min: 1, max: 5, charLimit: 40, label: 'campaigns.new.labels.headlines' },
    descriptions: { min: 1, max: 1, charLimit: 125, label: 'campaigns.new.labels.primaryText' },
    image: { required: true, recommendedSize: '1080x1080 or 1200x628', formats: ['jpg', 'png'] },
    video: { supported: true, recommendedSize: '1080x1080 or 9:16' },
    ctas: [
      { value: 'LEARN_MORE', label: 'campaigns.cta.learnMore' },
      { value: 'SHOP_NOW', label: 'campaigns.cta.shopNow' },
      { value: 'SIGN_UP', label: 'campaigns.cta.signUp' },
      { value: 'CONTACT_US', label: 'campaigns.cta.contactUs' },
      { value: 'GET_OFFER', label: 'campaigns.cta.getOffer' },
      { value: 'BOOK_TRAVEL', label: 'campaigns.cta.bookNow' },
      { value: 'DOWNLOAD', label: 'campaigns.cta.download' },
      { value: 'WATCH_MORE', label: 'campaigns.cta.watchMore' },
      { value: 'SUBSCRIBE', label: 'campaigns.cta.subscribe' },
      { value: 'GET_QUOTE', label: 'campaigns.cta.getQuote' },
    ],
    keywords: { supported: false, required: false },
    maxAdsPerCampaign: 6, // Meta recommends up to 6 ads per ad set
  },
  linkedin: {
    name: 'LinkedIn Ads',
    icon: LinkedInAdsIcon,
    headlines: { min: 1, max: 1, charLimit: 70, label: 'campaigns.new.labels.headline' },
    descriptions: { min: 1, max: 1, charLimit: 600, label: 'campaigns.new.labels.introText' },
    image: { required: true, recommendedSize: '1200x627', formats: ['jpg', 'png'] },
    video: { supported: true, recommendedSize: '1920x1080' },
    ctas: [
      { value: 'LEARN_MORE', label: 'campaigns.cta.learnMore' },
      { value: 'SIGN_UP', label: 'campaigns.cta.signUp' },
      { value: 'SUBSCRIBE', label: 'campaigns.cta.subscribe' },
      { value: 'REGISTER', label: 'campaigns.cta.register' },
      { value: 'DOWNLOAD', label: 'campaigns.cta.download' },
      { value: 'GET_QUOTE', label: 'campaigns.cta.getQuote' },
      { value: 'APPLY_NOW', label: 'campaigns.cta.applyNow' },
      { value: 'REQUEST_DEMO', label: 'campaigns.cta.requestDemo' },
      { value: 'CONTACT_US', label: 'campaigns.cta.contactUs' },
    ],
    keywords: { supported: false, required: false },
    maxAdsPerCampaign: 10, // LinkedIn practical limit (API allows up to 100)
  },
};

// Platform-specific creative interface
interface PlatformCreative {
  id: string;
  platform: string;
  // Meta only — the Facebook page that will publish this ad. Each ad picks its
  // own page, which matches Meta's own structure (page_id is on the creative,
  // not the ad set or campaign).
  metaPageId?: string;
  // Meta only — the Instagram business account (instagram_actor_id) that will
  // represent the ad on Instagram placements. Defaults to the IG linked to the
  // selected FB page but can be overridden.
  metaInstagramId?: string;
  // LinkedIn only — the organization URN that will publish this ad.
  linkedinOrgUrn?: string;
  headlines: string[];
  descriptions: string[];
  /**
   * Google Ads only — parallel array to `headlines`. For each headline, either
   * null (unpinned, rotates freely) or a pin position 1/2/3 (HEADLINE_1..3).
   * Google Ads RSAs allow each headline to be pinned to a specific slot or
   * left unpinned. Undefined means "not yet configured".
   */
  headlinePins?: Array<1 | 2 | 3 | null>;
  imageUrl: string | null;
  imageFile: File | null;
  videoUrl: string;
  ctaText: string;
  destinationUrl: string;
  displayUrl?: string; // Google only
  keywords?: string[]; // Google only
  // Google only — identifica em qual grupo de anúncios local este criativo entra.
  // Referencia GoogleAdGroup.id (UUID local). Backend usa pra distribuir os ads
  // entre múltiplos ad groups (Google limita 3 RSAs ENABLED por ad group).
  adGroupId?: string;
  // Meta only — chave do grupo de anúncios (ad set). Criativos com a mesma chave
  // vão pro mesmo ad set. Default '1'. Vai pro backend em ad.settings.adsetGroup.
  adsetGroup?: string;
  isGeneratingImage: boolean;
  imagePrompt: string;
  imageStyle: string;
}

// Google Ads ad group — agrupa criativos que compartilham keywords/segmento.
// Limite Google: 3 RSAs ENABLED por ad group. Quando o user adiciona um 4º ad,
// a UI exige escolher (ou criar) outro grupo.
interface GoogleAdGroup {
  id: string;       // UUID local, persistido em campaign.settings.google_ad_groups
  name: string;
  externalId?: string; // preenchido depois do push pro Google (composite path)
}

const createEmptyAdGroup = (label?: string): GoogleAdGroup => ({
  id: `ag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  name: label || 'Grupo de Anúncios 1',
});

const createEmptyPlatformCreative = (platform: string): PlatformCreative => {
  const spec = PLATFORM_SPECS[platform];
  const headlineCount = spec?.headlines.min || 1;
  return {
    id: `creative-${platform}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    platform,
    headlines: Array(headlineCount).fill(''),
    descriptions: Array(spec?.descriptions.min || 1).fill(''),
    // Google: start with no pins (everything free to rotate). User can opt-in to pin.
    headlinePins: platform === 'google' ? Array(headlineCount).fill(null) : undefined,
    imageUrl: null,
    imageFile: null,
    videoUrl: '',
    ctaText: spec?.ctas[0]?.value || 'learn_more',
    destinationUrl: '',
    displayUrl: '',
    keywords: [],
    isGeneratingImage: false,
    imagePrompt: '',
    imageStyle: 'realistic',
  };
};

// Campaign types per platform (kept for backwards compatibility) - uses translation keys
const GOOGLE_CAMPAIGN_TYPES = [
  { id: 'search', name: 'Search', description: 'campaigns.types.searchDesc', icon: Search },
  { id: 'display', name: 'Display', description: 'campaigns.types.displayDesc', icon: Monitor },
  { id: 'video', name: 'Video', description: 'campaigns.types.videoDesc', icon: Youtube },
  { id: 'shopping', name: 'Shopping', description: 'campaigns.types.shoppingDesc', icon: ShoppingBag },
];

const META_CAMPAIGN_TYPES = [
  { id: 'feed', name: 'Feed', description: 'campaigns.types.feedDesc', icon: Image },
  { id: 'stories', name: 'Stories', description: 'campaigns.types.storiesDesc', icon: Layers },
  { id: 'reels', name: 'Reels', description: 'campaigns.types.reelsDesc', icon: Film },
  { id: 'carousel', name: 'Carousel', description: 'campaigns.types.carouselDesc', icon: Layers },
];

// Campaign objectives - uses translation keys
const CAMPAIGN_OBJECTIVES = [
  { id: 'awareness', name: 'campaigns.objectives.awareness', description: 'campaigns.objectives.awarenessDesc', icon: Eye, color: 'blue' },
  { id: 'traffic', name: 'campaigns.objectives.traffic', description: 'campaigns.objectives.trafficDesc', icon: MousePointer, color: 'green' },
  { id: 'engagement', name: 'campaigns.objectives.engagement', description: 'campaigns.objectives.engagementDesc', icon: MessageSquare, color: 'purple' },
  { id: 'leads', name: 'campaigns.objectives.leads', description: 'campaigns.objectives.leadsDesc', icon: Users, color: 'orange' },
  { id: 'sales', name: 'campaigns.objectives.sales', description: 'campaigns.objectives.salesDesc', icon: ShoppingCart, color: 'red' },
  { id: 'remarketing', name: 'campaigns.objectives.remarketing', description: 'campaigns.objectives.remarketingDesc', icon: Target, color: 'indigo' },
];

// Channel-specific specs for validation (used in preview)
const CHANNEL_SPECS = {
  google_search: {
    headlines: { min: 3, max: 15, charLimit: 30 },
    descriptions: { min: 2, max: 4, charLimit: 90 },
    supportedMedia: ['none'],
  },
  google_display: {
    headlines: { min: 1, max: 5, charLimit: 30 },
    descriptions: { min: 1, max: 5, charLimit: 90 },
    supportedMedia: ['image'],
    imageSpecs: { minWidth: 300, minHeight: 250, aspectRatios: ['1.91:1', '1:1', '4:5'] },
  },
  youtube: {
    headlines: { min: 1, max: 2, charLimit: 40 },
    descriptions: { min: 1, max: 2, charLimit: 70 },
    supportedMedia: ['video'],
    videoSpecs: { minDuration: 6, maxDuration: 180, formats: ['16:9'] },
  },
  google_shopping: {
    headlines: { min: 1, max: 1, charLimit: 150 },
    descriptions: { min: 1, max: 1, charLimit: 5000 },
    supportedMedia: ['image'],
    imageSpecs: { minWidth: 100, minHeight: 100, aspectRatios: ['1:1'] },
  },
  facebook_feed: {
    headlines: { min: 1, max: 5, charLimit: 40 },
    descriptions: { min: 1, max: 5, charLimit: 125 },
    supportedMedia: ['image', 'video'],
    imageSpecs: { minWidth: 1080, minHeight: 1080, aspectRatios: ['1:1', '4:5', '16:9'] },
  },
  facebook_stories: {
    headlines: { min: 1, max: 1, charLimit: 40 },
    descriptions: { min: 0, max: 1, charLimit: 125 },
    supportedMedia: ['image', 'video'],
    imageSpecs: { minWidth: 1080, minHeight: 1920, aspectRatios: ['9:16'] },
  },
  instagram_feed: {
    headlines: { min: 1, max: 5, charLimit: 40 },
    descriptions: { min: 1, max: 1, charLimit: 2200 },
    supportedMedia: ['image', 'video'],
    imageSpecs: { minWidth: 1080, minHeight: 1080, aspectRatios: ['1:1', '4:5'] },
  },
  instagram_stories: {
    headlines: { min: 1, max: 1, charLimit: 40 },
    descriptions: { min: 0, max: 1, charLimit: 125 },
    supportedMedia: ['image', 'video'],
    imageSpecs: { minWidth: 1080, minHeight: 1920, aspectRatios: ['9:16'] },
  },
  instagram_reels: {
    headlines: { min: 1, max: 1, charLimit: 40 },
    descriptions: { min: 1, max: 1, charLimit: 2200 },
    supportedMedia: ['video'],
    videoSpecs: { minDuration: 3, maxDuration: 900, formats: ['9:16'] },
  },
  linkedin_sponsored_content: {
    headlines: { min: 1, max: 3, charLimit: 100 },
    descriptions: { min: 1, max: 1, charLimit: 300 },
    supportedMedia: ['image', 'video'],
    imageSpecs: { minWidth: 1200, minHeight: 627, aspectRatios: ['1.91:1', '1:1'] },
  },
  linkedin_inmail: {
    headlines: { min: 1, max: 1, charLimit: 100 },
    descriptions: { min: 1, max: 1, charLimit: 200 },
    supportedMedia: ['none'],
  },
  linkedin_text_ads: {
    headlines: { min: 1, max: 1, charLimit: 50 },
    descriptions: { min: 1, max: 1, charLimit: 120 },
    supportedMedia: ['none'],
  },
};

// CTA options - uses translation keys
const CTA_OPTIONS = [
  { id: 'learn_more', label: 'campaigns.cta.learnMore' },
  { id: 'shop_now', label: 'campaigns.cta.shopNow' },
  { id: 'sign_up', label: 'campaigns.cta.signUp' },
  { id: 'contact_us', label: 'campaigns.cta.contactUs' },
  { id: 'download', label: 'campaigns.cta.download' },
  { id: 'get_quote', label: 'campaigns.cta.getQuote' },
  { id: 'book_now', label: 'campaigns.cta.bookNow' },
  { id: 'get_offer', label: 'campaigns.cta.getOffer' },
  { id: 'watch_more', label: 'campaigns.cta.watchMore' },
  { id: 'apply_now', label: 'campaigns.cta.applyNow' },
];

// Steps - uses translation keys
const STEPS = [
  { id: 'platform', title: 'campaigns.steps.platforms', description: 'campaigns.steps.platformsDesc' },
  { id: 'channels', title: 'campaigns.steps.channels', description: 'campaigns.steps.channelsDesc' },
  { id: 'objective', title: 'campaigns.steps.objective', description: 'campaigns.steps.objectiveDesc' },
  { id: 'details', title: 'campaigns.steps.details', description: 'campaigns.steps.detailsDesc' },
  { id: 'budget', title: 'campaigns.steps.budget', description: 'campaigns.steps.budgetDesc' },
  { id: 'pixel', title: 'campaigns.steps.pixel', description: 'campaigns.steps.pixelDesc' },
  { id: 'audience', title: 'campaigns.steps.audience', description: 'campaigns.steps.audienceDesc' },
  { id: 'creatives', title: 'campaigns.steps.creatives', description: 'campaigns.steps.creativesDesc' },
  { id: 'review', title: 'campaigns.steps.review', description: 'campaigns.steps.reviewDesc' },
];

// Image style options for AI generation (must match backend aiService styles)
const IMAGE_STYLE_OPTIONS = [
  { id: 'realistic', label: 'campaigns.imageStyles.realistic' },
  { id: 'illustration', label: 'campaigns.imageStyles.illustration' },
  { id: 'minimalist', label: 'campaigns.imageStyles.minimalist' },
  { id: '3d', label: 'campaigns.imageStyles.3d' },
  { id: 'corporate', label: 'campaigns.imageStyles.corporate' },
  { id: 'creative', label: 'campaigns.imageStyles.creative' },
];

// Creative interface
interface Creative {
  id: string;
  headlines: string[];
  descriptions: string[];
  imageUrl: string | null;
  imageFile: File | null;
  videoUrl: string;
  ctaText: string;
  destinationUrl: string;
  isGeneratingImage: boolean;
  imagePrompt: string;
  imageStyle: string;
  metaPageId?: string;
  metaInstagramId?: string;
  linkedinOrgUrn?: string;
}

interface LocationWithRadius {
  name: string;
  canonicalName?: string;
  geoTargetId?: string;
  targetType?: string;
  countryCode?: string;
  lat?: number;
  lng?: number;
  radius: number; // 0 = entire area
}

interface CampaignData {
  platforms: string[];
  channels: string[];
  type: string;
  objective: string;
  name: string;
  description: string;
  budget_type: 'daily' | 'lifetime';
  budget_amount: number;
  currency: string;
  start_date: string;
  end_date: string;
  bid_strategy: 'automatic' | 'manual_cpc' | 'target_cpa';
  // Google only (RMF C.96/C.97/C.98). bid_strategy continua 'automatic' nesses modos
  // (ENUM do banco); a escolha real vai em settings.google_bid_strategy / google_portfolio_strategy.
  google_bid_mode?: 'target_roas' | 'portfolio' | null;
  google_target_roas_pct?: number; // ROAS alvo em % (400 = 400% → settings.target_roas = 4)
  google_portfolio_strategy?: string; // resource_name da estratégia de portfólio
  google_portfolio_strategy_name?: string;
  google_ai_max?: boolean;
  max_cpc?: number;
  target_cpa?: number;
  audience: {
    age_min: number;
    age_max: number;
    genders: string[];
    locations: LocationWithRadius[];
    // Mix de {id,name} (novo) e string (legacy de campanhas salvas antes)
    interests: Array<{ id: string; name: string } | string>;
    // LinkedIn B2B facets (industries, jobTitles, seniorities, skills, companies, etc).
    // Cada valor é { urn, name } — URN vai pro targetingCriteria da LinkedIn Marketing API.
    linkedin?: LinkedInTargeting;
    // Dispositivos onde veicular (opcional — se omitido, usa default: todos ativos sem ajuste).
    // Google aceita bidAdjustment por device; Meta/LinkedIn só usam o enabled.
    devices?: DevicesTargeting;
    // Google Ads languageConstant ids (RMF C.30), ex.: ['1014'] = Português.
    languages?: string[];
  };
  creatives: Creative[]; // Legacy - keeping for backwards compatibility
  platformCreatives: Record<string, PlatformCreative[]>; // New - array of creatives per platform (multi-ad)
  ad_set_name: string;           // Meta Ads: name for the Ad Set
  meta_lead_destination?: 'instant_form' | 'messenger' | 'app' | 'whatsapp' | 'phone_call' | 'website';  // Meta Leads destination
  meta_lead_dest_config?: {
    // Per-destination config — only the field relevant to the chosen destination is used.
    leadform_id?: string;      // instant_form: existing Meta lead-gen form id
    welcome_message?: string;  // messenger/whatsapp: first message customer sees
    app_id?: string;           // app: Meta App ID
    app_deep_link?: string;    // app: deep link URL
    phone_number?: string;     // whatsapp/phone_call: E.164 phone (+5511999999999)
  };
  campaign_group_name: string;   // LinkedIn Ads: name for the Campaign Group
  googleAssets: GoogleAssets;    // Google Ads: extensions (callouts, structured snippets, sitelinks)
  googleAdGroups: GoogleAdGroup[]; // Google Ads: lista de ad groups locais (UUID local + nome)
  // Meta only — Campaign Budget Optimization. Quando true, o orçamento fica na
  // campanha e a Meta distribui entre os grupos de anúncios (ad sets).
  metaCbo?: boolean;
}

// Google Ads asset extensions (campaign-level, Google only)
interface GoogleStructuredSnippet {
  header: string;  // e.g. "Serviços", "Tipos", "Marcas"
  values: string[]; // 3–10 items, each up to 25 chars
}

interface GoogleSitelink {
  title: string;       // 25 chars max
  url: string;
  desc1?: string;      // 35 chars max
  desc2?: string;      // 35 chars max
}

// Extensão de mensagem do Google Ads (click-to-WhatsApp).
// O Google pede país (ISO-2) e número NACIONAL separados, mensagem inicial
// (até 140 chars) e um CTA com descrição — os dois obrigatórios.
interface GoogleWhatsAppAsset {
  enabled: boolean;
  country_code: string;     // 'BR'
  phone_number: string;     // número nacional, sem DDI
  starter_message: string;  // até 140 chars
  cta: string;              // BusinessMessageCallToActionType
  cta_description: string;
  /** manda o clique do anúncio direto pro wa.me em vez do site */
  use_as_destination: boolean;
}

interface GoogleAssets {
  callouts: string[];                       // 2–20 items, each up to 25 chars
  structuredSnippets: GoogleStructuredSnippet[];
  sitelinks: GoogleSitelink[];
  whatsapp: GoogleWhatsAppAsset;
}

const GOOGLE_WHATSAPP_CTAS = [
  'CONTACT_US', 'GET_STARTED', 'LEARN_MORE', 'GET_INFO',
  'GET_QUOTE', 'GET_OFFER', 'BOOK_NOW', 'APPLY_NOW',
];

// Países com WhatsApp relevante para a base de clientes (DDI só para o preview
// do link — o Google recebe o ISO-2).
// `nat` = tamanho do número NACIONAL (com DDD). Usado para saber se o DDI foi
// colado no começo — no Brasil o DDD 55 faria "55 99991-2345" parecer +55.
// Precisa espelhar WHATSAPP_DIAL_BY_ISO/WHATSAPP_NATIONAL_LEN do backend.
const GOOGLE_WHATSAPP_COUNTRIES: { iso: string; dial: string; nat: [number, number] }[] = [
  { iso: 'BR', dial: '55', nat: [10, 11] }, { iso: 'PT', dial: '351', nat: [9, 9] },
  { iso: 'US', dial: '1', nat: [10, 10] }, { iso: 'AR', dial: '54', nat: [10, 11] },
  { iso: 'MX', dial: '52', nat: [10, 10] }, { iso: 'CL', dial: '56', nat: [9, 9] },
  { iso: 'CO', dial: '57', nat: [10, 10] }, { iso: 'ES', dial: '34', nat: [9, 9] },
  { iso: 'AO', dial: '244', nat: [9, 9] }, { iso: 'MZ', dial: '258', nat: [9, 9] },
];

const createEmptyGoogleWhatsApp = (): GoogleWhatsAppAsset => ({
  enabled: false,
  country_code: 'BR',
  phone_number: '',
  starter_message: '',
  cta: 'CONTACT_US',
  cta_description: '',
  use_as_destination: false,
});

const createEmptyGoogleAssets = (): GoogleAssets => ({
  callouts: [],
  structuredSnippets: [],
  sitelinks: [],
  whatsapp: createEmptyGoogleWhatsApp(),
});

// Google Ads structured snippet headers (official list, subset)
const STRUCTURED_SNIPPET_HEADERS = [
  'Amenities', 'Brands', 'Courses', 'Degree programs', 'Destinations',
  'Featured hotels', 'Insurance coverage', 'Models', 'Neighborhoods',
  'Service catalog', 'Shows', 'Styles', 'Types',
];

const createEmptyCreative = (): Creative => ({
  id: `creative-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  headlines: [''],
  descriptions: [''],
  imageUrl: null,
  imageFile: null,
  videoUrl: '',
  ctaText: 'learn_more',
  destinationUrl: '',
  isGeneratingImage: false,
  imagePrompt: '',
  imageStyle: 'realistic',
});

interface ConnectedAccount {
  id: string;
  platform: string;
  connected: boolean;
}

/**
 * Input that allows typing comma-separated values freely.
 * Only parses into an array on blur; keeps raw text while typing.
 */
const CommaSeparatedInput: React.FC<{
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
}> = ({ values, onChange, placeholder, className }) => {
  const [text, setText] = useState(values.join(', '));
  const internalChange = useRef(false);
  const valuesKey = values.join('\x00');
  const prevValuesKey = useRef(valuesKey);

  useEffect(() => {
    // Sync from external changes only (AI generation, loading campaign, etc.)
    if (prevValuesKey.current !== valuesKey && !internalChange.current) {
      setText(values.join(', '));
    }
    prevValuesKey.current = valuesKey;
    internalChange.current = false;
  }, [valuesKey, values]);

  return (
    <input
      type="text"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const parsed = text.split(',').map(s => s.trim()).filter(s => s);
        internalChange.current = true;
        onChange(parsed);
        setText(parsed.join(', '));
      }}
      placeholder={placeholder}
      className={className}
    />
  );
};

const NewCampaignPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: campaignId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { currentProject } = useProject();
  const isEditMode = !!campaignId;
  const googleLanguageLabel = useGoogleLanguageLabel();
  const [pushedPlatforms, setPushedPlatforms] = useState<string[]>([]);
  const isObjectiveLocked = pushedPlatforms.length > 0;

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiIgnorePattern, setAiIgnorePattern] = useState(false);
  const [aiVariationDirection, setAiVariationDirection] = useState('');
  const [campaignBrandProfile, setCampaignBrandProfile] = useState<any>(null);
  const [campaignBrandProfileLoaded, setCampaignBrandProfileLoaded] = useState(false);

  useEffect(() => {
    if (!showAIModal || campaignBrandProfileLoaded) return;
    let active = true;
    onboardingService.getProfile()
      .then(profile => {
        if (active) setCampaignBrandProfile(profile);
      })
      .catch(() => { /* noop */ })
      .finally(() => {
        if (active) setCampaignBrandProfileLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [showAIModal, campaignBrandProfileLoaded]);
  const [activeCreativeIndex, setActiveCreativeIndex] = useState(0);
  const [previewChannel, setPreviewChannel] = useState<string | null>(null);
  const [enteredFromPlatform, setEnteredFromPlatform] = useState(false);
  const [editingImagePlatform, setEditingImagePlatform] = useState<string | null>(null);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showImageGeneratorModal, setShowImageGeneratorModal] = useState(false);
  const [savingCreative, setSavingCreative] = useState(false);

  // Save-to-library modal state (asks for custom name + tags)
  const [saveModalPlatform, setSaveModalPlatform] = useState<string | null>(null);
  const [saveModalName, setSaveModalName] = useState('');
  const [saveModalTags, setSaveModalTags] = useState<string[]>([]);

  // Pixel selection state
  const [availablePixels, setAvailablePixels] = useState<PlatformPixels>({ meta: [], google: [], linkedin: [] });
  const [loadingPixels, setLoadingPixels] = useState(false);
  // Multi-pixel state (per platform): user can select N pixels, mark 1 as
  // primary (drives campaign optimization). For Meta, each pixel ALSO has an
  // event (LEAD, COMPLETE_REGISTRATION, etc) that determines what the
  // promoted_object optimizes for.
  type SelectedPixel = {
    id: string;
    name: string;
    primary: boolean;
    event?: string; // Meta only — custom_event_type
  };
  const [selectedPixels, setSelectedPixels] = useState<Record<string, SelectedPixel[]>>({ meta: [], google: [], meta_offline: [] });
  const pixelsLoaded = useRef(false);

  // Pixel creation modal state
  const [createPixelPlatform, setCreatePixelPlatform] = useState<'meta' | 'google' | 'linkedin' | null>(null);
  const [newPixelName, setNewPixelName] = useState('');
  const [newPixelCategory, setNewPixelCategory] = useState<string>('DEFAULT');
  const [creatingPixel, setCreatingPixel] = useState(false);

  // Pixel install snippet modal state
  const [snippetModal, setSnippetModal] = useState<{
    platform: 'meta' | 'google' | 'linkedin';
    pixelId: string;
    pixelName: string;
  } | null>(null);
  const [snippetData, setSnippetData] = useState<{
    html: string;
    noscript?: string;
    conversionSnippet?: string;
    instructions: string;
    docsUrl?: string;
  } | null>(null);
  const [loadingSnippet, setLoadingSnippet] = useState(false);

  const [campaignData, setCampaignData] = useState<CampaignData>({
    platforms: [],
    channels: [],
    type: '',
    objective: '',
    name: '',
    description: '',
    budget_type: 'daily',
    budget_amount: 50,
    currency: 'BRL',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    bid_strategy: 'automatic',
    max_cpc: undefined,
    target_cpa: undefined,
    audience: {
      age_min: 18,
      age_max: 65,
      genders: ['all'],
      locations: [{ name: 'Brasil', canonicalName: 'Brasil', geoTargetId: '2076', targetType: 'Country', countryCode: 'BR', radius: 0 }],
      interests: [],
      devices: DEFAULT_DEVICES,
      languages: DEFAULT_GOOGLE_LANGUAGES,
    },
    creatives: [createEmptyCreative()],
    platformCreatives: {},
    ad_set_name: '',
    campaign_group_name: '',
    meta_lead_destination: 'instant_form',
    meta_lead_dest_config: {},
    googleAssets: createEmptyGoogleAssets(),
    googleAdGroups: [],
    metaCbo: false,
  });

  // Meta pages available from connected facebook_ads accounts (for per-campaign page override)
  const [metaPages, setMetaPages] = useState<Array<{
    page_id: string;
    page_name: string;
    page_picture?: string | null;
    instagram_actor_id?: string | null;
    instagram_username?: string | null;
    instagram_picture?: string | null;
  }>>([]);
  const [defaultMetaPageId, setDefaultMetaPageId] = useState<string>('');

  // LinkedIn organizations available from connected linkedin_ads account (for per-campaign org override)
  const [linkedinOrgs, setLinkedinOrgs] = useState<Array<{
    organization_urn: string;
    name: string;
    logo_url?: string | null;
    vanity_name?: string | null;
  }>>([]);
  const [defaultLinkedinOrgUrn, setDefaultLinkedinOrgUrn] = useState<string>('');

  // State for active platform in creatives step
  const [activePlatformTab, setActivePlatformTab] = useState<string>('');
  const [activeCreativeIdx, setActiveCreativeIdx] = useState<Record<string, number>>({});

  // Edição inline de ad groups (rename ou criação). id=null + isNew=true = criando.
  // id=string + isNew=false = renomeando aquele ad group.
  const [editingAdGroup, setEditingAdGroup] = useState<{ id: string | null; name: string; isNew: boolean }>(
    { id: null, name: '', isNew: false }
  );
  // Estado da confirmação inline de remoção de ad group (substitui um window.confirm).
  // null = sem confirmação ativa; string = id do grupo aguardando confirmação.
  const [confirmingRemoveAdGroupId, setConfirmingRemoveAdGroupId] = useState<string | null>(null);

  // Meta audience size estimate (Audience step)
  const [metaAudienceEstimate, setMetaAudienceEstimate] = useState<MetaAudienceEstimate | null>(null);
  const [estimatingMetaAudience, setEstimatingMetaAudience] = useState(false);

  // Debounced Meta audience estimate — fires whenever the audience targeting
  // changes and Meta is in the selected platforms. Skipped when Meta isn't
  // selected since the metric comes from Meta's delivery_estimate endpoint.
  useEffect(() => {
    if (!campaignData.platforms.includes('meta')) {
      setMetaAudienceEstimate(null);
      return;
    }
    if (!campaignData.audience?.locations?.length) {
      setMetaAudienceEstimate(null);
      return;
    }
    const timer = setTimeout(async () => {
      setEstimatingMetaAudience(true);
      try {
        const result = await estimateMetaAudience({
          locations: campaignData.audience.locations as any,
          age_min: campaignData.audience.age_min,
          age_max: campaignData.audience.age_max,
          genders: campaignData.audience.genders
        });
        setMetaAudienceEstimate(result);
      } catch {
        setMetaAudienceEstimate(null);
      } finally {
        setEstimatingMetaAudience(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [
    campaignData.platforms.join(','),
    JSON.stringify(campaignData.audience?.locations || []),
    campaignData.audience?.age_min,
    campaignData.audience?.age_max,
    (campaignData.audience?.genders || []).join(',')
  ]);

  // Helpers for multi-creative-per-platform
  const getActiveIdx = (platform: string): number => activeCreativeIdx[platform] ?? 0;
  const getCreativesArr = (platform: string): PlatformCreative[] => campaignData.platformCreatives[platform] || [];
  const getActiveCreative = (platform: string): PlatformCreative | undefined => {
    const arr = campaignData.platformCreatives[platform];
    if (!arr || arr.length === 0) return undefined;
    const idx = getActiveIdx(platform);
    return arr[Math.min(idx, arr.length - 1)];
  };

  // Location autocomplete state (Google Ads GeoTargetConstants API)
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<Array<{ id: string; name: string; canonicalName: string; targetType: string; countryCode: string }>>([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [isSearchingLocations, setIsSearchingLocations] = useState(false);
  const locationSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLocationSearch = (query: string) => {
    if (locationSearchTimeout.current) clearTimeout(locationSearchTimeout.current);
    if (query.length < 2) {
      setLocationSuggestions([]);
      setShowLocationDropdown(false);
      return;
    }
    setIsSearchingLocations(true);
    locationSearchTimeout.current = setTimeout(async () => {
      try {
        const results = await searchLocations(query);
        setLocationSuggestions(results);
        setShowLocationDropdown(results.length > 0);
      } catch {
        setLocationSuggestions([]);
        setShowLocationDropdown(false);
      } finally {
        setIsSearchingLocations(false);
      }
    }, 300);
  };

  const handleSelectLocation = (suggestion: typeof locationSuggestions[0]) => {
    const isCountry = suggestion.targetType === 'Country';
    const newLoc: LocationWithRadius = {
      name: suggestion.name,
      canonicalName: suggestion.canonicalName,
      geoTargetId: suggestion.id,
      targetType: suggestion.targetType,
      countryCode: suggestion.countryCode,
      lat: (suggestion as any).lat,
      lng: (suggestion as any).lng,
      radius: isCountry ? 0 : 25
    };
    const isDuplicate = campaignData.audience.locations.some(l =>
      (l.geoTargetId && l.geoTargetId === suggestion.id) ||
      (!l.geoTargetId && l.name === suggestion.name && l.countryCode === suggestion.countryCode)
    );
    if (isDuplicate) {
      setLocationSearchQuery('');
      setLocationSuggestions([]);
      setShowLocationDropdown(false);
      return;
    }

    setCampaignData(prev => {
      let updatedLocations = [...prev.audience.locations];

      if (isCountry) {
        // Adding a country → remove all cities/regions within that country (they're redundant)
        const removedCount = updatedLocations.filter(l =>
          l.targetType !== 'Country' && l.countryCode === suggestion.countryCode
        ).length;
        updatedLocations = updatedLocations.filter(l =>
          !(l.targetType !== 'Country' && l.countryCode === suggestion.countryCode)
        );
        if (removedCount > 0) {
          toast(t('campaigns.new.overlapRemoved', 'Removed {{count}} location(s) already covered by {{country}}', {
            count: removedCount,
            country: suggestion.name
          }), { icon: '⚠️' });
        }
      } else {
        // Adding a city/region while its country is selected → auto-swap: remove the country
        // (which covers everything) so the specific city/region actually gets targeted & marked.
        const countryExists = updatedLocations.some(l =>
          l.targetType === 'Country' && l.countryCode === suggestion.countryCode
        );
        if (countryExists) {
          const countryName = updatedLocations.find(l =>
            l.targetType === 'Country' && l.countryCode === suggestion.countryCode
          )?.name || suggestion.countryCode;
          updatedLocations = updatedLocations.filter(l =>
            !(l.targetType === 'Country' && l.countryCode === suggestion.countryCode)
          );
          toast(t('campaigns.new.countrySwapped', 'Removi {{country}} para segmentar cidades/regiões específicas.', {
            country: countryName
          }), { icon: 'ℹ️' });
        }
      }

      updatedLocations.push(newLoc);
      return { ...prev, audience: { ...prev.audience, locations: updatedLocations } };
    });

    setLocationSearchQuery('');
    setLocationSuggestions([]);
    setShowLocationDropdown(false);
  };

  // State for remarketing audiences
  const [availableAudiences, setAvailableAudiences] = useState<Audience[]>([]);
  const [selectedAudiences, setSelectedAudiences] = useState<Array<{ audienceId: string; type: 'include' | 'exclude'; bidAdjustment: number }>>([]);
  const [loadingAudiences, setLoadingAudiences] = useState(false);

  // Audience CRUD modal state
  const [isAudienceModalOpen, setIsAudienceModalOpen] = useState(false);
  const [editingAudience, setEditingAudience] = useState<Audience | null>(null);
  const [audienceSubmitting, setAudienceSubmitting] = useState(false);
  const [deleteAudienceId, setDeleteAudienceId] = useState<string | null>(null);
  const [landingPagesForAudience, setLandingPagesForAudience] = useState<LandingPageOption[]>([]);
  const [externalSiteForm, setExternalSiteForm] = useState({ name: '', url: '' });
  const [isAddingExternalSite, setIsAddingExternalSite] = useState(false);
  const [audienceFormData, setAudienceFormData] = useState({
    name: '',
    description: '',
    type: 'website_visitors' as Audience['type'],
    source: 'landing_page' as 'landing_page' | 'pixel_events',
    landing_page_ids: [] as string[],
    lookback_days: 30,
    refresh_frequency: 'daily' as Audience['refresh_frequency'],
    is_dynamic: true,
    pixel_event_types: [] as string[],
    project_id: undefined as string | undefined
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<Array<{ email?: string; phone?: string; first_name?: string; last_name?: string }>>([]);
  const [estimatedAudienceSize, setEstimatedAudienceSize] = useState<number | null>(null);

  // Load existing campaign data when editing
  useEffect(() => {
    if (isEditMode && campaignId) {
      loadCampaign(campaignId);
    } else {
      // Check for platform in URL for new campaigns
      const platform = searchParams.get('platform') as 'google' | 'meta' | 'linkedin' | null;
      if (platform && (platform === 'google' || platform === 'meta' || platform === 'linkedin')) {
        setCampaignData(prev => ({ ...prev, platforms: [platform] }));
        setEnteredFromPlatform(true);
        setCurrentStep(1); // Skip to channels selection
      }
    }
  }, [campaignId, isEditMode, searchParams]);

  // Load available audiences for remarketing
  const loadAudiences = async () => {
    setLoadingAudiences(true);
    try {
      // Don't filter by status - show all audiences (building, ready, syncing)
      const response = await audienceService.getAudiences({});
      if (response.success) {
        // Filter out archived and error audiences
        const activeAudiences = (response.data || []).filter(
          (a: Audience) => a.status !== 'archived' && a.status !== 'error'
        );
        setAvailableAudiences(activeAudiences);
      }
    } catch (error) {
      console.error('Error loading audiences:', error);
    } finally {
      setLoadingAudiences(false);
    }
  };

  // Load install snippet for a pixel and open the modal
  const handleViewPixelSnippet = async (platform: 'meta' | 'google' | 'linkedin', pixel: { id: string; name: string }) => {
    if (!currentProject?.id) return;
    setSnippetModal({ platform, pixelId: pixel.id, pixelName: pixel.name });
    setSnippetData(null);
    setLoadingSnippet(true);
    try {
      const response = await campaignService.getPlatformPixelSnippet(currentProject.id, platform, pixel.id);
      if (response.success && response.data) {
        setSnippetData(response.data);
      } else {
        toast.error(response.message || t('campaigns.new.pixelSnippetError', 'Erro ao gerar código'));
        setSnippetModal(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('campaigns.new.pixelSnippetError', 'Erro ao gerar código'));
      setSnippetModal(null);
    } finally {
      setLoadingSnippet(false);
    }
  };

  // Copy text to clipboard
  const handleCopySnippet = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('campaigns.new.snippetCopied', '{{label}} copiado!', { label }));
    } catch {
      toast.error(t('campaigns.new.snippetCopyError', 'Erro ao copiar'));
    }
  };

  // Delete a pixel from the platform
  const handleDeletePixel = async (platform: 'meta' | 'google' | 'linkedin', pixel: { id: string; name: string }) => {
    if (!currentProject?.id) return;
    const confirmed = window.confirm(
      t('campaigns.new.confirmDeletePixel', 'Tem certeza que deseja remover o pixel "{{name}}"? Essa ação remove o pixel na plataforma — se estiver em uso, as conversões param de ser rastreadas.', { name: pixel.name })
    );
    if (!confirmed) return;
    try {
      const response = await campaignService.deletePlatformPixel(currentProject.id, platform, pixel.id);
      if (response.success) {
        toast.success(t('campaigns.new.pixelDeleted', 'Pixel removido!'));
        // Remove from local list
        setAvailablePixels(prev => ({
          ...prev,
          [platform]: prev[platform].filter(p => p.id !== pixel.id)
        }));
        // Unselect if it was selected (remove from the array)
        setSelectedPixels(prev => ({
          ...prev,
          [platform]: (prev[platform] || []).filter(p => p.id !== pixel.id)
        }));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('campaigns.new.pixelDeleteError', 'Erro ao remover pixel'));
    }
  };

  // Create a new pixel on the platform
  const handleCreatePixel = async () => {
    if (!createPixelPlatform || !currentProject?.id || !newPixelName.trim()) return;
    setCreatingPixel(true);
    try {
      const response = await campaignService.createPlatformPixel(
        currentProject.id,
        createPixelPlatform,
        newPixelName.trim(),
        newPixelCategory
      );
      if (response.success && response.data) {
        toast.success(t('campaigns.new.pixelCreated', 'Pixel criado com sucesso!'));
        // Reload the pixel list to include the new one
        pixelsLoaded.current = false;
        await loadPixels();
        // Pre-select the new pixel — first added becomes primary by default
        setSelectedPixels(prev => {
          const list = prev[createPixelPlatform] || [];
          const newPixel: SelectedPixel = {
            id: response.data!.id,
            name: response.data!.name,
            primary: list.length === 0,
            event: createPixelPlatform === 'meta' ? 'LEAD' : undefined
          };
          return { ...prev, [createPixelPlatform]: [...list, newPixel] };
        });
        // Close modal
        setCreatePixelPlatform(null);
        setNewPixelName('');
        setNewPixelCategory('DEFAULT');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t('campaigns.new.pixelCreateError', 'Erro ao criar pixel'));
    } finally {
      setCreatingPixel(false);
    }
  };

  // Load available pixels from connected platforms
  const loadPixels = async () => {
    if (!currentProject?.id) {
      pixelsLoaded.current = true;
      return;
    }
    setLoadingPixels(true);
    try {
      const response = await campaignService.getPlatformPixels(currentProject.id);
      if (response.success && response.data) {
        const pixelData = response.data;
        setAvailablePixels(pixelData);
        // Pre-select previously selected pixels — only seed when current
        // selection is empty so we don't overwrite the user's local choices.
        setSelectedPixels(prev => {
          const next = { ...prev };
          (['meta', 'google', 'linkedin'] as const).forEach(platform => {
            if ((next[platform] || []).length > 0) return; // keep user picks
            const selected = (pixelData[platform] || []).filter((p: any) => p.selected);
            if (selected.length > 0) {
              next[platform] = selected.map((p: any, i: number) => ({
                id: p.id,
                name: p.name,
                primary: i === 0,
                event: platform === 'meta' ? 'LEAD' : undefined
              }));
            }
          });
          return next;
        });
      }
    } catch (error) {
      console.error('Error loading pixels:', error);
    } finally {
      setLoadingPixels(false);
      pixelsLoaded.current = true;
    }
  };

  // Load pixels when entering the pixel step
  useEffect(() => {
    if (currentStep === 5 && !pixelsLoaded.current && !loadingPixels) {
      loadPixels();
    }
  }, [currentStep]);

  useEffect(() => {
    loadAudiences();
    loadLandingPagesForAudience();
  }, []);

  // Load landing pages for audience creation
  const loadLandingPagesForAudience = async () => {
    try {
      const response = await audienceService.getAvailableLandingPages();
      if (response.success) {
        setLandingPagesForAudience(response.data || []);
      }
    } catch (error) {
      console.error('Error loading landing pages:', error);
    }
  };

  // Add external website for remarketing
  const handleAddExternalSiteInline = async () => {
    if (!externalSiteForm.name.trim() || !externalSiteForm.url.trim()) {
      toast.error(t('audiences.externalNameUrlRequired', 'Name and URL are required'));
      return;
    }
    setIsAddingExternalSite(true);
    try {
      const response = await audienceService.createExternalWebsite({
        name: externalSiteForm.name,
        url: externalSiteForm.url,
        project_id: currentProject?.id
      });
      if (response.success) {
        toast.success(t('audiences.externalCreated', 'External site registered successfully!'));
        setExternalSiteForm({ name: '', url: '' });
        loadLandingPagesForAudience();
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || t('audiences.errorCreatingExternal', 'Error registering external site');
      toast.error(msg);
    } finally {
      setIsAddingExternalSite(false);
    }
  };

  // Reset audience form
  const resetAudienceForm = () => {
    setAudienceFormData({
      name: '',
      description: '',
      type: 'website_visitors',
      source: 'landing_page',
      landing_page_ids: [],
      lookback_days: 30,
      refresh_frequency: 'daily',
      is_dynamic: true,
      pixel_event_types: [],
      project_id: currentProject?.id
    });
    setCsvFile(null);
    setCsvPreview([]);
    setEstimatedAudienceSize(null);
    setEditingAudience(null);
  };

  // Open audience modal for create
  const openCreateAudienceModal = () => {
    resetAudienceForm();
    setIsAudienceModalOpen(true);
  };

  // Open audience modal for edit
  const openEditAudienceModal = (audience: Audience) => {
    setEditingAudience(audience);
    setAudienceFormData({
      name: audience.name,
      description: audience.description || '',
      type: audience.type,
      source: audience.source === 'pixel_events' ? 'pixel_events' : 'landing_page',
      landing_page_ids: audience.landing_page_ids || [],
      lookback_days: audience.lookback_days,
      refresh_frequency: audience.refresh_frequency,
      is_dynamic: audience.is_dynamic !== false,
      pixel_event_types: (audience.rules as any)?.pixel_event_types || [],
      project_id: audience.project_id || currentProject?.id
    });
    setIsAudienceModalOpen(true);
  };

  // Toggle landing page selection
  const toggleAudienceLandingPage = (lpId: string) => {
    setAudienceFormData(prev => ({
      ...prev,
      landing_page_ids: prev.landing_page_ids.includes(lpId)
        ? prev.landing_page_ids.filter(id => id !== lpId)
        : [...prev.landing_page_ids, lpId]
    }));
  };

  // CSV file handling
  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt') && !file.name.endsWith('.xlsx')) {
      toast.error(t('audiences.invalidFileType', 'File must be CSV or TXT'));
      e.target.value = ''; // Reset input
      return;
    }

    setCsvFile(file);
    parseCsvPreview(file);
    toast.success(t('audiences.fileSelected', 'File selected: ') + file.name);

    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const parseCsvPreview = (file: File) => {
    const reader = new FileReader();
    reader.onerror = () => {
      toast.error(t('audiences.fileReadError', 'Error reading file'));
      console.error('FileReader error:', reader.error);
    };
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        console.log('File content length:', text?.length);

        const lines = text.split('\n').filter(line => line.trim());
        console.log('Lines found:', lines.length);

        if (lines.length === 0) {
          toast.error(t('audiences.emptyFile', 'Empty file'));
          return;
        }

        const firstLine = lines[0];
        const delimiter = firstLine.includes(';') ? ';' : ',';
        const headers = firstLine.split(delimiter).map(h => h.trim().toLowerCase().replace(/["']/g, ''));
        console.log('Headers detected:', headers);

        const headerMap: Record<string, string> = {
          'email': 'email', 'e-mail': 'email', 'e_mail': 'email',
          'phone': 'phone', 'telefone': 'phone', 'tel': 'phone', 'celular': 'phone', 'mobile': 'phone',
          'first_name': 'first_name', 'firstname': 'first_name', 'primeiro_nome': 'first_name', 'nome': 'first_name',
          'last_name': 'last_name', 'lastname': 'last_name', 'sobrenome': 'last_name', 'ultimo_nome': 'last_name',
          'country': 'country', 'pais': 'country'
        };

        const columnIndices: Record<string, number> = {};
        headers.forEach((h, idx) => {
          const mapped = headerMap[h];
          if (mapped) columnIndices[mapped] = idx;
        });
        console.log('Column indices:', columnIndices);

        const previewData: Array<{ email?: string; phone?: string; first_name?: string; last_name?: string }> = [];
        const dataLines = lines.slice(1, 6);

        for (const line of dataLines) {
          const values = line.split(delimiter).map(v => v.trim().replace(/["']/g, ''));
          const row: any = {};

          if (columnIndices.email !== undefined) row.email = values[columnIndices.email];
          if (columnIndices.phone !== undefined) row.phone = values[columnIndices.phone];
          if (columnIndices.first_name !== undefined) row.first_name = values[columnIndices.first_name];
          if (columnIndices.last_name !== undefined) row.last_name = values[columnIndices.last_name];

          if (row.email || row.phone) {
            previewData.push(row);
          }
        }

        console.log('Preview data:', previewData);
        setCsvPreview(previewData);

        if (previewData.length === 0) {
          toast.error(t('audiences.noValidData', 'No valid data found. Make sure you have email or phone columns.'));
        }
      } catch (err) {
        console.error('Parse error:', err);
        toast.error(t('audiences.parseError', 'Error processing file'));
      }
    };
    reader.readAsText(file);
  };

  // Preview audience size
  const handlePreviewAudienceSize = async () => {
    if (['website_visitors', 'leads', 'converters'].includes(audienceFormData.type) && audienceFormData.landing_page_ids.length === 0) {
      setEstimatedAudienceSize(null);
      return;
    }

    try {
      const response = await audienceService.previewAudienceSize({
        type: audienceFormData.type,
        landing_page_ids: audienceFormData.landing_page_ids,
        lookback_days: audienceFormData.lookback_days
      });
      if (response.success) {
        setEstimatedAudienceSize(response.data.estimated_size);
      }
    } catch (error) {
      console.error('Error previewing size:', error);
    }
  };

  // Create or update audience
  const handleSaveAudience = async () => {
    if (!audienceFormData.name.trim()) {
      toast.error(t('audiences.nameRequired', 'Name is required'));
      return;
    }

    if (['website_visitors', 'leads', 'converters'].includes(audienceFormData.type) && audienceFormData.landing_page_ids.length === 0) {
      toast.error(t('audiences.selectLandingPage', 'Select at least one landing page or site'));
      return;
    }

    if (audienceFormData.type === 'customer_list' && !csvFile && !editingAudience) {
      toast.error(t('audiences.selectCSV', 'Select a CSV file'));
      return;
    }

    // Validate pixel event types when source is pixel_events
    if (audienceFormData.source === 'pixel_events' && audienceFormData.pixel_event_types.length === 0) {
      toast.error(t('audiences.selectPixelEvents', 'Selecione pelo menos um tipo de evento do pixel'));
      return;
    }

    setAudienceSubmitting(true);
    try {
      if (editingAudience) {
        // Update existing audience
        const response = await audienceService.updateAudience(editingAudience.id, {
          ...audienceFormData,
          is_dynamic: audienceFormData.is_dynamic
        });
        if (response.success) {
          toast.success(t('audiences.updated', 'Audience updated successfully'));
          setIsAudienceModalOpen(false);
          resetAudienceForm();
          loadAudiences();
        }
      } else {
        // Create new audience
        const resolvedSource = audienceFormData.type === 'customer_list'
          ? 'csv_upload'
          : audienceFormData.source === 'pixel_events'
            ? 'pixel_events'
            : 'landing_page';
        const response = await audienceService.createAudience({
          ...audienceFormData,
          source: resolvedSource,
          is_dynamic: audienceFormData.is_dynamic,
          pixel_event_types: audienceFormData.source === 'pixel_events' ? audienceFormData.pixel_event_types : undefined
        });

        if (response.success) {
          const audienceId = response.data.id;

          // If customer_list, upload the CSV data
          if (audienceFormData.type === 'customer_list' && csvFile) {
            const text = await csvFile.text();
            const lines = text.split('\n').filter(line => line.trim());
            const firstLine = lines[0];
            const delimiter = firstLine.includes(';') ? ';' : ',';
            const headers = firstLine.split(delimiter).map(h => h.trim().toLowerCase().replace(/["']/g, ''));

            const headerMap: Record<string, string> = {
              'email': 'email', 'e-mail': 'email', 'e_mail': 'email',
              'phone': 'phone', 'telefone': 'phone', 'tel': 'phone', 'celular': 'phone', 'mobile': 'phone',
              'first_name': 'first_name', 'firstname': 'first_name', 'primeiro_nome': 'first_name', 'nome': 'first_name',
              'last_name': 'last_name', 'lastname': 'last_name', 'sobrenome': 'last_name', 'ultimo_nome': 'last_name',
              'country': 'country', 'pais': 'country'
            };

            const columnIndices: Record<string, number> = {};
            headers.forEach((h, idx) => {
              const mapped = headerMap[h];
              if (mapped) columnIndices[mapped] = idx;
            });

            const users: Array<{ email?: string; phone?: string; first_name?: string; last_name?: string; country?: string }> = [];
            const dataLines = lines.slice(1);

            for (const line of dataLines) {
              const values = line.split(delimiter).map(v => v.trim().replace(/["']/g, ''));
              const row: any = {};

              if (columnIndices.email !== undefined && values[columnIndices.email]) row.email = values[columnIndices.email];
              if (columnIndices.phone !== undefined && values[columnIndices.phone]) row.phone = values[columnIndices.phone];
              if (columnIndices.first_name !== undefined) row.first_name = values[columnIndices.first_name];
              if (columnIndices.last_name !== undefined) row.last_name = values[columnIndices.last_name];
              if (columnIndices.country !== undefined) row.country = values[columnIndices.country];

              if (row.email || row.phone) {
                users.push(row);
              }
            }

            if (users.length > 0) {
              const uploadResponse = await audienceService.addUsersToAudience(audienceId, users);
              if (uploadResponse.success) {
                toast.success(t('audiences.createdWithUsers', 'Audience created with {{count}} users')
                  .replace('{{count}}', uploadResponse.data.added.toString()));
              }
            }
          } else {
            toast.success(t('audiences.created', 'Audience created successfully'));
          }

          setIsAudienceModalOpen(false);
          resetAudienceForm();
          loadAudiences();
        }
      }
    } catch (error: any) {
      toast.error(error.message || t('audiences.errorCreating', 'Error creating audience'));
    } finally {
      setAudienceSubmitting(false);
    }
  };

  // Delete audience
  const handleDeleteAudience = async (audienceId: string) => {
    try {
      const response = await audienceService.deleteAudience(audienceId);
      if (response.success) {
        toast.success(t('audiences.deleted', 'Audience deleted'));
        setDeleteAudienceId(null);
        // Remove from selected if it was selected
        setSelectedAudiences(prev => prev.filter(s => s.audienceId !== audienceId));
        loadAudiences();
      }
    } catch (error: any) {
      toast.error(error.message || t('audiences.errorDeleting', 'Error deleting audience'));
    }
  };

  // Effect to preview size when form changes
  useEffect(() => {
    if (isAudienceModalOpen) {
      handlePreviewAudienceSize();
    }
  }, [audienceFormData.type, audienceFormData.landing_page_ids, audienceFormData.lookback_days, isAudienceModalOpen]);

  // Set preview channel when channels change
  useEffect(() => {
    if (campaignData.channels.length > 0 && !previewChannel) {
      setPreviewChannel(campaignData.channels[0]);
    }
  }, [campaignData.channels]);

  // Initialize platformCreatives when platforms are selected
  useEffect(() => {
    if (campaignData.platforms.length > 0) {
      setCampaignData(prev => {
        const newPlatformCreatives = { ...prev.platformCreatives };
        // Add empty creative array for each new platform
        campaignData.platforms.forEach(platform => {
          if (!newPlatformCreatives[platform] || newPlatformCreatives[platform].length === 0) {
            newPlatformCreatives[platform] = [createEmptyPlatformCreative(platform)];
          }
        });
        // Remove creatives for deselected platforms
        Object.keys(newPlatformCreatives).forEach(platform => {
          if (!campaignData.platforms.includes(platform)) {
            delete newPlatformCreatives[platform];
          }
        });
        return { ...prev, platformCreatives: newPlatformCreatives };
      });
      // Set active platform tab if not set
      if (!activePlatformTab || !campaignData.platforms.includes(activePlatformTab)) {
        setActivePlatformTab(campaignData.platforms[0]);
      }
      // Reset pixel data when platforms change so we re-fetch for the new selection
      pixelsLoaded.current = false;
      setSelectedPixels(prev => {
        const cleaned: Record<string, SelectedPixel[]> = {};
        campaignData.platforms.forEach(p => {
          if (p in prev) cleaned[p] = prev[p];
        });
        return cleaned;
      });
    }
  }, [campaignData.platforms]);

  // Update preview channel when platform tab changes
  useEffect(() => {
    if (activePlatformTab && currentStep === 7) {
      // Find first channel matching the active platform
      const platformChannels = campaignData.channels.filter(ch => {
        const channel = [...GOOGLE_CHANNELS, ...META_CHANNELS, ...LINKEDIN_CHANNELS].find(c => c.id === ch);
        return channel?.platform === activePlatformTab;
      });
      if (platformChannels.length > 0 && (!previewChannel || !platformChannels.includes(previewChannel))) {
        setPreviewChannel(platformChannels[0]);
      }
    }
  }, [activePlatformTab, currentStep, campaignData.channels]);

  // Load connected ad accounts (same logic as CampaignsPage)
  useEffect(() => {
    const loadConnectedAccounts = async () => {
      setLoadingAccounts(true);
      try {
        // Fetch all accounts filtered by current project (same as CampaignsPage)
        const params: Record<string, string> = { include_ads: 'true' };
        if (currentProject?.id) params.project_id = currentProject.id;
        const response = await api.get('/social/accounts', { params });
        console.log('All accounts from API:', response.data);

        if (response.data.success && response.data.data) {
          // Filter for ad-capable platforms: Google Ads, Meta/Facebook Ads, LinkedIn (personal/company)
          // openai_ads entra só pra UI saber que existe conexão (criação de
          // campanha ChatGPT é fase 2 — isPlatformConnected não o mapeia)
          const adCapablePlatforms = ['google_ads', 'facebook_ads', 'linkedin_ads', 'linkedin', 'openai_ads'];
          const adAccounts = response.data.data.filter((a: any) =>
            adCapablePlatforms.includes(a.platform) && a.connected === true
          );
          console.log('Filtered ad accounts:', adAccounts);
          setConnectedAccounts(adAccounts);
        }
      } catch (error) {
        console.error('Error loading connected accounts:', error);
      } finally {
        setLoadingAccounts(false);
      }
    };
    loadConnectedAccounts();
  }, [currentProject?.id]);

  // Load available Facebook pages from connected Meta Ads accounts so the user
  // can override the default page at the campaign level.
  useEffect(() => {
    if (!campaignData.platforms.includes('meta')) return;
    if (metaPages.length > 0) return;
    campaignService.getMetaInstagramAccounts().then(res => {
      if (res.success && Array.isArray(res.data)) {
        const pages = res.data
          .filter((p: any) => p && p.page_id)
          .map((p: any) => ({
            page_id: p.page_id,
            page_name: p.page_name,
            page_picture: p.page_picture || null,
            instagram_actor_id: p.instagram_actor_id || null,
            instagram_username: p.instagram_username || null,
            instagram_picture: p.instagram_picture || null,
          }));
        setMetaPages(pages);
        // Default fallback used when an ad has no explicit page selected yet
        if (pages.length > 0) {
          setDefaultMetaPageId(pages[0].page_id);
        }
      }
    }).catch(() => {});
  }, [campaignData.platforms]);

  // Load LinkedIn organizations where the user is admin so the user can pick
  // which company publishes the sponsored ad.
  useEffect(() => {
    if (!campaignData.platforms.includes('linkedin')) return;
    if (linkedinOrgs.length > 0) return;
    campaignService.getLinkedinOrganizations().then(res => {
      if (res.success && Array.isArray(res.data)) {
        setLinkedinOrgs(res.data);
        const defaultOrg = res.data.find(o => o.is_default) || res.data[0];
        if (defaultOrg) {
          setDefaultLinkedinOrgUrn(defaultOrg.organization_urn);
        }
      }
    }).catch(() => {});
  }, [campaignData.platforms]);

  // Helper to check if platform is connected
  // connectedAccounts already contains only connected accounts (filtered in useEffect)
  const isPlatformConnected = (platform: string): boolean => {
    // Only the dedicated ADS platforms count as "connected" for campaign
    // creation. The organic post accounts (linkedin/instagram/facebook via
    // "social login") do NOT give access to the ad APIs and would fail at push.
    const platformMap: Record<string, string[]> = {
      'google': ['google_ads'],
      'meta': ['facebook_ads'],
      'linkedin': ['linkedin_ads']
    };
    const validPlatforms = platformMap[platform] || [];
    return connectedAccounts.some(a => validPlatforms.includes(a.platform));
  };

  const loadCampaign = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await campaignService.getCampaign(id);
      if (response.success && response.data) {
        const campaign = response.data;

        // Calculate platforms and channels
        const platforms = campaign.platforms || (campaign.platform ? [campaign.platform] : []);
        let channels = campaign.channels || [];

        // If no channels saved, derive from platforms
        if (channels.length === 0 && platforms.length > 0) {
          if (platforms.includes('google')) {
            channels = [...channels, 'google_search', 'google_display'];
          }
          if (platforms.includes('meta')) {
            channels = [...channels, 'facebook_feed', 'instagram_feed'];
          }
          if (platforms.includes('linkedin')) {
            channels = [...channels, 'linkedin_sponsored_content'];
          }
        }

        // Map ads to platformCreatives (group all ads per platform into arrays)
        const loadedPlatformCreatives: Record<string, PlatformCreative[]> = {};
        // Ids dos creatives Google que já estão publicados (têm external_id_google)
        // mas não têm ad_group_local_id — candidatos a herdar o "Grupo importado"
        // pra desbloquear edição de campanhas legacy sem quebrar a publicação.
        const legacyPublishedGoogleIds = new Set<string>();

        if (campaign.ads && campaign.ads.length > 0) {
          campaign.ads.forEach((ad: any) => {
            const adPlatform = ad.settings?.platform || platforms[0] || 'meta';
            const rawImageUrl = ad.media_urls?.[0] || null;
            const validImageUrl = rawImageUrl && !rawImageUrl.startsWith('blob:') && !rawImageUrl.startsWith('data:') ? rawImageUrl : null;

            const loadedHeadlines: string[] = ad.headlines?.length > 0 ? ad.headlines : [''];
            const savedPins: Array<1 | 2 | 3 | null> | undefined = ad.settings?.headlinePins;
            const creative: PlatformCreative = {
              id: ad.id || `creative-${adPlatform}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              platform: adPlatform,
              headlines: loadedHeadlines,
              descriptions: ad.descriptions?.length > 0 ? ad.descriptions : [''],
              // Only Google uses pins; rehydrate saved array or initialize all null.
              headlinePins: adPlatform === 'google'
                ? (Array.isArray(savedPins) && savedPins.length === loadedHeadlines.length
                    ? savedPins
                    : Array(loadedHeadlines.length).fill(null))
                : undefined,
              imageUrl: validImageUrl,
              imageFile: null,
              videoUrl: ad.video_url || '',
              ctaText: ad.call_to_action || 'learn_more',
              destinationUrl: ad.final_url || '',
              displayUrl: ad.settings?.displayUrl || '',
              keywords: ad.settings?.keywords || [],
              isGeneratingImage: false,
              imagePrompt: ad.settings?.imagePrompt || '',
              imageStyle: ad.settings?.imageStyle || 'realistic',
              metaPageId: adPlatform === 'meta' ? (ad.settings?.meta_page_id || ad.settings?.page_id || '') : undefined,
              metaInstagramId: adPlatform === 'meta' ? (ad.settings?.meta_instagram_id || '') : undefined,
              linkedinOrgUrn: adPlatform === 'linkedin' ? (ad.settings?.linkedin_org_urn || '') : undefined,
              adGroupId: adPlatform === 'google' ? (ad.settings?.ad_group_local_id || '') : undefined,
              adsetGroup: adPlatform === 'meta' ? (ad.settings?.adsetGroup || '1') : undefined,
            };

            if (!loadedPlatformCreatives[adPlatform]) {
              loadedPlatformCreatives[adPlatform] = [];
            }
            loadedPlatformCreatives[adPlatform].push(creative);
            // Marca creative Google que já está publicado mas sem ad_group_local_id
            if (adPlatform === 'google' && ad.external_id_google && !ad.settings?.ad_group_local_id) {
              legacyPublishedGoogleIds.add(creative.id);
            }
          });
        }

        // Ensure all platforms have at least one creative (empty if none loaded)
        platforms.forEach((platform: string) => {
          if (!loadedPlatformCreatives[platform] || loadedPlatformCreatives[platform].length === 0) {
            loadedPlatformCreatives[platform] = [createEmptyPlatformCreative(platform)];
          }
        });

        // Map ads to legacy creatives (for backwards compatibility)
        const loadedCreatives: Creative[] = campaign.ads && campaign.ads.length > 0
          ? campaign.ads.map((ad: any) => {
              const rawUrl = ad.media_urls?.[0] || null;
              const validUrl = rawUrl && !rawUrl.startsWith('blob:') && !rawUrl.startsWith('data:') ? rawUrl : null;
              return {
              id: ad.id || `creative-${Date.now()}-${Math.random()}`,
              headlines: ad.headlines || [''],
              descriptions: ad.descriptions || [''],
              imageUrl: validUrl,
              imageFile: null,
              videoUrl: ad.video_url || '',
              ctaText: ad.call_to_action || 'learn_more',
              destinationUrl: ad.final_url || '',
              isGeneratingImage: false,
              imagePrompt: ad.settings?.imagePrompt || '',
              imageStyle: ad.settings?.imageStyle || 'realistic',
            };})
          : [createEmptyCreative()];

        // Normalize locations to new format (support old string[] format)
        const rawAudience: any = campaign.target_audience || {
          age_min: 18,
          age_max: 65,
          genders: ['all'],
          locations: [{ name: 'Brasil', canonicalName: 'Brasil', geoTargetId: '2076', targetType: 'Country', countryCode: 'BR', radius: 0 }],
          interests: [],
          devices: DEFAULT_DEVICES,
        };
        if (rawAudience.locations && rawAudience.locations.length > 0) {
          rawAudience.locations = rawAudience.locations.map((loc: any) => {
            if (typeof loc === 'string') {
              if (loc.toLowerCase() === 'brasil' || loc.toLowerCase() === 'brazil') {
                return { name: 'Brasil', canonicalName: 'Brasil', geoTargetId: '2076', targetType: 'Country', countryCode: 'BR', radius: 0 };
              }
              return { name: loc, canonicalName: loc, geoTargetId: '', targetType: 'City', countryCode: 'BR', radius: 25 };
            }
            // Ensure countryCode has fallback for legacy objects without it
            return { ...loc, countryCode: loc.countryCode || 'BR', radius: loc.radius ?? 0 };
          });
        }
        // Normaliza o audience: campanha "limpa" (sem geo/idade/interesses) não tem todos os
        // campos → garante arrays/valores definidos pra não quebrar os .map/.length no render.
        rawAudience.age_min = rawAudience.age_min ?? 18;
        rawAudience.age_max = rawAudience.age_max ?? 65;
        rawAudience.genders = (rawAudience.genders && rawAudience.genders.length) ? rawAudience.genders : ['all'];
        rawAudience.locations = Array.isArray(rawAudience.locations) ? rawAudience.locations : [];
        rawAudience.interests = Array.isArray(rawAudience.interests) ? rawAudience.interests : [];
        rawAudience.behaviors = Array.isArray(rawAudience.behaviors) ? rawAudience.behaviors : [];
        rawAudience.devices = rawAudience.devices || DEFAULT_DEVICES;
        // Idiomas (Google): prefill do que foi salvo; campanha antiga sem idiomas fica sem
        // (= todos os idiomas) até o usuário escolher.
        if (Array.isArray(rawAudience.languages)) {
          rawAudience.languages = rawAudience.languages.map((l: any) => String(l)).filter(Boolean);
        } else if (!campaign.target_audience) {
          rawAudience.languages = DEFAULT_GOOGLE_LANGUAGES;
        } else {
          delete rawAudience.languages;
        }

        const pushed: string[] = [];
        if (campaign.external_id_meta) pushed.push('Meta');
        if (campaign.external_id_google) pushed.push('Google');
        if (campaign.external_id_linkedin) pushed.push('LinkedIn');
        setPushedPlatforms(pushed);

        setCampaignData({
          platforms,
          channels,
          type: campaign.type,
          objective: campaign.objective,
          name: campaign.name,
          description: campaign.description || '',
          budget_type: campaign.budget_type,
          budget_amount: campaign.budget_amount || 50,
          currency: campaign.currency || 'BRL',
          start_date: campaign.start_date?.split('T')[0] || new Date().toISOString().split('T')[0],
          end_date: campaign.end_date?.split('T')[0] || '',
          bid_strategy: campaign.bid_strategy || 'automatic',
          ...(() => {
            const st: any = campaign.settings || {};
            const portfolio = typeof st.google_portfolio_strategy === 'string' && st.google_portfolio_strategy ? st.google_portfolio_strategy : undefined;
            const isRoas = !portfolio && st.google_bid_strategy === 'target_roas';
            return {
              google_bid_mode: portfolio ? 'portfolio' as const : isRoas ? 'target_roas' as const : null,
              google_target_roas_pct: Number(st.target_roas) > 0 ? Math.round(Number(st.target_roas) * 10000) / 100 : undefined,
              google_portfolio_strategy: portfolio,
              google_portfolio_strategy_name: st.google_portfolio_strategy_name || undefined,
            };
          })(),
          google_ai_max: !!(campaign.settings && (campaign.settings as any).google_ai_max),
          max_cpc: campaign.max_cpc || undefined,
          target_cpa: campaign.target_cpa || undefined,
          audience: rawAudience,
          creatives: loadedCreatives,
          platformCreatives: loadedPlatformCreatives,
          ad_set_name: campaign.settings?.ad_set_name || '',
          campaign_group_name: campaign.settings?.campaign_group_name || '',
          meta_lead_destination: campaign.settings?.meta_lead_destination || 'instant_form',
          meta_lead_dest_config: campaign.settings?.meta_lead_dest_config || {},
          googleAssets: {
            callouts: campaign.settings?.google_assets?.callouts || [],
            structuredSnippets: campaign.settings?.google_assets?.structuredSnippets || [],
            sitelinks: campaign.settings?.google_assets?.sitelinks || [],
            whatsapp: {
              ...createEmptyGoogleWhatsApp(),
              ...(campaign.settings?.google_assets?.whatsapp || {}),
            },
          },
          googleAdGroups: (() => {
            const fromSettings: GoogleAdGroup[] = Array.isArray(campaign.settings?.google_ad_groups)
              ? campaign.settings.google_ad_groups.map((ag: any) => ({
                  id: String(ag.id || `ag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`),
                  name: String(ag.name || ''),
                  externalId: ag.externalId ? String(ag.externalId) : undefined,
                }))
              : [];
            // Legacy: campanhas publicadas antes desta feature têm Google creatives
            // com external_id_google mas sem ad_group_local_id e sem google_ad_groups.
            // Cria um ad group "Grupo importado" pra absorver SÓ os creatives legacy
            // publicados (preserva fluxo "novo creative precisa ter grupo escolhido").
            const googleCreatives = loadedPlatformCreatives.google || [];
            const legacyOrphans = googleCreatives.filter((c: PlatformCreative) =>
              !c.adGroupId && legacyPublishedGoogleIds.has(c.id)
            );
            if (fromSettings.length === 0 && legacyOrphans.length > 0) {
              const imported: GoogleAdGroup = {
                id: `ag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                name: 'Grupo importado',
              };
              legacyOrphans.forEach((c: PlatformCreative) => { c.adGroupId = imported.id; });
              return [imported];
            }
            return fromSettings;
          })(),
          metaCbo: campaign.settings?.meta_cbo === true,
        });

        // Load audience associations for remarketing
        const campaignAudiences = (campaign as any).audiences;
        if (campaignAudiences && campaignAudiences.length > 0) {
          setSelectedAudiences(campaignAudiences.map((a: any) => ({
            audienceId: a.id,
            type: a.CampaignAudience?.type || 'include',
            bidAdjustment: a.CampaignAudience?.bid_adjustment || 1.0,
          })));
        }

        // Load pixel selections from campaign settings — supports both legacy
        // single shape ({id, name}) and new array shape ([{id, name, primary, event}]).
        if (campaign.settings?.pixels) {
          const pixelSelections: Record<string, SelectedPixel[]> = {};
          Object.entries(campaign.settings.pixels).forEach(([platform, value]: [string, any]) => {
            if (Array.isArray(value)) {
              pixelSelections[platform] = value
                .filter((p: any) => p && p.id)
                .map((p: any, i: number) => ({
                  id: p.id,
                  name: p.name,
                  primary: p.primary ?? i === 0,
                  event: p.event || (platform === 'meta' ? 'LEAD' : undefined)
                }));
            } else if (value && value.id) {
              // Legacy: single object → wrap in array as primary
              pixelSelections[platform] = [{
                id: value.id,
                name: value.name,
                primary: true,
                event: value.event || (platform === 'meta' ? 'LEAD' : undefined)
              }];
            }
          });
          setSelectedPixels(pixelSelections);
        }

        // Set active platform tab
        if (platforms.length > 0) {
          setActivePlatformTab(platforms[0]);
        }

        // Set preview channel if channels are available
        if (channels.length > 0) {
          setPreviewChannel(channels[0]);
        }

        // Jump to specific step if provided via URL (?step=creatives|review|...)
        const requestedStep = searchParams.get('step');
        const stepIndexByName: Record<string, number> = {
          platforms: 0, channels: 1, objective: 2, details: 3,
          budget: 4, pixel: 5, audience: 6, creatives: 7, review: 8,
        };
        if (requestedStep && stepIndexByName[requestedStep] !== undefined) {
          setCurrentStep(stepIndexByName[requestedStep]);
        } else if (requestedStep && /^\d+$/.test(requestedStep)) {
          const idx = parseInt(requestedStep, 10);
          if (idx >= 0 && idx < STEPS.length) setCurrentStep(idx);
          else setCurrentStep(8);
        } else {
          setCurrentStep(8); // Default: review
        }

        // If a specific ad was requested (e.g. "Editar anúncio" from details page),
        // auto-select the platform tab and the ad's index so the user lands on it.
        const requestedAdId = searchParams.get('adId');
        if (requestedAdId) {
          let foundPlatform: string | null = null;
          let foundIdx = -1;
          Object.keys(loadedPlatformCreatives).forEach((platform) => {
            const idx = loadedPlatformCreatives[platform].findIndex((c) => c.id === requestedAdId);
            if (idx >= 0) { foundPlatform = platform; foundIdx = idx; }
          });
          if (foundPlatform && foundIdx >= 0) {
            setActivePlatformTab(foundPlatform);
            setActiveCreativeIdx((prev) => ({ ...prev, [foundPlatform as string]: foundIdx }));
          }
        }
      }
    } catch (error) {
      console.error('Error loading campaign:', error);
      toast.error(t('campaigns.new.loadError', 'Error loading campaign'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t('campaigns.ai.describeGoal', 'Describe what you want for your campaign'));
      return;
    }

    if (campaignData.platforms.length === 0) {
      toast.error(t('campaigns.ai.selectAtLeastOne', 'Select at least one platform'));
      return;
    }

    setIsGeneratingAI(true);
    // Show loading toast - image generation takes time
    const loadingToast = toast.loading(
      t('campaigns.ai.generatingWithImages', 'Generating campaign with AI... This may take a moment as we create images for each platform.'),
      { duration: 180000 }
    );

    try {
      // Call the real AI endpoint with async mode
      const response = await campaignService.generateWithAI(aiPrompt, campaignData.platforms, true, {
        ignorePattern: aiIgnorePattern,
        variationDirection: aiVariationDirection.trim() || undefined
      });

      // If async mode, poll for result
      let aiData: any;
      if (response.async && response.jobId) {
        try {
          const result = await pollJobUntilComplete(
            response.jobId,
            (progress, status) => {
              console.log(`Campaign AI job progress: ${progress}% - ${status}`);
            },
            2000, // Poll every 2 seconds
            180000 // 3 minute timeout
          );
          aiData = result?.data || result;
        } catch (pollError: any) {
          toast.dismiss(loadingToast);
          throw new Error(pollError.message || 'Failed to generate campaign');
        }
      } else {
        if (!response.success || !response.data) {
          toast.dismiss(loadingToast);
          throw new Error(response.message || 'Failed to generate campaign');
        }
        aiData = response.data;
      }

      toast.dismiss(loadingToast);

      // Build platformCreatives from AI-generated data (array per platform)
      const aiPlatformCreatives: Record<string, PlatformCreative[]> = {};

      // Resolve a lista de ad groups que a IA gerou pra Google. Cria entries
      // locais com UUID, e expõe um lookup por nome pra atribuir aos creatives.
      const aiGeneratedAdGroups: GoogleAdGroup[] = Array.isArray(aiData.googleAdGroups)
        ? aiData.googleAdGroups
            .map((ag: any) => ({
              id: `ag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              name: String(ag?.name || '').trim(),
            }))
            .filter((ag: GoogleAdGroup) => ag.name)
        : [];
      const adGroupByName = new Map<string, string>();
      aiGeneratedAdGroups.forEach(ag => adGroupByName.set(ag.name.toLowerCase(), ag.id));

      // If AI returns platform-specific creatives, use them (now includes images)
      if (aiData.platformCreatives) {
        Object.entries(aiData.platformCreatives).forEach(([platform, creative]: [string, any]) => {
          // Google: tenta casar adGroupName retornado pela IA com a lista de ad groups.
          // Fallback: primeiro grupo gerado, ou nenhum (validação no canProceed avisa).
          let resolvedAdGroupId: string | undefined;
          if (platform === 'google') {
            const wanted = String(creative.adGroupName || '').trim().toLowerCase();
            resolvedAdGroupId = (wanted && adGroupByName.get(wanted))
              || aiGeneratedAdGroups[0]?.id
              || undefined;
          }
          aiPlatformCreatives[platform] = [{
            ...createEmptyPlatformCreative(platform),
            headlines: creative.headlines || [''],
            descriptions: creative.descriptions || [''],
            ctaText: creative.ctaText || PLATFORM_SPECS[platform]?.ctas[0]?.value || 'learn_more',
            destinationUrl: creative.destinationUrl || 'https://yoursite.com',
            displayUrl: creative.displayUrl || '',
            imageUrl: creative.imageUrl || null, // AI-generated image
            imagePrompt: creative.imagePrompt || '',
            keywords: creative.keywords || [],
            ...(platform === 'google' && resolvedAdGroupId ? { adGroupId: resolvedAdGroupId } : {}),
          }];
        });
      } else if (aiData.creatives && aiData.creatives.length > 0) {
        // Use generic creatives and apply to each platform
        const firstCreative = aiData.creatives[0];
        campaignData.platforms.forEach(platform => {
          const spec = PLATFORM_SPECS[platform];
          if (spec) {
            aiPlatformCreatives[platform] = [{
              ...createEmptyPlatformCreative(platform),
              headlines: (firstCreative.headlines || ['']).slice(0, spec.headlines.max),
              descriptions: (firstCreative.descriptions || ['']).slice(0, spec.descriptions.max),
              ctaText: firstCreative.ctaText || spec.ctas[0]?.value || 'learn_more',
              destinationUrl: firstCreative.destinationUrl || 'https://yoursite.com',
            }];
          }
        });
      } else {
        // Create empty platformCreatives for each platform
        campaignData.platforms.forEach(platform => {
          aiPlatformCreatives[platform] = [createEmptyPlatformCreative(platform)];
        });
      }

      // Map the AI response to our campaign data structure
      const aiGeneratedData: Partial<CampaignData> = {
        name: aiData.name || `${t('campaigns.ai.campaignPrefix', 'AI Campaign')} - ${aiPrompt.substring(0, 30)}...`,
        description: aiData.description || '',
        channels: (aiData.channels || []).filter((c: string) => isEditMode || !GOOGLE_COMING_SOON_CHANNELS.has(c)),
        type: aiData.type || 'feed',
        objective: aiData.objective || 'traffic',
        budget_type: (aiData.budget_type === 'lifetime' ? 'lifetime' : 'daily') as 'daily' | 'lifetime',
        budget_amount: aiData.budget_amount || 100,
        audience: {
          age_min: aiData.audience?.age_min || 18,
          age_max: aiData.audience?.age_max || 65,
          genders: aiData.audience?.genders || ['all'],
          locations: (aiData.audience?.locations || ['Brasil']).map((loc: string | LocationWithRadius) =>
            typeof loc === 'string'
              ? (loc.toLowerCase() === 'brasil' || loc.toLowerCase() === 'brazil')
                ? { name: 'Brasil', canonicalName: 'Brasil', geoTargetId: '2076', targetType: 'Country', countryCode: 'BR', radius: 0 }
                : { name: loc, canonicalName: loc, geoTargetId: '', targetType: 'City', countryCode: 'BR', radius: 25 }
              : { ...loc, countryCode: (loc as any).countryCode || 'BR' }
          ),
          interests: aiData.audience?.interests || [],
          devices: campaignData.audience.devices,
          languages: campaignData.audience.languages || DEFAULT_GOOGLE_LANGUAGES,
        },
        creatives: aiData.creatives?.map((creative: { headlines?: string[]; descriptions?: string[]; ctaText?: string; destinationUrl?: string }) => ({
          ...createEmptyCreative(),
          headlines: creative.headlines || [],
          descriptions: creative.descriptions || [],
          ctaText: creative.ctaText || 'learn_more',
          destinationUrl: creative.destinationUrl || 'https://yoursite.com',
        })) || [{
          ...createEmptyCreative(),
          headlines: [''],
          descriptions: [''],
          ctaText: 'learn_more',
          destinationUrl: 'https://yoursite.com',
        }],
        platformCreatives: aiPlatformCreatives,
        // Ad groups locais gerados pela IA (Google). Sem isso, ad fica sem grupo
        // e canProceed bloqueia avanço.
        ...(aiGeneratedAdGroups.length > 0 ? { googleAdGroups: aiGeneratedAdGroups } : {}),
        // Google Ads extensions (sitelinks, callouts, structured snippets) — IA agora preenche
        ...(aiData.googleAssets ? {
          googleAssets: {
            callouts: Array.isArray(aiData.googleAssets.callouts) ? aiData.googleAssets.callouts.filter((c: any) => typeof c === 'string' && c.trim()) : [],
            sitelinks: Array.isArray(aiData.googleAssets.sitelinks) ? aiData.googleAssets.sitelinks.map((s: any) => ({
              title: String(s?.title || '').slice(0, 25),
              url: String(s?.url || ''),
              desc1: s?.desc1 ? String(s.desc1).slice(0, 35) : '',
              desc2: s?.desc2 ? String(s.desc2).slice(0, 35) : '',
            })).filter((s: GoogleSitelink) => s.title && s.url) : [],
            structuredSnippets: Array.isArray(aiData.googleAssets.structuredSnippets) ? aiData.googleAssets.structuredSnippets.map((ss: any) => ({
              header: String(ss?.header || ''),
              values: Array.isArray(ss?.values) ? ss.values.filter((v: any) => typeof v === 'string' && v.trim()).map((v: string) => v.slice(0, 25)) : [],
            })).filter((ss: GoogleStructuredSnippet) => ss.header && ss.values.length >= 3) : [],
            // A IA não inventa número de WhatsApp — preserva o que o usuário já preencheu
            whatsapp: campaignData.googleAssets.whatsapp || createEmptyGoogleWhatsApp(),
          },
        } : {}),
      };

      setCampaignData(prev => ({ ...prev, ...aiGeneratedData }));
      setShowAIModal(false);
      setActivePlatformTab(campaignData.platforms[0] || 'google');
      setCurrentStep(8); // Go to review

      // Check if images were generated
      const imagesGenerated = Object.values(aiPlatformCreatives).flat().some(c => c.imageUrl);
      if (imagesGenerated) {
        toast.success(t('campaigns.ai.successWithImages', 'Campaign generated with AI including images! Review and adjust as needed.'));
      } else {
        toast.success(t('campaigns.ai.success', 'Campaign generated by AI! Review and adjust as needed.'));
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.dismiss(loadingToast);
      // Limite/assinatura: o interceptor global já mostrou o toast com CTA
      if (!isHandledByInterceptor(error)) {
        toast.error(t('campaigns.ai.error', 'Error generating campaign. Please try again.'));
      }
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return campaignData.platforms.length > 0;
      case 1: return campaignData.channels.length > 0;
      case 2: return campaignData.objective !== '';
      case 3: return campaignData.name.trim() !== '';
      case 4: {
        if (!(campaignData.budget_amount > 0)) return false;
        // Google: ROAS alvo exige a meta; portfólio exige uma estratégia escolhida.
        if (campaignData.platforms.includes('google') && campaignData.bid_strategy === 'automatic') {
          if (campaignData.google_bid_mode === 'target_roas' && !(Number(campaignData.google_target_roas_pct) > 0)) return false;
          if (campaignData.google_bid_mode === 'portfolio' && !campaignData.google_portfolio_strategy) return false;
        }
        return true;
      }
      case 5: return true; // Pixel selection is optional
      case 6: {
        // If remarketing campaign, at least one audience is required
        if (campaignData.objective === 'remarketing') {
          return selectedAudiences.length > 0;
        }
        return true; // For other objectives, audience is optional
      }
      case 7: {
        // Validate platformCreatives - each platform must have at least one valid creative
        const platforms = campaignData.platforms;
        if (platforms.length === 0) return false;

        return platforms.every(platform => {
          const creatives = campaignData.platformCreatives[platform];
          if (!creatives || creatives.length === 0) return false;
          // Meta Leads com destinos como instant_form/whatsapp/messenger/phone_call/app
          // constroem o link internamente (lead_gen_form_id, wa.me, m.me, tel:, etc),
          // então destinationUrl não deve ser exigida nesses casos.
          const skipUrlRequirement = (platform === 'meta'
            && campaignData.objective === 'leads'
            && !!campaignData.meta_lead_destination
            && campaignData.meta_lead_destination !== 'website')
            // Google com destino WhatsApp: o backend gera o wa.me, o usuário
            // não precisa (nem costuma ter) um site pra preencher aqui.
            || (platform === 'google'
              && campaignData.googleAssets.whatsapp?.enabled === true
              && campaignData.googleAssets.whatsapp?.use_as_destination === true
              && !!(campaignData.googleAssets.whatsapp?.phone_number || '').replace(/\D/g, ''));
          return creatives.every(creative =>
            creative.headlines.some(h => h.trim() !== '') &&
            creative.descriptions.some(d => d.trim() !== '') &&
            (skipUrlRequirement || creative.destinationUrl.trim() !== '') &&
            // Google: todo criativo precisa ter um ad group atribuído. Backend
            // distribui as RSAs entre os ad groups (limite Google = 3/grupo).
            (platform !== 'google' || (!!creative.adGroupId && campaignData.googleAdGroups.some(ag => ag.id === creative.adGroupId)))
          );
        });
      }
      case 8: return true; // Review
      default: return false;
    }
  };

  const handleNext = () => {
    if (canProceed() && currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      // Se entrou direto de uma plataforma e está no step 1, permite voltar ao step 0
      // para adicionar mais plataformas, e reseta a flag
      if (currentStep === 1 && enteredFromPlatform) {
        setEnteredFromPlatform(false);
      }
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/campaigns');
    }
  };

  const togglePlatform = (platform: string) => {
    setCampaignData(prev => {
      const newPlatforms = prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform];

      // Also remove channels from deselected platform
      const newChannels = prev.channels.filter(ch => {
        const channel = [...GOOGLE_CHANNELS, ...META_CHANNELS, ...LINKEDIN_CHANNELS].find(c => c.id === ch);
        return channel && newPlatforms.includes(channel.platform);
      });

      // Clean up platformCreatives for deselected platforms
      const newPlatformCreatives = { ...prev.platformCreatives };
      Object.keys(newPlatformCreatives).forEach(p => {
        if (!newPlatforms.includes(p)) {
          delete newPlatformCreatives[p];
        }
      });

      return { ...prev, platforms: newPlatforms, channels: newChannels, platformCreatives: newPlatformCreatives };
    });
  };

  const toggleChannel = (channelId: string) => {
    setCampaignData(prev => ({
      ...prev,
      channels: prev.channels.includes(channelId)
        ? prev.channels.filter(c => c !== channelId)
        : [...prev.channels, channelId]
    }));
  };

  const addCreative = () => {
    setCampaignData(prev => ({
      ...prev,
      creatives: [...prev.creatives, createEmptyCreative()]
    }));
    setActiveCreativeIndex(campaignData.creatives.length);
  };

  const removeCreative = (index: number) => {
    if (campaignData.creatives.length <= 1) {
      toast.error(t('campaigns.new.needAtLeastOne', 'You need at least one creative'));
      return;
    }
    setCampaignData(prev => ({
      ...prev,
      creatives: prev.creatives.filter((_, i) => i !== index)
    }));
    if (activeCreativeIndex >= index && activeCreativeIndex > 0) {
      setActiveCreativeIndex(activeCreativeIndex - 1);
    }
  };

  const updateCreative = (index: number, updates: Partial<Creative>) => {
    setCampaignData(prev => ({
      ...prev,
      creatives: prev.creatives.map((c, i) => i === index ? { ...c, ...updates } : c)
    }));
  };

  const addHeadline = (creativeIndex: number) => {
    const creative = campaignData.creatives[creativeIndex];
    if (creative.headlines.length < 15) {
      updateCreative(creativeIndex, { headlines: [...creative.headlines, ''] });
    }
  };

  const removeHeadline = (creativeIndex: number, headlineIndex: number) => {
    const creative = campaignData.creatives[creativeIndex];
    if (creative.headlines.length > 1) {
      updateCreative(creativeIndex, {
        headlines: creative.headlines.filter((_, i) => i !== headlineIndex)
      });
    }
  };

  const updateHeadline = (creativeIndex: number, headlineIndex: number, value: string) => {
    const creative = campaignData.creatives[creativeIndex];
    const newHeadlines = [...creative.headlines];
    newHeadlines[headlineIndex] = value;
    updateCreative(creativeIndex, { headlines: newHeadlines });
  };

  const addDescription = (creativeIndex: number) => {
    const creative = campaignData.creatives[creativeIndex];
    if (creative.descriptions.length < 5) {
      updateCreative(creativeIndex, { descriptions: [...creative.descriptions, ''] });
    }
  };

  const removeDescription = (creativeIndex: number, descIndex: number) => {
    const creative = campaignData.creatives[creativeIndex];
    if (creative.descriptions.length > 1) {
      updateCreative(creativeIndex, {
        descriptions: creative.descriptions.filter((_, i) => i !== descIndex)
      });
    }
  };

  const updateDescription = (creativeIndex: number, descIndex: number, value: string) => {
    const creative = campaignData.creatives[creativeIndex];
    const newDescriptions = [...creative.descriptions];
    newDescriptions[descIndex] = value;
    updateCreative(creativeIndex, { descriptions: newDescriptions });
  };

  const handleImageUpload = async (creativeIndex: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Show local preview immediately
      const previewUrl = URL.createObjectURL(file);
      updateCreative(creativeIndex, { imageUrl: previewUrl, imageFile: file, isGeneratingImage: true });
      try {
        const s3Url = await uploadImageToS3(file);
        URL.revokeObjectURL(previewUrl);
        updateCreative(creativeIndex, { imageUrl: s3Url, imageFile: null, isGeneratingImage: false });
      } catch (error) {
        console.error('Error uploading image to S3:', error);
        URL.revokeObjectURL(previewUrl);
        updateCreative(creativeIndex, { imageUrl: null, imageFile: null, isGeneratingImage: false });
        toast.error(t('campaigns.new.imageUploadError', 'Error uploading image'));
      }
    }
  };

  const handleGenerateImage = async (creativeIndex: number) => {
    const creative = campaignData.creatives[creativeIndex];
    const prompt = creative.imagePrompt?.trim();

    if (!prompt) {
      toast.error(t('campaigns.new.enterImagePrompt', 'Please describe the image you want to generate'));
      return;
    }

    updateCreative(creativeIndex, { isGeneratingImage: true });
    try {
      const response = await imageStudioService.generateImage({
        prompt,
        style: (creative.imageStyle as any) || 'realistic',
        size: '1024x1024'
      });

      if (response.success && response.data) {
        const imageUrl = response.data.original_url || response.data.edited_url;
        if (imageUrl) {
          updateCreative(creativeIndex, {
            imageUrl,
            imageFile: null,
            isGeneratingImage: false
          });
          toast.success(t('campaigns.new.imageGenerated', 'Image generated successfully!'));
        } else {
          throw new Error('No image URL returned');
        }
      } else {
        throw new Error('Error generating image');
      }
    } catch (error: any) {
      console.error('Error generating image:', error);
      updateCreative(creativeIndex, { isGeneratingImage: false });
      if (!isHandledByInterceptor(error)) {
        const errMsg = error.response?.data?.message || error.message || t('campaigns.new.imageError', 'Error generating image');
        toast.error(errMsg);
      }
    }
  };

  // Save creative to library
  const handleSaveToLibrary = async (creativeIndex: number) => {
    const creative = campaignData.creatives[creativeIndex];

    if (!creative.headlines.some(h => h.trim()) || !creative.descriptions.some(d => d.trim())) {
      toast.error(t('campaigns.new.fillCreativeFirst', 'Fill in at least one headline and description'));
      return;
    }

    setSavingCreative(true);
    try {
      const creativeName = creative.headlines.find(h => h.trim()) || t('campaigns.new.untitledCreative', 'Untitled Creative');

      // Upload image to S3 if it's a local file
      let imageUrl = creative.imageUrl;
      if (creative.imageFile) {
        imageUrl = await uploadImageToS3(creative.imageFile);
        updateCreative(creativeIndex, { imageUrl, imageFile: null });
      }
      // Sanitize: never persist blob/data URLs
      if (imageUrl && (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:'))) {
        imageUrl = null;
      }

      const response = await creativeService.createCreative({
        name: creativeName.substring(0, 100),
        headlines: creative.headlines.filter(h => h.trim()),
        descriptions: creative.descriptions.filter(d => d.trim()),
        image_url: imageUrl,
        video_url: creative.videoUrl || null,
        cta_text: creative.ctaText,
        destination_url: creative.destinationUrl,
        platform: campaignData.platforms,
        tags: []
      });

      if (response.success) {
        toast.success(t('campaigns.new.savedToLibrary', 'Creative saved to library!'));
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      console.error('Error saving creative:', error);
      toast.error(error.message || t('campaigns.new.errorSavingCreative', 'Error saving creative'));
    } finally {
      setSavingCreative(false);
    }
  };

  // Save platform creative to library
  // Open the save-to-library modal pre-filled with first headline + suggested name
  const handleSavePlatformCreativeToLibrary = (platform: string) => {
    const creative = getActiveCreative(platform);
    if (!creative || !creative.headlines.some(h => h.trim()) || !creative.descriptions.some(d => d.trim())) {
      toast.error(t('campaigns.new.fillCreativeFirst', 'Fill in at least one headline and description'));
      return;
    }
    const firstHeadline = creative.headlines.find(h => h.trim()) || '';
    setSaveModalName(firstHeadline.substring(0, 100));
    setSaveModalTags([]);
    setSaveModalPlatform(platform);
  };

  // Actually save (called from the modal)
  const handleConfirmSaveToLibrary = async () => {
    if (!saveModalPlatform) return;
    const platform = saveModalPlatform;
    const creative = getActiveCreative(platform);
    if (!creative) return;
    const finalName = saveModalName.trim();
    if (!finalName) {
      toast.error(t('campaigns.new.creativeNameRequired', 'Nome do creative é obrigatório'));
      return;
    }

    setSavingCreative(true);
    try {
      // Upload image to S3 if it's a local file
      let imageUrl = creative.imageUrl;
      if (creative.imageFile) {
        imageUrl = await uploadImageToS3(creative.imageFile);
        updatePlatformCreative(platform, { imageUrl, imageFile: null });
      }
      // Sanitize: never persist blob/data URLs
      if (imageUrl && (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:'))) {
        imageUrl = null;
      }

      // Carry platform-specific identity (FB page + IG + LinkedIn org) with
      // the library entry so reloading it into another campaign reapplies the
      // same publisher choice automatically.
      const settings: Record<string, string> = {};
      if (platform === 'meta') {
        if (creative.metaPageId) settings.meta_page_id = creative.metaPageId;
        if (creative.metaInstagramId !== undefined) settings.meta_instagram_id = creative.metaInstagramId;
      }
      if (platform === 'linkedin' && creative.linkedinOrgUrn) {
        settings.linkedin_org_urn = creative.linkedinOrgUrn;
      }

      const response = await creativeService.createCreative({
        name: finalName.substring(0, 100),
        headlines: creative.headlines.filter(h => h.trim()),
        descriptions: creative.descriptions.filter(d => d.trim()),
        image_url: imageUrl,
        video_url: creative.videoUrl || null,
        cta_text: creative.ctaText,
        destination_url: creative.destinationUrl,
        platform: [platform],
        tags: saveModalTags.filter(tg => tg && tg.trim()),
        settings: Object.keys(settings).length > 0 ? settings : null
      });

      if (response.success) {
        toast.success(t('campaigns.new.savedToLibrary', 'Creative saved to library!'));
        setSaveModalPlatform(null);
        setSaveModalName('');
        setSaveModalTags([]);
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      console.error('Error saving creative:', error);
      toast.error(error.message || t('campaigns.new.errorSavingCreative', 'Error saving creative'));
    } finally {
      setSavingCreative(false);
    }
  };

  // Load library creative to a specific platform — clip to destination platform limits
  const handleLoadLibraryToPlatform = (libraryCreative: LibraryCreative) => {
    if (!activePlatformTab) return;
    const spec = PLATFORM_SPECS[activePlatformTab];
    if (!spec) return;

    // Clip headlines to destination platform's char limit and max count
    const sourceHeadlines = libraryCreative.headlines?.length ? libraryCreative.headlines : [''];
    const clippedHeadlines = sourceHeadlines
      .slice(0, spec.headlines.max)
      .map(h => h.length > spec.headlines.charLimit ? h.substring(0, spec.headlines.charLimit) : h);
    // Pad to minimum required
    while (clippedHeadlines.length < spec.headlines.min) clippedHeadlines.push('');

    const sourceDescriptions = libraryCreative.descriptions?.length ? libraryCreative.descriptions : [''];
    const clippedDescriptions = sourceDescriptions
      .slice(0, spec.descriptions.max)
      .map(d => d.length > spec.descriptions.charLimit ? d.substring(0, spec.descriptions.charLimit) : d);
    while (clippedDescriptions.length < spec.descriptions.min) clippedDescriptions.push('');

    // Map CTA — keep if valid for platform, else use platform default
    const validCta = spec.ctas.find(c => c.value === libraryCreative.cta_text);
    const ctaText = validCta?.value || spec.ctas[0]?.value || 'learn_more';

    // Restore platform-specific publisher identity (FB page / IG / LinkedIn org)
    // from the saved settings when the target platform matches.
    const settings = (libraryCreative as any).settings || {};
    const updates: Partial<PlatformCreative> = {
      headlines: clippedHeadlines,
      descriptions: clippedDescriptions,
      imageUrl: libraryCreative.image_url || null,
      imageFile: null,
      videoUrl: libraryCreative.video_url || '',
      ctaText,
      destinationUrl: libraryCreative.destination_url || '',
      isGeneratingImage: false,
    };
    if (activePlatformTab === 'meta') {
      if (settings.meta_page_id) updates.metaPageId = settings.meta_page_id;
      if (settings.meta_instagram_id !== undefined) updates.metaInstagramId = settings.meta_instagram_id;
    }
    if (activePlatformTab === 'linkedin' && settings.linkedin_org_urn) {
      updates.linkedinOrgUrn = settings.linkedin_org_urn;
    }
    updatePlatformCreative(activePlatformTab, updates);

    // Warn if anything was clipped
    const anyClipped = sourceHeadlines.some((h, i) => h !== clippedHeadlines[i] && i < spec.headlines.max)
      || sourceDescriptions.some((d, i) => d !== clippedDescriptions[i] && i < spec.descriptions.max)
      || sourceHeadlines.length > spec.headlines.max
      || sourceDescriptions.length > spec.descriptions.max
      || (libraryCreative.cta_text && !validCta);

    if (anyClipped) {
      toast.success(t('campaigns.new.creativeLoadedClipped', 'Creative carregado e ajustado pros limites da plataforma'), { duration: 4000 });
    } else {
      toast.success(t('campaigns.new.creativeLoaded', 'Creative loaded from library!'));
    }
    setShowLibraryModal(false);
  };

  // ===========================================
  // PLATFORM CREATIVE FUNCTIONS
  // ===========================================

  const updatePlatformCreative = (platform: string, updates: Partial<PlatformCreative>, creativeIdx?: number) => {
    setCampaignData(prev => {
      const arr = prev.platformCreatives[platform] || [];
      const idx = creativeIdx ?? (activeCreativeIdx[platform] ?? 0);
      if (arr.length === 0) return prev;
      const safeIdx = Math.min(idx, arr.length - 1);
      const newArr = [...arr];
      newArr[safeIdx] = { ...newArr[safeIdx], ...updates };
      return { ...prev, platformCreatives: { ...prev.platformCreatives, [platform]: newArr } };
    });
  };

  // Add a new empty creative to a platform (multi-ad support)
  const addPlatformCreative = (platform: string) => {
    setCampaignData(prev => {
      const arr = prev.platformCreatives[platform] || [];
      const newCreative = createEmptyPlatformCreative(platform);
      return {
        ...prev,
        platformCreatives: { ...prev.platformCreatives, [platform]: [...arr, newCreative] }
      };
    });
    // Activate the new creative
    setActiveCreativeIdx(prev => {
      const currentLen = (campaignData.platformCreatives[platform] || []).length;
      return { ...prev, [platform]: currentLen };
    });
  };

  // Remove a creative from a platform (keeps at least one)
  const removePlatformCreative = (platform: string, creativeIdx: number) => {
    setCampaignData(prev => {
      const arr = prev.platformCreatives[platform] || [];
      if (arr.length <= 1) return prev;
      const newArr = arr.filter((_, i) => i !== creativeIdx);
      return { ...prev, platformCreatives: { ...prev.platformCreatives, [platform]: newArr } };
    });
    setActiveCreativeIdx(prev => {
      const current = prev[platform] ?? 0;
      const newIdx = current >= creativeIdx && current > 0 ? current - 1 : current;
      return { ...prev, [platform]: newIdx };
    });
  };

  const addPlatformHeadline = (platform: string) => {
    const creative = getActiveCreative(platform);
    const spec = PLATFORM_SPECS[platform];
    if (creative && creative.headlines.length < spec.headlines.max) {
      const newHeadlines = [...creative.headlines, ''];
      const nextPins = platform === 'google'
        ? [...(creative.headlinePins || Array(creative.headlines.length).fill(null)), null]
        : creative.headlinePins;
      updatePlatformCreative(platform, { headlines: newHeadlines, headlinePins: nextPins });
    }
  };

  const removePlatformHeadline = (platform: string, index: number) => {
    const creative = getActiveCreative(platform);
    const spec = PLATFORM_SPECS[platform];
    if (creative && creative.headlines.length > spec.headlines.min) {
      const newHeadlines = creative.headlines.filter((_, i) => i !== index);
      const nextPins = platform === 'google'
        ? (creative.headlinePins || []).filter((_, i) => i !== index)
        : creative.headlinePins;
      updatePlatformCreative(platform, { headlines: newHeadlines, headlinePins: nextPins });
    }
  };

  const updatePlatformHeadline = (platform: string, index: number, value: string) => {
    const creative = getActiveCreative(platform);
    if (creative) {
      const newHeadlines = [...creative.headlines];
      newHeadlines[index] = value;
      updatePlatformCreative(platform, { headlines: newHeadlines });
    }
  };

  /**
   * Toggle pin for a Google headline. Simple rule:
   *  - If already pinned → unpin (null).
   *  - If unpinned and fewer than 3 are pinned → assign max(existing)+1
   *    (falls back to the first free slot 1..3 when max is already 3).
   *  - If 3 are already pinned → block (show toast).
   */
  const togglePlatformHeadlinePin = (platform: string, index: number) => {
    if (platform !== 'google') return;
    const creative = getActiveCreative(platform);
    if (!creative) return;
    const pins = [...(creative.headlinePins || Array(creative.headlines.length).fill(null))];
    const current = pins[index];

    if (current != null) {
      pins[index] = null;
      updatePlatformCreative(platform, { headlinePins: pins });
      return;
    }

    // Block if already 3 pinned
    const used = pins.filter((p): p is 1 | 2 | 3 => p === 1 || p === 2 || p === 3);
    if (used.length >= 3) {
      toast.error(t('campaigns.new.pinMaxReached', 'Máximo de 3 títulos fixados. Desfixe um primeiro.'));
      return;
    }

    const max = used.length > 0 ? Math.max(...used) : 0;
    let next: 1 | 2 | 3;
    if (max < 3) {
      next = (max + 1) as 1 | 2 | 3;
    } else {
      // Max is already 3 but fewer than 3 slots used (there's a gap) → fill smallest free
      next = (([1, 2, 3].find((p) => !used.includes(p as 1 | 2 | 3))) as 1 | 2 | 3) ?? 1;
    }
    pins[index] = next;
    updatePlatformCreative(platform, { headlinePins: pins });
  };

  const addPlatformDescription = (platform: string) => {
    const creative = getActiveCreative(platform);
    const spec = PLATFORM_SPECS[platform];
    if (creative && creative.descriptions.length < spec.descriptions.max) {
      updatePlatformCreative(platform, { descriptions: [...creative.descriptions, ''] });
    }
  };

  const removePlatformDescription = (platform: string, index: number) => {
    const creative = getActiveCreative(platform);
    const spec = PLATFORM_SPECS[platform];
    if (creative && creative.descriptions.length > spec.descriptions.min) {
      updatePlatformCreative(platform, {
        descriptions: creative.descriptions.filter((_, i) => i !== index)
      });
    }
  };

  const updatePlatformDescription = (platform: string, index: number, value: string) => {
    const creative = getActiveCreative(platform);
    if (creative) {
      const newDescriptions = [...creative.descriptions];
      newDescriptions[index] = value;
      updatePlatformCreative(platform, { descriptions: newDescriptions });
    }
  };

  const handlePlatformImageUpload = async (platform: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Show local preview immediately
      const previewUrl = URL.createObjectURL(file);
      updatePlatformCreative(platform, { imageUrl: previewUrl, imageFile: file, isGeneratingImage: true });
      const targetIdx = getActiveIdx(platform);
      try {
        const s3Url = await uploadImageToS3(file);
        URL.revokeObjectURL(previewUrl);
        // Only update if the preview URL is still current (user hasn't selected another image)
        setCampaignData(prev => {
          const arr = prev.platformCreatives[platform] || [];
          const current = arr[targetIdx];
          if (current && (current.imageUrl === previewUrl || current.imageFile === file)) {
            const newArr = [...arr];
            newArr[targetIdx] = { ...current, imageUrl: s3Url, imageFile: null, isGeneratingImage: false };
            return { ...prev, platformCreatives: { ...prev.platformCreatives, [platform]: newArr } };
          }
          return prev; // Another image was selected, don't overwrite
        });
      } catch (error) {
        console.error('Error uploading image to S3:', error);
        URL.revokeObjectURL(previewUrl);
        setCampaignData(prev => {
          const arr = prev.platformCreatives[platform] || [];
          const current = arr[targetIdx];
          if (current && (current.imageUrl === previewUrl || current.imageFile === file)) {
            const newArr = [...arr];
            newArr[targetIdx] = { ...current, imageUrl: null, imageFile: null, isGeneratingImage: false };
            return { ...prev, platformCreatives: { ...prev.platformCreatives, [platform]: newArr } };
          }
          return prev;
        });
        toast.error(t('campaigns.new.imageUploadError', 'Error uploading image for {{platform}}', { platform }));
      }
    }
  };

  const handleGeneratePlatformImage = async (platform: string) => {
    const creative = getActiveCreative(platform);
    const prompt = creative?.imagePrompt?.trim();

    if (!prompt) {
      toast.error(t('campaigns.new.enterImagePrompt', 'Please describe the image you want to generate'));
      return;
    }

    if (!creative) return;
    updatePlatformCreative(platform, { isGeneratingImage: true });
    try {
      const response = await imageStudioService.generateImage({
        prompt,
        style: (creative.imageStyle as any) || 'realistic',
        size: '1024x1024'
      });

      if (response.success && response.data) {
        const imageUrl = response.data.original_url || response.data.edited_url;
        if (imageUrl) {
          updatePlatformCreative(platform, {
            imageUrl,
            imageFile: null,
            isGeneratingImage: false
          });
          toast.success(t('campaigns.new.imageGenerated', 'Image generated successfully!'));
        } else {
          throw new Error('No image URL returned');
        }
      } else {
        throw new Error('Error generating image');
      }
    } catch (error: any) {
      console.error('Error generating image:', error);
      updatePlatformCreative(platform, { isGeneratingImage: false });
      if (!isHandledByInterceptor(error)) {
        const errMsg = error.response?.data?.message || error.message || t('campaigns.new.imageError', 'Error generating image');
        toast.error(errMsg);
      }
    }
  };

  // ============================================
  // Google Ads asset helpers (campaign-level)
  // ============================================
  const updateGoogleAssets = (updates: Partial<GoogleAssets>) => {
    setCampaignData(prev => ({
      ...prev,
      googleAssets: { ...prev.googleAssets, ...updates },
    }));
  };

  // ===== Google Ad Groups (CRUD inline) =====
  // Mantém lista local de ad groups. Cada criativo Google referencia o id de um.
  // Backend distribui RSAs nos ad groups respeitando o limite de 3 ENABLED por grupo.
  const addGoogleAdGroup = (label?: string): string => {
    const newAg = createEmptyAdGroup(label || `Grupo de Anúncios ${campaignData.googleAdGroups.length + 1}`);
    setCampaignData(prev => ({ ...prev, googleAdGroups: [...prev.googleAdGroups, newAg] }));
    return newAg.id;
  };

  const renameGoogleAdGroup = (id: string, name: string) => {
    setCampaignData(prev => ({
      ...prev,
      googleAdGroups: prev.googleAdGroups.map(ag => ag.id === id ? { ...ag, name } : ag),
    }));
  };

  const removeGoogleAdGroup = (id: string): boolean => {
    // Bloqueia se algum criativo Google ainda referencia este grupo
    const inUse = Object.values(campaignData.platformCreatives).flat().some(c => c.adGroupId === id);
    if (inUse) {
      toast.error(t('campaigns.new.adGroupInUse', 'Grupo em uso por algum anúncio. Mude o anúncio antes de remover.'));
      return false;
    }
    setCampaignData(prev => ({ ...prev, googleAdGroups: prev.googleAdGroups.filter(ag => ag.id !== id) }));
    return true;
  };

  const addCallout = () => {
    if (campaignData.googleAssets.callouts.length < 20) {
      updateGoogleAssets({ callouts: [...campaignData.googleAssets.callouts, ''] });
    }
  };

  const updateCallout = (idx: number, value: string) => {
    const next = [...campaignData.googleAssets.callouts];
    next[idx] = value;
    updateGoogleAssets({ callouts: next });
  };

  const removeCallout = (idx: number) => {
    updateGoogleAssets({ callouts: campaignData.googleAssets.callouts.filter((_, i) => i !== idx) });
  };

  const addStructuredSnippet = () => {
    // Pre-create 3 empty value slots so the user sees Google's minimum requirement upfront.
    updateGoogleAssets({
      structuredSnippets: [...campaignData.googleAssets.structuredSnippets, { header: 'Service catalog', values: ['', '', ''] }],
    });
  };

  const updateStructuredSnippet = (idx: number, patch: Partial<GoogleStructuredSnippet>) => {
    const next = [...campaignData.googleAssets.structuredSnippets];
    next[idx] = { ...next[idx], ...patch };
    updateGoogleAssets({ structuredSnippets: next });
  };

  const removeStructuredSnippet = (idx: number) => {
    updateGoogleAssets({
      structuredSnippets: campaignData.googleAssets.structuredSnippets.filter((_, i) => i !== idx),
    });
  };

  const addSitelink = () => {
    if (campaignData.googleAssets.sitelinks.length < 10) {
      updateGoogleAssets({
        sitelinks: [...campaignData.googleAssets.sitelinks, { title: '', url: '', desc1: '', desc2: '' }],
      });
    }
  };

  const updateSitelink = (idx: number, patch: Partial<GoogleSitelink>) => {
    const next = [...campaignData.googleAssets.sitelinks];
    next[idx] = { ...next[idx], ...patch };
    updateGoogleAssets({ sitelinks: next });
  };

  const removeSitelink = (idx: number) => {
    updateGoogleAssets({ sitelinks: campaignData.googleAssets.sitelinks.filter((_, i) => i !== idx) });
  };

  // ===== WhatsApp (extensão de mensagem do Google) =====
  const googleWhatsApp = campaignData.googleAssets.whatsapp || createEmptyGoogleWhatsApp();
  const updateGoogleWhatsApp = (patch: Partial<GoogleWhatsAppAsset>) => {
    updateGoogleAssets({ whatsapp: { ...googleWhatsApp, ...patch } });
  };
  // Preview do link que o usuário vai ver no anúncio (mesma regra do backend:
  // DDI + número, sem o "+", com a mensagem inicial no ?text=).
  const googleWhatsAppLink = (() => {
    const country = GOOGLE_WHATSAPP_COUNTRIES.find(c => c.iso === googleWhatsApp.country_code);
    const dial = country?.dial || '';
    let digits = (googleWhatsApp.phone_number || '').replace(/\D/g, '');
    const isNational = (len: number) => (country ? len >= country.nat[0] && len <= country.nat[1] : len >= 8);
    if (dial && digits.startsWith(dial) && !isNational(digits.length) && isNational(digits.length - dial.length)) {
      digits = digits.slice(dial.length);
    }
    if (!digits || !dial) return '';
    const msg = googleWhatsApp.starter_message?.trim();
    return `https://wa.me/${dial}${digits}${msg ? `?text=${encodeURIComponent(msg)}` : ''}`;
  })();

  const updatePlatformKeywords = (platform: string, keywords: string[]) => {
    updatePlatformCreative(platform, { keywords });
  };

  // Render platform-specific creative form
  const renderPlatformCreativeForm = (platform: string) => {
    const creatives = getCreativesArr(platform);
    const spec = PLATFORM_SPECS[platform];
    const activeIdx = getActiveIdx(platform);
    const creative = creatives[Math.min(activeIdx, creatives.length - 1)];

    if (!creative || !spec) return null;

    const PlatformIcon = spec.icon;

    // Determine media requirements based on the channels selected for THIS platform.
    // If any selected channel is video-only (Reels, YouTube, Shorts, TikTok), video
    // becomes required and image isn't applicable for that placement.
    const platformChannels = (campaignData.channels || []).filter(channelId => {
      const chSpec = (CHANNEL_SPECS as any)[channelId];
      if (!chSpec) return false;
      // google platform covers google_* and youtube channels
      if (platform === 'google') return channelId.startsWith('google_') || channelId === 'youtube';
      if (platform === 'meta') return channelId.startsWith('facebook_') || channelId.startsWith('instagram_');
      if (platform === 'linkedin') return channelId.startsWith('linkedin_');
      return false;
    });
    const videoRequired = platformChannels.some(channelId => {
      const chSpec = (CHANNEL_SPECS as any)[channelId];
      return chSpec && Array.isArray(chSpec.supportedMedia) && chSpec.supportedMedia.length === 1 && chSpec.supportedMedia[0] === 'video';
    });
    // Image section stays visible when ANY selected channel accepts image OR video
    // (video-only channels like Reels/YouTube still allow a cover image). Only hide
    // when every channel is text-only (e.g. google_search → supportedMedia: ['none']).
    const imageSupported = platformChannels.length === 0
      ? true // no channel selected yet — default to platform spec
      : platformChannels.some(channelId => {
          const chSpec = (CHANNEL_SPECS as any)[channelId];
          if (!chSpec || !Array.isArray(chSpec.supportedMedia)) return false;
          return chSpec.supportedMedia.includes('image') || chSpec.supportedMedia.includes('video');
        });
    // Names of channels driving the "video required" constraint, for user-facing hint text
    const videoRequiredChannelNames = platformChannels
      .filter(channelId => {
        const chSpec = (CHANNEL_SPECS as any)[channelId];
        return chSpec && Array.isArray(chSpec.supportedMedia) && chSpec.supportedMedia.length === 1 && chSpec.supportedMedia[0] === 'video';
      })
      .map(channelId => {
        const names: Record<string, string> = {
          youtube: 'YouTube',
          instagram_reels: 'Instagram Reels'
        };
        return names[channelId] || channelId;
      });

    // Effective char limits — take the MAX among selected channels of this
    // platform. Example: Meta platform default is 125 chars (safe for feed
    // preview), but Instagram Reels actually allows 2200. When Reels is
    // selected, raise the limit so the user isn't artificially capped.
    const effectiveHeadlineLimit = (() => {
      const channelLimits = platformChannels
        .map((c) => (CHANNEL_SPECS as any)[c]?.headlines?.charLimit)
        .filter((n): n is number => typeof n === 'number' && n > 0);
      return channelLimits.length > 0
        ? Math.max(spec.headlines.charLimit, ...channelLimits)
        : spec.headlines.charLimit;
    })();
    const effectiveDescriptionLimit = (() => {
      const channelLimits = platformChannels
        .map((c) => (CHANNEL_SPECS as any)[c]?.descriptions?.charLimit)
        .filter((n): n is number => typeof n === 'number' && n > 0);
      return channelLimits.length > 0
        ? Math.max(spec.descriptions.charLimit, ...channelLimits)
        : spec.descriptions.charLimit;
    })();

    return (
      <div className="space-y-6">
        {/* Multi-creative tabs: Ad 1, Ad 2, ..., + Add */}
        {creatives.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap pb-4 border-b border-gray-200 dark:border-gray-700">
            {creatives.map((_, idx) => {
              const isActive = idx === activeIdx;
              const isValid = creatives[idx].headlines.some(h => h.trim()) &&
                              creatives[idx].descriptions.some(d => d.trim()) &&
                              creatives[idx].destinationUrl.trim() !== '';
              const canRemove = creatives.length > 1;
              return (
                <div
                  key={idx}
                  onClick={() => setActiveCreativeIdx(prev => ({ ...prev, [platform]: idx }))}
                  className={`inline-flex items-center gap-1.5 h-9 ${canRemove ? 'pl-3 pr-2' : 'px-3'} text-sm font-medium rounded-lg border cursor-pointer transition-colors ${
                    isActive
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span>{t('campaigns.new.adLabel', 'Anúncio')} {idx + 1}</span>
                  {isValid && <CheckCircle className={`w-3.5 h-3.5 ${isActive ? 'text-white/90' : 'text-green-500'}`} />}
                  {canRemove && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(t('campaigns.new.confirmRemoveAd', 'Remover este anúncio?'))) {
                          removePlatformCreative(platform, idx);
                        }
                      }}
                      className={`flex items-center justify-center w-5 h-5 rounded transition-colors ${
                        isActive
                          ? 'text-white/80 hover:bg-white/20 hover:text-white'
                          : 'text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/30'
                      }`}
                      title={t('campaigns.new.removeAd', 'Remover anúncio')}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
            {creatives.length < spec.maxAdsPerCampaign ? (
              <button
                type="button"
                onClick={() => addPlatformCreative(platform)}
                className="inline-flex items-center gap-1 h-9 px-3 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 border border-dashed border-primary-300 dark:border-primary-700 rounded-lg transition-colors"
                title={t('campaigns.new.addAdTooltip', 'Adicionar anúncio ({{current}}/{{max}})', { current: creatives.length, max: spec.maxAdsPerCampaign })}
              >
                <Plus className="w-3.5 h-3.5" />
                {t('campaigns.new.addAd', 'Adicionar anúncio')}
                <span className="text-xs opacity-70 ml-0.5">
                  {creatives.length}/{spec.maxAdsPerCampaign}
                </span>
              </button>
            ) : (
              <span className="inline-flex items-center gap-1 h-9 px-3 text-xs text-gray-500 dark:text-gray-400 italic" title={t('campaigns.new.adLimitReachedTooltip', 'Limite recomendado pela plataforma')}>
                {t('campaigns.new.adLimitReached', 'Limite atingido')} ({creatives.length}/{spec.maxAdsPerCampaign})
              </span>
            )}
          </div>
        )}

        {/* Library Actions */}
        <div className="flex items-center gap-2 pb-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowLibraryModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FolderOpen className="w-4 h-4" />
            {t('campaigns.new.useFromLibrary', 'Usar da Biblioteca')}
          </button>
          <button
            onClick={() => handleSavePlatformCreativeToLibrary(platform)}
            disabled={savingCreative}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg transition-colors disabled:opacity-50"
          >
            {savingCreative ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('campaigns.new.saveToLibrary', 'Salvar na Biblioteca')}
          </button>
        </div>

        {/* Meta Leads destination config — fields specific to the destination
            chosen in step Details (URL vai no próprio campo "URL de destino"
            do criativo abaixo). Shows only for Meta + objective=leads + a
            destination that needs extra config. */}
        {platform === 'meta' && campaignData.objective === 'leads' && campaignData.meta_lead_destination && campaignData.meta_lead_destination !== 'website' && (() => {
          const cfg = campaignData.meta_lead_dest_config || {};
          const setCfg = (patch: Record<string, string>) =>
            setCampaignData(prev => ({ ...prev, meta_lead_dest_config: { ...(prev.meta_lead_dest_config || {}), ...patch } }));
          const destLabels: Record<string, string> = {
            instant_form: t('campaigns.new.leadDest.instantForm', 'Formulário instantâneo') as string,
            messenger: t('campaigns.new.leadDest.messenger', 'Messenger') as string,
            app: t('campaigns.new.leadDest.app', 'Aplicativo') as string,
            whatsapp: t('campaigns.new.leadDest.whatsapp', 'WhatsApp') as string,
            phone_call: t('campaigns.new.leadDest.phoneCall', 'Chamadas') as string,
          };
          return (
            <div className="p-3 rounded-lg border-2 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 space-y-3">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  {t('campaigns.new.leadDestConfigTitle', 'Configuração do destino')}: {destLabels[campaignData.meta_lead_destination] || campaignData.meta_lead_destination}
                </span>
              </div>
              {campaignData.meta_lead_destination === 'instant_form' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('campaigns.new.leadDestConfig.leadformIdLabel', 'ID do formulário (lead form)')}
                  </label>
                  <input
                    type="text"
                    value={cfg.leadform_id || ''}
                    onChange={(e) => setCfg({ leadform_id: e.target.value })}
                    placeholder="Ex: 123456789012345"
                    className="input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('campaigns.new.leadDestConfig.leadformIdHint', 'Crie o formulário no Meta Business Suite (Publisher Tools > Instant Forms) e cole o ID aqui.')}
                  </p>
                </div>
              )}
              {campaignData.meta_lead_destination === 'messenger' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('campaigns.new.leadDestConfig.welcomeLabel', 'Mensagem de boas-vindas')}
                  </label>
                  <textarea
                    value={cfg.welcome_message || ''}
                    onChange={(e) => setCfg({ welcome_message: e.target.value })}
                    placeholder={t('campaigns.new.leadDestConfig.welcomePlaceholder', 'Ex: Oi! Como posso te ajudar hoje?') as string}
                    rows={2}
                    maxLength={500}
                    className="input"
                  />
                </div>
              )}
              {campaignData.meta_lead_destination === 'app' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('campaigns.new.leadDestConfig.appIdLabel', 'App ID (Meta)')}
                    </label>
                    <input type="text" value={cfg.app_id || ''} onChange={(e) => setCfg({ app_id: e.target.value })} placeholder="Ex: 1234567890" className="input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('campaigns.new.leadDestConfig.deepLinkLabel', 'Deep link')}
                    </label>
                    <input type="text" value={cfg.app_deep_link || ''} onChange={(e) => setCfg({ app_deep_link: e.target.value })} placeholder="myapp://product/123" className="input" />
                  </div>
                </div>
              )}
              {campaignData.meta_lead_destination === 'whatsapp' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('campaigns.new.leadDestConfig.whatsappNumberLabel', 'Número do WhatsApp Business')}
                    </label>
                    <input type="text" value={cfg.phone_number || ''} onChange={(e) => setCfg({ phone_number: e.target.value })} placeholder="+5511999999999" className="input" />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('campaigns.new.leadDestConfig.whatsappNumberHint', 'Formato E.164 com +DDI. O número precisa estar vinculado à página do Facebook.')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('campaigns.new.leadDestConfig.welcomeLabel', 'Mensagem de boas-vindas')}
                    </label>
                    <textarea value={cfg.welcome_message || ''} onChange={(e) => setCfg({ welcome_message: e.target.value })} placeholder={t('campaigns.new.leadDestConfig.whatsappWelcomePlaceholder', 'Ex: Olá! Vi seu anúncio e gostaria de saber mais.') as string} rows={2} maxLength={500} className="input" />
                  </div>
                </div>
              )}
              {campaignData.meta_lead_destination === 'phone_call' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('campaigns.new.leadDestConfig.phoneLabel', 'Número de telefone')}
                  </label>
                  <input type="text" value={cfg.phone_number || ''} onChange={(e) => setCfg({ phone_number: e.target.value })} placeholder="+5511999999999" className="input" />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('campaigns.new.leadDestConfig.phoneHint', 'Formato E.164 com +DDI. Ao clicar no anúncio, o cliente liga direto.')}
                  </p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Meta ad set group — agrupa criativos no mesmo grupo de anúncios (ad set) */}
        {platform === 'meta' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('campaigns.new.adsetGroup', 'Grupo de anúncios')}
            </label>
            <select
              value={creative.adsetGroup || '1'}
              onChange={(e) => updatePlatformCreative(platform, { adsetGroup: e.target.value })}
              className="input w-full sm:w-56"
            >
              {['1', '2', '3', '4', '5'].map(g => (
                <option key={g} value={g}>
                  {t('campaigns.new.adsetGroupOption', 'Grupo {{n}}', { n: g })}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('campaigns.new.adsetGroupHelp', 'Criativos no mesmo grupo são veiculados juntos no mesmo conjunto de anúncios (ad set).')}
            </p>
          </div>
        )}

        {/* Publisher selector — per-ad Facebook Page (Meta) or LinkedIn Company */}
        {platform === 'meta' && metaPages.length > 0 && (() => {
          const currentPageId = creative.metaPageId || defaultMetaPageId;
          const currentPage = metaPages.find(p => p.page_id === currentPageId);
          // Unique list of IG accounts across ALL connected FB pages so the user
          // can pick a different IG from any page they administer, not only the
          // one tied to the selected FB page.
          const igOptionsMap: Record<string, {
            instagram_actor_id: string;
            instagram_username: string;
            instagram_picture: string | null;
          }> = {};
          metaPages.forEach(p => {
            if (p.instagram_actor_id && p.instagram_username) {
              igOptionsMap[p.instagram_actor_id] = {
                instagram_actor_id: p.instagram_actor_id,
                instagram_username: p.instagram_username,
                instagram_picture: p.instagram_picture || null,
              };
            }
          });
          const igOptions = Object.values(igOptionsMap);
          const defaultIgId = currentPage?.instagram_actor_id || '';
          const currentIgId = creative.metaInstagramId || defaultIgId;
          return (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                  <MetaAdsIcon className="w-4 h-4" />
                  {t('campaigns.hierarchy.metaPage', 'Página do Facebook que publicará o anúncio')}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {metaPages.map(page => {
                    const isSelected = currentPageId === page.page_id;
                    return (
                      <button
                        key={page.page_id}
                        type="button"
                        onClick={() => updatePlatformCreative(platform, {
                          metaPageId: page.page_id,
                          // Re-default the IG to this page's IG so the pair stays consistent
                          metaInstagramId: page.instagram_actor_id || ''
                        })}
                        className={`p-3 rounded-lg border-2 transition-all text-left flex items-center gap-3 ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'
                        }`}
                      >
                        {page.page_picture ? (
                          <img src={page.page_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                            {page.page_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{page.page_name}</p>
                          {page.instagram_username && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">@{page.instagram_username}</p>
                          )}
                        </div>
                        {isSelected && <Check className="w-5 h-5 text-primary-600 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('campaigns.hierarchy.metaPageHint', 'O anúncio será publicado a partir desta página. Você pode alterar a qualquer momento.')}
                </p>
              </div>
              {igOptions.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                    <Instagram className="w-4 h-4" />
                    {t('campaigns.hierarchy.metaInstagram', 'Conta do Instagram que publicará o anúncio')}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => updatePlatformCreative(platform, { metaInstagramId: 'none' })}
                      className={`p-3 rounded-lg border-2 transition-all text-left flex items-center gap-3 ${
                        creative.metaInstagramId === 'none'
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <X className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {t('campaigns.hierarchy.metaInstagramNone', 'Sem Instagram (somente Facebook)')}
                        </p>
                      </div>
                      {creative.metaInstagramId === 'none' && <Check className="w-5 h-5 text-primary-600 flex-shrink-0" />}
                    </button>
                    {igOptions.map(ig => {
                      const isSelected = currentIgId === ig.instagram_actor_id;
                      return (
                        <button
                          key={ig.instagram_actor_id}
                          type="button"
                          onClick={() => updatePlatformCreative(platform, { metaInstagramId: ig.instagram_actor_id })}
                          className={`p-3 rounded-lg border-2 transition-all text-left flex items-center gap-3 ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'
                          }`}
                        >
                          {ig.instagram_picture ? (
                            <img src={ig.instagram_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                              {ig.instagram_username.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 dark:text-white truncate">@{ig.instagram_username}</p>
                          </div>
                          {isSelected && <Check className="w-5 h-5 text-primary-600 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('campaigns.hierarchy.metaInstagramHint', 'Essa é a conta do Instagram que aparece nos placements Instagram (Feed, Reels, Stories).')}
                  </p>
                </div>
              )}
            </>
          );
        })()}
        {platform === 'linkedin' && linkedinOrgs.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
              <LinkedInAdsIcon className="w-4 h-4" />
              {t('campaigns.hierarchy.linkedinOrg', 'Empresa que publicará o anúncio')}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {linkedinOrgs.map(org => {
                const currentUrn = creative.linkedinOrgUrn || defaultLinkedinOrgUrn;
                const isSelected = currentUrn === org.organization_urn;
                return (
                  <button
                    key={org.organization_urn}
                    type="button"
                    onClick={() => updatePlatformCreative(platform, { linkedinOrgUrn: org.organization_urn })}
                    className={`p-3 rounded-lg border-2 transition-all text-left flex items-center gap-3 ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'
                    }`}
                  >
                    {org.logo_url ? (
                      <img src={org.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold">
                        {org.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{org.name}</p>
                      {org.vanity_name && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{org.vanity_name}</p>
                      )}
                    </div>
                    {isSelected && <Check className="w-5 h-5 text-primary-600 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {t('campaigns.hierarchy.linkedinOrgHint', 'Os anúncios serão publicados a partir desta empresa. Você pode alterar a qualquer momento.')}
            </p>
          </div>
        )}

        {/* Google Ad Group selector — só pra Google */}
        {platform === 'google' && (() => {
          const googleCreatives = campaignData.platformCreatives['google'] || [];
          const activeIdx = activeCreativeIdx[platform] ?? 0;
          const countAdsInGroup = (adGroupId: string) =>
            googleCreatives.filter(c => c.adGroupId === adGroupId).length;
          const RSAS_LIMIT = 3;
          const currentGroup = campaignData.googleAdGroups.find(ag => ag.id === creative.adGroupId);
          const currentCount = creative.adGroupId ? countAdsInGroup(creative.adGroupId) : 0;
          const isInEditMode = editingAdGroup.isNew || editingAdGroup.id !== null;
          const isConfirmingRemove = confirmingRemoveAdGroupId === creative.adGroupId && !!creative.adGroupId;

          const startNewMode = () => {
            setConfirmingRemoveAdGroupId(null);
            setEditingAdGroup({ id: null, name: `Grupo ${campaignData.googleAdGroups.length + 1}`, isNew: true });
          };
          const startRenameMode = () => {
            if (!currentGroup) return;
            setConfirmingRemoveAdGroupId(null);
            setEditingAdGroup({ id: currentGroup.id, name: currentGroup.name, isNew: false });
          };
          const cancelEditMode = () => setEditingAdGroup({ id: null, name: '', isNew: false });
          const confirmEditMode = () => {
            const trimmed = editingAdGroup.name.trim();
            if (!trimmed) {
              toast.error(t('campaigns.new.adGroupNameRequired', 'O nome do grupo não pode ficar vazio.'));
              return;
            }
            if (editingAdGroup.isNew) {
              const newId = addGoogleAdGroup(trimmed);
              updatePlatformCreative(platform, { adGroupId: newId });
            } else if (editingAdGroup.id) {
              renameGoogleAdGroup(editingAdGroup.id, trimmed);
            }
            cancelEditMode();
          };
          const askRemove = () => {
            if (!creative.adGroupId) return;
            setConfirmingRemoveAdGroupId(creative.adGroupId);
          };
          const cancelRemove = () => setConfirmingRemoveAdGroupId(null);
          const confirmRemove = () => {
            if (!creative.adGroupId) return;
            const idToRemove = creative.adGroupId;
            const otherUsage = googleCreatives.some((c, i) => {
              if (i === activeIdx) return false;
              return c.adGroupId === idToRemove;
            });
            if (otherUsage) {
              toast.error(t('campaigns.new.adGroupInUseOther', 'Grupo está em uso por outro anúncio. Mude lá antes de remover.'));
              setConfirmingRemoveAdGroupId(null);
              return;
            }
            setCampaignData(prev => {
              const arr = prev.platformCreatives[platform] || [];
              const safeIdx = Math.min(activeIdx, arr.length - 1);
              const newArr = [...arr];
              if (newArr.length > 0) {
                newArr[safeIdx] = { ...newArr[safeIdx], adGroupId: '' };
              }
              return {
                ...prev,
                googleAdGroups: prev.googleAdGroups.filter(ag => ag.id !== idToRemove),
                platformCreatives: { ...prev.platformCreatives, [platform]: newArr },
              };
            });
            setConfirmingRemoveAdGroupId(null);
          };

          // Indicador visual de capacidade (3 pontos coloridos)
          const renderCapacityDots = (count: number) => {
            const dotClass = (i: number) => {
              if (i < count) {
                if (count > RSAS_LIMIT) return 'bg-red-500';
                if (count === RSAS_LIMIT) return 'bg-amber-500';
                return 'bg-emerald-500';
              }
              return 'bg-gray-300 dark:bg-gray-600';
            };
            return (
              <span className="inline-flex items-center gap-1" aria-label={`${count} de ${RSAS_LIMIT}`}>
                {[0, 1, 2].map(i => (
                  <span key={i} className={`w-2 h-2 rounded-full ${dotClass(i)}`} />
                ))}
              </span>
            );
          };

          return (
            <div className="bg-blue-50/60 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4 space-y-3">
              {/* Header: label + contagem do grupo atual */}
              <div className="flex items-center justify-between gap-2">
                <label className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  {t('campaigns.new.adGroupLabel', 'Grupo de Anúncios')}
                  <span className="text-red-500">*</span>
                </label>
                {creative.adGroupId && currentGroup && !isInEditMode && (
                  <div className="flex items-center gap-2 text-xs">
                    {renderCapacityDots(currentCount)}
                    <span className={`font-medium ${currentCount > RSAS_LIMIT ? 'text-red-600' : currentCount === RSAS_LIMIT ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      {currentCount}/{RSAS_LIMIT} {t('campaigns.new.adsInGroupShort', 'anúncios')}
                    </span>
                  </div>
                )}
              </div>

              {/* Conteúdo: edit mode / confirm remove / empty / lista */}
              {isInEditMode ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    autoFocus
                    value={editingAdGroup.name}
                    onFocus={(e) => e.currentTarget.select()}
                    onChange={(e) => setEditingAdGroup(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('campaigns.new.adGroupName', 'Nome do grupo') as string}
                    maxLength={120}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); confirmEditMode(); }
                      if (e.key === 'Escape') { e.preventDefault(); cancelEditMode(); }
                    }}
                    className="input flex-1 min-w-[min(220px,100%)] ring-2 ring-primary-500/40 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={confirmEditMode}
                    disabled={!editingAdGroup.name.trim()}
                    className="px-3 py-2 text-sm bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-1"
                  >
                    <Check className="w-4 h-4" />
                    {editingAdGroup.isNew ? t('common.create', 'Criar') : t('common.save', 'Salvar')}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditMode}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white dark:hover:bg-gray-700 rounded-lg"
                    aria-label={t('common.cancel', 'Cancelar') as string}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : isConfirmingRemove ? (
                <div className="flex flex-wrap items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2">
                  <span className="text-sm text-red-800 dark:text-red-200 flex-1">
                    {t('campaigns.new.adGroupConfirmRemove', 'Remover "{{name}}"?').replace('{{name}}', currentGroup?.name || '')}
                  </span>
                  <button
                    type="button"
                    onClick={confirmRemove}
                    className="px-3 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {t('common.remove', 'Remover')}
                  </button>
                  <button
                    type="button"
                    onClick={cancelRemove}
                    className="px-3 py-1.5 text-sm bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    {t('common.cancel', 'Cancelar')}
                  </button>
                </div>
              ) : campaignData.googleAdGroups.length === 0 ? (
                <div className="flex flex-col items-start gap-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('campaigns.new.adGroupEmptyState', 'Nenhum grupo criado ainda. Crie o primeiro pra organizar seus anúncios.')}
                  </p>
                  <button
                    type="button"
                    onClick={startNewMode}
                    className="px-3 py-2 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded-lg flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    {t('campaigns.new.createFirstAdGroup', 'Criar primeiro grupo')}
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={creative.adGroupId || ''}
                    onChange={(e) => updatePlatformCreative(platform, { adGroupId: e.target.value })}
                    className="input flex-1 min-w-[min(240px,100%)] bg-white dark:bg-gray-800"
                  >
                    <option value="">{t('campaigns.new.selectAdGroup', '— Selecione um grupo —')}</option>
                    {campaignData.googleAdGroups.map(ag => {
                      const count = countAdsInGroup(ag.id);
                      const isCurrent = ag.id === creative.adGroupId;
                      const isFullForOthers = !isCurrent && count >= RSAS_LIMIT;
                      return (
                        <option key={ag.id} value={ag.id} disabled={isFullForOthers}>
                          {ag.name} · {count}/{RSAS_LIMIT}
                          {isFullForOthers ? ` — ${t('campaigns.new.adGroupFull', 'lotado')}` : ''}
                        </option>
                      );
                    })}
                  </select>
                  {creative.adGroupId && currentGroup && (
                    <>
                      <button
                        type="button"
                        onClick={startRenameMode}
                        title={t('campaigns.new.renameAdGroup', 'Renomear grupo') as string}
                        aria-label={t('campaigns.new.renameAdGroup', 'Renomear grupo') as string}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-white dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={askRemove}
                        title={t('campaigns.new.removeAdGroup', 'Remover grupo') as string}
                        aria-label={t('campaigns.new.removeAdGroup', 'Remover grupo') as string}
                        className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-red-200 dark:hover:border-red-800 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={startNewMode}
                    className="px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg flex items-center gap-1 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t('campaigns.new.newAdGroupShort', 'Novo')}
                  </button>
                </div>
              )}

              {/* Avisos contextuais */}
              {!isInEditMode && !isConfirmingRemove && (
                <>
                  {creative.adGroupId && currentCount > RSAS_LIMIT && (
                    <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md px-2 py-1.5">
                      ⚠️ {t('campaigns.new.adGroupOverLimitWarn', 'Acima do limite de 3 anúncios — mova {{n}} pra outro grupo antes de publicar').replace('{{n}}', String(currentCount - RSAS_LIMIT))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('campaigns.new.adGroupHintShort', 'Máximo 3 anúncios por grupo (regra do Google). Use grupos pra separar segmentos/intenções de keywords.')}
                  </p>
                </>
              )}
            </div>
          );
        })()}

        {/* Headlines */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Type className="w-4 h-4" />
              {t(spec.headlines.label)}
            </label>
            <span className="text-xs text-gray-500">
              {creative.headlines.length}/{spec.headlines.max} (min: {spec.headlines.min})
            </span>
          </div>
          {platform === 'google' && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mb-2 flex items-start gap-1">
              <Pin className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>{t('campaigns.new.pinTogglesNote', 'Clique no alfinete para fixar até 3 títulos (posições 1, 2 e 3 do Google Ads). Os demais rodam livremente.')}</span>
            </p>
          )}
          <div className="space-y-2">
            {creative.headlines.map((headline, hIndex) => {
              const pinValue = (platform === 'google' && creative.headlinePins?.[hIndex]) || null;
              const isPinned = pinValue != null;
              const pinnedCount = platform === 'google'
                ? (creative.headlinePins || []).filter((p) => p === 1 || p === 2 || p === 3).length
                : 0;
              const pinDisabled = platform === 'google' && !isPinned && pinnedCount >= 3;
              return (
              <div key={hIndex} className="flex items-center gap-2">
                {platform === 'google' && (
                  <button
                    type="button"
                    onClick={() => togglePlatformHeadlinePin(platform, hIndex)}
                    disabled={pinDisabled}
                    className={`flex-shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center text-xs font-bold transition-colors ${
                      isPinned
                        ? 'bg-blue-500 border-blue-500 text-white shadow-sm hover:bg-blue-600'
                        : pinDisabled
                          ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 hover:text-blue-500 hover:border-blue-300'
                    }`}
                    title={isPinned
                      ? (t('campaigns.new.pinnedHeadlineTooltip', 'Fixado na posição {{position}}', { position: pinValue }) as string)
                      : pinDisabled
                        ? (t('campaigns.new.pinMaxReached', 'Máximo de 3 títulos fixados. Desfixe um primeiro.') as string)
                        : (t('campaigns.new.pinHeadlineTooltip', 'Clique para fixar') as string)}
                  >
                    <Pin className={`w-3.5 h-3.5 ${isPinned ? 'fill-current' : ''}`} />
                    {isPinned && <span className="ml-0.5 text-[10px]">{pinValue}</span>}
                  </button>
                )}
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={headline}
                    onChange={(e) => updatePlatformHeadline(platform, hIndex, e.target.value)}
                    placeholder={`${t(spec.headlines.label)} ${hIndex + 1}`}
                    className={`input pr-16 ${headline.length > effectiveHeadlineLimit ? 'border-red-500' : ''}`}
                    maxLength={effectiveHeadlineLimit + 20}
                  />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${
                    headline.length > effectiveHeadlineLimit ? 'text-red-500 font-bold' : 'text-gray-400'
                  }`}>
                    {headline.length}/{effectiveHeadlineLimit}
                  </span>
                </div>
                {creative.headlines.length > spec.headlines.min && (
                  <button
                    onClick={() => removePlatformHeadline(platform, hIndex)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              );
            })}
          </div>
          {creative.headlines.length < spec.headlines.max && (
            <button
              onClick={() => addPlatformHeadline(platform)}
              className="mt-2 text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              {t('campaigns.new.addItem', 'Adicionar')} {t(spec.headlines.label).toLowerCase()}
            </button>
          )}
        </div>

        {/* Descriptions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t(spec.descriptions.label)}
            </label>
            <span className="text-xs text-gray-500">
              {creative.descriptions.length}/{spec.descriptions.max} (min: {spec.descriptions.min})
            </span>
          </div>
          <div className="space-y-2">
            {creative.descriptions.map((desc, dIndex) => (
              <div key={dIndex} className="flex items-start gap-2">
                <div className="flex-1 relative">
                  <textarea
                    value={desc}
                    onChange={(e) => updatePlatformDescription(platform, dIndex, e.target.value)}
                    placeholder={`${t(spec.descriptions.label)} ${dIndex + 1}`}
                    className={`input min-h-[80px] pr-16 ${desc.length > effectiveDescriptionLimit ? 'border-red-500' : ''}`}
                    maxLength={effectiveDescriptionLimit + 50}
                  />
                  <span className={`absolute right-3 bottom-3 text-xs ${
                    desc.length > effectiveDescriptionLimit ? 'text-red-500 font-bold' : 'text-gray-400'
                  }`}>
                    {desc.length}/{effectiveDescriptionLimit}
                  </span>
                </div>
                {creative.descriptions.length > spec.descriptions.min && (
                  <button
                    onClick={() => removePlatformDescription(platform, dIndex)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg mt-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {creative.descriptions.length < spec.descriptions.max && (
            <button
              onClick={() => addPlatformDescription(platform)}
              className="mt-2 text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              {t('campaigns.new.addItem', 'Adicionar')} {t(spec.descriptions.label).toLowerCase()}
            </button>
          )}
        </div>

        {/* Shared media picker (image upload + AI generation with reference + video upload + frame-to-thumb) */}
        <MediaPicker
          imageUrl={creative.imageUrl}
          onImageChange={(url) => updatePlatformCreative(platform, { imageUrl: url, imageFile: null })}
          imageSupported={imageSupported}
          imageRequired={spec.image.required && !videoRequired}
          recommendedImageSize={spec.image.recommendedSize}
          allowedImageFormats={spec.image.formats}
          imageHint={videoRequired
            ? t('campaigns.new.imageOptionalHint', '(opcional — usado como cover/thumb em {{channels}})', { channels: videoRequiredChannelNames.join(', ') }) as string
            : null}
          onEditImage={() => setEditingImagePlatform(platform)}
          videoSupported={spec.video.supported}
          videoRequired={videoRequired}
          videoUrl={creative.videoUrl}
          onVideoChange={(url) => updatePlatformCreative(platform, { videoUrl: url, _videoUploading: false } as any)}
          recommendedVideoSize={spec.video.recommendedSize}
          videoHint={videoRequired && videoRequiredChannelNames.length > 0
            ? t('campaigns.new.videoRequiredHint', '(obrigatório para {{channels}})', { channels: videoRequiredChannelNames.join(', ') }) as string
            : null}
          aiEnabled={true}
          imagePrompt={creative.imagePrompt || ''}
          onImagePromptChange={(v) => updatePlatformCreative(platform, { imagePrompt: v })}
          imageStyle={creative.imageStyle || 'realistic'}
          onImageStyleChange={(v) => updatePlatformCreative(platform, { imageStyle: v })}
          imageStyleOptions={IMAGE_STYLE_OPTIONS.map(s => ({ value: s.id, label: s.label }))}
          uploadImage={async (file: File) => {
            // Hard floor: pra Meta usar 500x500 (rejeição real da plataforma). Os
            // "minWidth: 1080" do CHANNEL_SPECS pra IG Feed/Reels são RECOMENDAÇÕES
            // (Meta serve em placements compatíveis e exclui as outras), não hard blocks.
            // Pra Google/LinkedIn, os specs por canal são requisitos reais.
            let minW = 0;
            let minH = 0;
            let strictestName = '';
            if (platform === 'meta') {
              minW = 500;
              minH = 500;
              strictestName = 'Meta';
            } else {
              const platformChannels = (
                platform === 'google' ? GOOGLE_CHANNELS :
                platform === 'linkedin' ? LINKEDIN_CHANNELS :
                []
              );
              const selectedForPlatform = platformChannels.filter(c => campaignData.channels.includes(c.id));
              for (const ch of selectedForPlatform) {
                const sp = (CHANNEL_SPECS as any)[ch.id]?.imageSpecs;
                if (sp?.minWidth && sp.minWidth > minW) { minW = sp.minWidth; strictestName = ch.name; }
                if (sp?.minHeight && sp.minHeight > minH) { minH = sp.minHeight; }
              }
            }
            if (minW > 0 || minH > 0) {
              const dims = await new Promise<{ w: number; h: number } | null>((resolve) => {
                const img = document.createElement('img');
                const probe = URL.createObjectURL(file);
                img.onload = () => { resolve({ w: img.naturalWidth, h: img.naturalHeight }); URL.revokeObjectURL(probe); };
                img.onerror = () => { resolve(null); URL.revokeObjectURL(probe); };
                img.src = probe;
              });
              if (dims && (dims.w < minW || dims.h < minH)) {
                const msg = t('campaigns.new.imageTooSmall',
                  'Imagem {{w}}x{{h}}px é menor que o mínimo de {{minW}}x{{minH}}px exigido por {{ch}}. Use uma imagem maior.')
                  .replace('{{w}}', String(dims.w))
                  .replace('{{h}}', String(dims.h))
                  .replace('{{minW}}', String(minW))
                  .replace('{{minH}}', String(minH))
                  .replace('{{ch}}', strictestName);
                toast.error(msg, { duration: 6000 });
                throw new Error(msg);
              }
            }
            return await uploadImageToS3(file);
          }}
          uploadVideo={async (file: File) => {
            // Hard floor: pra Meta usar 500x500. Mesma justificativa do uploadImage:
            // as recomendações 1080x1920 (Reels) / 1080x1080 (Feed) são guidelines,
            // não rejeições de publish. Meta serve nas placements compatíveis e exclui
            // as demais. Validar 1080x1920 universalmente bloqueava vídeos vertical
            // 608x1080 que publicariam normalmente.
            let minW = 0;
            let minH = 0;
            let strictestName = '';
            if (platform === 'meta') {
              minW = 500;
              minH = 500;
              strictestName = 'Meta';
            } else {
              const platformChannels = (
                platform === 'google' ? GOOGLE_CHANNELS :
                platform === 'linkedin' ? LINKEDIN_CHANNELS :
                []
              );
              const selectedForPlatform = platformChannels.filter(c => campaignData.channels.includes(c.id));
              for (const ch of selectedForPlatform) {
                const vSpec = (CHANNEL_SPECS as any)[ch.id]?.videoSpecs;
                const iSpec = (CHANNEL_SPECS as any)[ch.id]?.imageSpecs;
                const refW = vSpec?.minWidth || iSpec?.minWidth || 0;
                const refH = vSpec?.minHeight || iSpec?.minHeight || 0;
                if (refW > minW) { minW = refW; strictestName = ch.name; }
                if (refH > minH) { minH = refH; }
              }
            }
            if (minW > 0 || minH > 0) {
              const dims = await new Promise<{ w: number; h: number } | null>((resolve) => {
                const video = document.createElement('video');
                video.preload = 'metadata';
                const probe = URL.createObjectURL(file);
                video.onloadedmetadata = () => {
                  resolve({ w: video.videoWidth, h: video.videoHeight });
                  URL.revokeObjectURL(probe);
                };
                video.onerror = () => { resolve(null); URL.revokeObjectURL(probe); };
                video.src = probe;
              });
              if (dims && (dims.w < minW || dims.h < minH)) {
                const msg = t('campaigns.new.videoTooSmall',
                  'Vídeo {{w}}x{{h}}px é menor que o mínimo de {{minW}}x{{minH}}px exigido por {{ch}}. Use um vídeo com resolução maior.')
                  .replace('{{w}}', String(dims.w))
                  .replace('{{h}}', String(dims.h))
                  .replace('{{minW}}', String(minW))
                  .replace('{{minH}}', String(minH))
                  .replace('{{ch}}', strictestName);
                toast.error(msg, { duration: 6000 });
                throw new Error(msg);
              }
            }
            // Upload via the same S3 MULTIPART path as the social composer
            // (chunked, direct-to-S3, up to 2GB) instead of the old backend
            // /upload-video, which is Cloudflare-limited (~100MB) and would fail
            // on larger creatives.
            const res = await socialService.uploadVideo(file);
            if (res?.success && res.data?.url) return res.data.url;
            throw new Error('Upload failed');
          }}
          generateImage={async ({ prompt, style, referenceImageUrl }) => {
            const response = await imageStudioService.generateImage({
              prompt,
              style: style as any,
              size: '1024x1024',
              reference_image_url: referenceImageUrl
            } as any);
            if (response.success && response.data) {
              const url = response.data.original_url || response.data.edited_url;
              if (url) return url;
            }
            throw new Error(response?.message || 'Failed to generate image');
          }}
          uploadReference={async (file: File) => {
            return await imageStudioService.uploadReference(file);
          }}
        />


        {/* Keywords (Google only) — shared across all ads in the campaign */}
        {spec.keywords.supported && activeIdx === 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
              <Search className="w-4 h-4" />
              {t('campaigns.new.keywords', 'Palavras-chave')} {spec.keywords.required && '*'}
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                {t('campaigns.new.keywordsSharedBadge', '(compartilhadas entre anúncios)')}
              </span>
            </label>
            <CommaSeparatedInput
              values={creatives[0]?.keywords || []}
              onChange={(keywords) => updatePlatformCreative(platform, { keywords }, 0)}
              placeholder={t('campaigns.new.keywordsPlaceholder', 'marketing digital, google ads, performance...')}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">{t('campaigns.new.separateKeywords', 'Separe as palavras-chave com vírgulas')}</p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-start gap-1">
              <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>{t('campaigns.new.keywordsNote', 'Após criar a campanha, você poderá editar as palavras-chave e adicionar palavras-chave negativas para ter um controle mais fino do anúncio.')}</span>
            </p>
          </div>
        )}
        {spec.keywords.supported && activeIdx > 0 && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-800 dark:text-blue-300 flex items-start gap-2">
            <Search className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              {t('campaigns.new.keywordsSharedNote', 'As palavras-chave são compartilhadas entre todos os anúncios desta campanha. Edite-as no')} <strong>{t('campaigns.new.adLabel', 'Anúncio')} 1</strong>.
              {creatives[0]?.keywords && creatives[0].keywords.length > 0 && (
                <span className="block mt-1 text-blue-600 dark:text-blue-400">
                  ({creatives[0].keywords.length} {creatives[0].keywords.length === 1 ? t('campaigns.new.keywordSingular', 'palavra-chave') : t('campaigns.new.keywordPlural', 'palavras-chave')})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Google Ads Assets (callouts, structured snippets, sitelinks) — shown on every Google ad tab.
            Data is campaign-level (Google Ads model), so editing from any ad tab affects all ads in the campaign. */}
        {platform === 'google' && (
          <div className="space-y-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-xl border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t('campaigns.new.googleAssets', 'Extensões do Google Ads')}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('campaigns.new.googleAssetsDesc', 'Callouts, snippets estruturados e sitelinks aparecem nos anúncios — compartilhados em toda a campanha.')}
                </p>
                {creatives.length > 1 && (
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1 italic">
                    {t('campaigns.new.googleAssetsSharedNote', 'Editar aqui afeta todos os anúncios da campanha (modelo do Google Ads).')}
                  </p>
                )}
              </div>
            </div>

            {/* Callouts */}
            <details open={isEditMode ? campaignData.googleAssets.callouts.length > 0 : true} className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1 list-none py-1">
                <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                {t('campaigns.new.callouts', 'Frases de chamada (callouts)')}
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({campaignData.googleAssets.callouts.length}/20)
                </span>
              </summary>
              <div className="mt-2 space-y-2 pl-5">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('campaigns.new.calloutsHelp', 'Textos curtos que destacam pontos fortes. Ex: "Frete Grátis", "Atendimento 24h". Mínimo 2, máximo 25 caracteres cada.')}
                </p>
                {campaignData.googleAssets.callouts.map((co, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={co}
                        onChange={(e) => updateCallout(i, e.target.value)}
                        placeholder={t('campaigns.new.calloutPlaceholder', 'Ex: Frete Grátis')}
                        maxLength={40}
                        className={`input pr-12 text-sm ${co.length > 25 ? 'border-red-500' : ''}`}
                      />
                      <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${co.length > 25 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                        {co.length}/25
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCallout(i)}
                      className="p-2 text-gray-400 hover:text-red-500 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {campaignData.googleAssets.callouts.length < 20 && (
                  <button
                    type="button"
                    onClick={addCallout}
                    className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    {t('campaigns.new.addCallout', 'Adicionar callout')}
                  </button>
                )}
              </div>
            </details>

            {/* Structured Snippets */}
            <details open={campaignData.googleAssets.structuredSnippets.length > 0} className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1 list-none py-1">
                <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                {t('campaigns.new.structuredSnippets', 'Snippets estruturados')}
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({campaignData.googleAssets.structuredSnippets.length})
                </span>
              </summary>
              <div className="mt-2 space-y-3 pl-5">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('campaigns.new.structuredSnippetsHelp', 'Cabeçalho + lista de itens. Ex: Serviços: Landing Pages, SEO, Ads.')}
                </p>
                {campaignData.googleAssets.structuredSnippets.map((ss, i) => {
                  const validValues = ss.values.filter(v => v && v.trim() && v.length <= 25);
                  const overLimit = ss.values.filter(v => v && v.length > 25);
                  const tooFew = validValues.length < 3;
                  const tooMany = ss.values.length > 10;
                  return (
                  <div key={i} className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2">
                    <div className="flex items-center gap-2">
                      <select
                        value={ss.header}
                        onChange={(e) => updateStructuredSnippet(i, { header: e.target.value })}
                        className="input text-sm flex-1"
                      >
                        {STRUCTURED_SNIPPET_HEADERS.map(h => (
                          <option key={h} value={h}>
                            {t(`campaigns.new.snippetHeaders.${h.replace(/[\s-]/g, '_').toLowerCase()}`, h)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeStructuredSnippet(i)}
                        className="p-2 text-gray-400 hover:text-red-500 rounded-lg"
                        title={t('campaigns.new.removeStructuredSnippet', 'Remover snippet')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Items list — one input per value */}
                    <div className="space-y-1.5">
                      {ss.values.map((value, vi) => (
                        <div key={vi} className="flex items-center gap-2">
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => {
                                const next = [...ss.values];
                                next[vi] = e.target.value;
                                updateStructuredSnippet(i, { values: next });
                              }}
                              placeholder={t('campaigns.new.snippetItemPlaceholder', 'Item {{n}}', { n: vi + 1 })}
                              maxLength={35}
                              className={`input pr-12 text-sm ${value.length > 25 ? 'border-red-500' : ''}`}
                            />
                            <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${value.length > 25 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                              {value.length}/25
                            </span>
                          </div>
                          {ss.values.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const next = ss.values.filter((_, k) => k !== vi);
                                updateStructuredSnippet(i, { values: next });
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg"
                              title={t('campaigns.new.removeItem', 'Remover item')}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Add item button */}
                    {ss.values.length < 10 && (
                      <button
                        type="button"
                        onClick={() => {
                          updateStructuredSnippet(i, { values: [...ss.values, ''] });
                        }}
                        className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        {t('campaigns.new.addItem', 'Adicionar item')} ({ss.values.length}/10)
                      </button>
                    )}

                    {/* Validation feedback */}
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className={tooFew ? 'text-red-500 font-medium' : 'text-gray-500'}>
                        {validValues.length}/3 {t('campaigns.new.snippetMinItems', 'mín. necessários')}
                      </span>
                      {tooMany && (
                        <span className="text-red-500 font-medium">
                          ⚠ {t('campaigns.new.snippetTooMany', 'Máximo 10 itens')}
                        </span>
                      )}
                      {overLimit.length > 0 && (
                        <span className="text-red-500 font-medium">
                          ⚠ {overLimit.length} {t('campaigns.new.snippetOverLimit', 'item(ns) excedem 25 caracteres')}
                        </span>
                      )}
                    </div>
                  </div>
                  );
                })}
                <button
                  type="button"
                  onClick={addStructuredSnippet}
                  className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  {t('campaigns.new.addStructuredSnippet', 'Adicionar snippet estruturado')}
                </button>
              </div>
            </details>

            {/* Sitelinks */}
            <details open={campaignData.googleAssets.sitelinks.length > 0} className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1 list-none py-1">
                <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                {t('campaigns.new.sitelinks', 'Sitelinks')}
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({campaignData.googleAssets.sitelinks.length}/10)
                </span>
              </summary>
              <div className="mt-2 space-y-3 pl-5">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('campaigns.new.sitelinksHelp', 'Links adicionais abaixo do anúncio. Mínimo 2 pra aparecer. Máximo 10.')}
                </p>
                {campaignData.googleAssets.sitelinks.map((sl, i) => (
                  <div key={i} className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={sl.title}
                          onChange={(e) => updateSitelink(i, { title: e.target.value })}
                          placeholder={t('campaigns.new.sitelinkTitlePlaceholder', 'Título do link (25 chars)')}
                          maxLength={35}
                          className={`input pr-12 text-sm ${sl.title.length > 25 ? 'border-red-500' : ''}`}
                        />
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${sl.title.length > 25 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                          {sl.title.length}/25
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSitelink(i)}
                        className="p-2 text-gray-400 hover:text-red-500 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <input
                      type="url"
                      value={sl.url}
                      onChange={(e) => updateSitelink(i, { url: e.target.value })}
                      placeholder="https://seusite.com/pagina"
                      className="input text-sm"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="relative">
                        <input
                          type="text"
                          value={sl.desc1 || ''}
                          onChange={(e) => updateSitelink(i, { desc1: e.target.value })}
                          placeholder={t('campaigns.new.sitelinkDesc1Placeholder', 'Descrição 1 (opcional, 35 chars)')}
                          maxLength={45}
                          className={`input pr-12 text-sm ${(sl.desc1 || '').length > 35 ? 'border-red-500' : ''}`}
                        />
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${(sl.desc1 || '').length > 35 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                          {(sl.desc1 || '').length}/35
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          value={sl.desc2 || ''}
                          onChange={(e) => updateSitelink(i, { desc2: e.target.value })}
                          placeholder={t('campaigns.new.sitelinkDesc2Placeholder', 'Descrição 2 (opcional, 35 chars)')}
                          maxLength={45}
                          className={`input pr-12 text-sm ${(sl.desc2 || '').length > 35 ? 'border-red-500' : ''}`}
                        />
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${(sl.desc2 || '').length > 35 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                          {(sl.desc2 || '').length}/35
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {campaignData.googleAssets.sitelinks.length < 10 && (
                  <button
                    type="button"
                    onClick={addSitelink}
                    className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    {t('campaigns.new.addSitelink', 'Adicionar sitelink')}
                  </button>
                )}
              </div>
            </details>

            {/* WhatsApp — extensão de mensagem do Google Ads */}
            <details open={googleWhatsApp.enabled} className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 py-2">
                <MessageCircle className="w-4 h-4 text-emerald-500" />
                {t('campaigns.new.googleWhatsapp', 'WhatsApp (extensão de mensagem)')}
                {googleWhatsApp.enabled && (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400">
                    {t('campaigns.new.googleWhatsappOn', 'ativo')}
                  </span>
                )}
              </summary>
              <div className="pl-6 space-y-3 mt-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={googleWhatsApp.enabled}
                    onChange={(e) => updateGoogleWhatsApp({ enabled: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  {t('campaigns.new.googleWhatsappEnable', 'Mostrar botão de WhatsApp nos anúncios desta campanha')}
                </label>

                {googleWhatsApp.enabled && (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <select
                        value={googleWhatsApp.country_code}
                        onChange={(e) => updateGoogleWhatsApp({ country_code: e.target.value })}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      >
                        {GOOGLE_WHATSAPP_COUNTRIES.map(c => (
                          <option key={c.iso} value={c.iso}>{c.iso} +{c.dial}</option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        value={googleWhatsApp.phone_number}
                        onChange={(e) => updateGoogleWhatsApp({ phone_number: e.target.value })}
                        placeholder={t('campaigns.new.googleWhatsappPhone', 'Número com DDD (11 98888-7777)') as string}
                        className="col-span-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <input
                        type="text"
                        value={googleWhatsApp.starter_message}
                        maxLength={140}
                        onChange={(e) => updateGoogleWhatsApp({ starter_message: e.target.value })}
                        placeholder={t('campaigns.new.googleWhatsappStarter', 'Mensagem que o cliente envia: "Olá! Quero saber mais sobre..."') as string}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      />
                      <span className="text-[11px] text-gray-400">{googleWhatsApp.starter_message.length}/140</span>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <select
                        value={googleWhatsApp.cta}
                        onChange={(e) => updateGoogleWhatsApp({ cta: e.target.value })}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      >
                        {GOOGLE_WHATSAPP_CTAS.map(c => (
                          <option key={c} value={c}>{t(`campaigns.new.googleWhatsappCta.${c}`, c)}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={googleWhatsApp.cta_description}
                        maxLength={50}
                        onChange={(e) => updateGoogleWhatsApp({ cta_description: e.target.value })}
                        placeholder={t('campaigns.new.googleWhatsappCtaDesc', 'Ex.: Fale agora com nosso time') as string}
                        className="col-span-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      />
                    </div>

                    <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={googleWhatsApp.use_as_destination}
                        onChange={(e) => updateGoogleWhatsApp({ use_as_destination: e.target.checked })}
                        className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        {t('campaigns.new.googleWhatsappDestination', 'Mandar o clique do anúncio direto para o WhatsApp (em vez do site)')}
                        <span className="block text-[11px] text-gray-500">
                          {t('campaigns.new.googleWhatsappDestinationHint', 'Use quando não houver site: a URL final do anúncio vira o link wa.me.')}
                        </span>
                      </span>
                    </label>

                    {googleWhatsAppLink && (
                      <p className="text-[11px] text-gray-500 break-all">
                        {t('campaigns.new.googleWhatsappPreview', 'Link gerado')}: {googleWhatsAppLink}
                      </p>
                    )}
                    {(!googleWhatsApp.starter_message.trim() || !googleWhatsApp.cta_description.trim()) && (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400">
                        {t('campaigns.new.googleWhatsappRequired', 'O Google exige mensagem inicial e descrição do CTA para publicar a extensão.')}
                      </p>
                    )}
                  </>
                )}
              </div>
            </details>
          </div>
        )}

        {/* CTA + URL — hidden/auto-overridden when Meta Leads destination
            dictates the CTA (WhatsApp → WHATSAPP_MESSAGE, Phone → CALL_NOW,
            etc). Keeps preview consistent with what actually goes to Meta. */}
        {(() => {
          const metaLeadOverride = platform === 'meta'
            && campaignData.objective === 'leads'
            && campaignData.meta_lead_destination
            && campaignData.meta_lead_destination !== 'website';
          const forcedCtaLabels: Record<string, string> = {
            whatsapp: t('campaigns.cta.whatsappMessage', 'Enviar mensagem') as string,
            messenger: t('campaigns.cta.messagePage', 'Enviar mensagem') as string,
            phone_call: t('campaigns.cta.callNow', 'Ligar agora') as string,
            instant_form: t('campaigns.cta.signUp', 'Cadastre-se') as string,
            app: t('campaigns.cta.installApp', 'Instalar') as string,
          };
          const showUrlField = !metaLeadOverride || campaignData.meta_lead_destination === 'website';
          return (
            <>
              {metaLeadOverride ? (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                    <MousePointer className="w-4 h-4" />
                    {t('campaigns.new.callToAction', 'Call to Action (CTA)')}
                  </label>
                  <div className="input bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 flex items-center justify-between cursor-not-allowed">
                    <span>{forcedCtaLabels[campaignData.meta_lead_destination!] || t('campaigns.cta.learnMore', 'Saiba Mais')}</span>
                    <span className="text-xs text-gray-400 italic">
                      {t('campaigns.new.ctaAutoForced', 'definido pelo destino escolhido')}
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                    <MousePointer className="w-4 h-4" />
                    {t('campaigns.new.callToAction', 'Call to Action (CTA)')}
                  </label>
                  <select
                    value={creative.ctaText}
                    onChange={(e) => updatePlatformCreative(platform, { ctaText: e.target.value })}
                    className="input"
                  >
                    {spec.ctas.map(cta => (
                      <option key={cta.value} value={cta.value}>{t(cta.label)}</option>
                    ))}
                  </select>
                </div>
              )}

              {showUrlField && (
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                    <ExternalLink className="w-4 h-4" />
                    {t('campaigns.new.destinationUrl', 'Destination URL')} *
                  </label>
                  <input
                    type="url"
                    value={creative.destinationUrl}
                    onChange={(e) => updatePlatformCreative(platform, { destinationUrl: e.target.value })}
                    placeholder={t('campaigns.new.urlPlaceholder', 'https://yoursite.com/page')}
                    className="input"
                  />
                </div>
              )}
            </>
          );
        })()}

        {/* Display URL (Google only) */}
        {spec.extraFields?.includes('display_url') && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
              <Link className="w-4 h-4" />
              Display URL Path ({t('common.optional', 'optional')})
            </label>
            <div className="flex flex-wrap sm:flex-nowrap gap-2">
              <input
                type="text"
                value={creative.displayUrl || ''}
                onChange={(e) => updatePlatformCreative(platform, { displayUrl: e.target.value })}
                placeholder="yoursite.com/"
                className="input flex-1 min-w-0"
                disabled
              />
              <input
                type="text"
                placeholder="path1"
                className="input w-full sm:w-24"
                maxLength={15}
              />
              <input
                type="text"
                placeholder="path2"
                className="input w-full sm:w-24"
                maxLength={15}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Load creative from library
  const handleLoadFromLibrary = (libraryCreative: LibraryCreative) => {
    const newCreative: Creative = {
      id: `creative-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      headlines: libraryCreative.headlines?.length ? libraryCreative.headlines : [''],
      descriptions: libraryCreative.descriptions?.length ? libraryCreative.descriptions : [''],
      imageUrl: libraryCreative.image_url,
      imageFile: null,
      videoUrl: libraryCreative.video_url || '',
      ctaText: libraryCreative.cta_text || 'learn_more',
      destinationUrl: libraryCreative.destination_url || '',
      isGeneratingImage: false,
      imagePrompt: '',
      imageStyle: 'realistic'
    };

    updateCreative(activeCreativeIndex, newCreative);
    toast.success(t('campaigns.new.creativeLoaded', 'Creative loaded from library!'));
  };

  const getValidationErrors = (creative: Creative, channelId: string): string[] => {
    const errors: string[] = [];
    const specs = CHANNEL_SPECS[channelId as keyof typeof CHANNEL_SPECS];

    if (!specs) return errors;

    // Check headlines
    const validHeadlines = creative.headlines.filter(h => h.trim() !== '');
    if (validHeadlines.length < specs.headlines.min) {
      errors.push(t('campaigns.new.validation.minHeadlines', { min: specs.headlines.min, channel: channelId }));
    }
    validHeadlines.forEach((h, i) => {
      if (h.length > specs.headlines.charLimit) {
        errors.push(t('campaigns.new.validation.headlineExceeds', { index: i + 1, limit: specs.headlines.charLimit }));
      }
    });

    // Check descriptions
    const validDescriptions = creative.descriptions.filter(d => d.trim() !== '');
    if (validDescriptions.length < specs.descriptions.min) {
      errors.push(t('campaigns.new.validation.minDescriptions', { min: specs.descriptions.min, channel: channelId }));
    }
    validDescriptions.forEach((d, i) => {
      if (d.length > specs.descriptions.charLimit) {
        errors.push(t('campaigns.new.validation.descriptionExceeds', { index: i + 1, limit: specs.descriptions.charLimit }));
      }
    });

    return errors;
  };

  const uploadImageToS3 = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success && response.data.data?.url) {
      return response.data.data.url;
    }
    throw new Error('Failed to upload image to S3');
  };

  const handleSubmit = async () => {
    // Pre-submit validation with specific feedback
    const validationErrors: string[] = [];

    if (!campaignData.name.trim()) {
      validationErrors.push(t('campaigns.new.validation.nameRequired', 'Nome da campanha é obrigatório'));
    }
    if (campaignData.platforms.length === 0) {
      validationErrors.push(t('campaigns.new.validation.platformRequired', 'Selecione pelo menos uma plataforma'));
    }
    if (campaignData.channels.length === 0) {
      validationErrors.push(t('campaigns.new.validation.channelRequired', 'Selecione pelo menos um canal'));
    }
    if (!campaignData.objective) {
      validationErrors.push(t('campaigns.new.validation.objectiveRequired', 'Selecione um objetivo'));
    }
    if (campaignData.budget_amount <= 0) {
      validationErrors.push(t('campaigns.new.validation.budgetRequired', 'Orçamento deve ser maior que zero'));
    }
    if (campaignData.objective === 'remarketing' && selectedAudiences.length === 0) {
      validationErrors.push(t('campaigns.new.validation.audienceRequired', 'Campanhas de remarketing requerem pelo menos uma audiência'));
    }

    // Validate creatives per platform (all creatives per platform must be valid)
    const platforms = campaignData.platforms;
    for (const platform of platforms) {
      const creatives = campaignData.platformCreatives[platform];
      if (!creatives || creatives.length === 0) {
        validationErrors.push(t('campaigns.new.validation.creativeRequired', 'Creative para {{platform}} é obrigatório').replace('{{platform}}', platform));
        continue;
      }
      // Determine if any selected channel for this platform forces video-required semantics
      const platformChannelsForValidation = campaignData.channels.filter(channelId => {
        if (platform === 'google') return channelId.startsWith('google_') || channelId === 'youtube';
        if (platform === 'meta') return channelId.startsWith('facebook_') || channelId.startsWith('instagram_');
        if (platform === 'linkedin') return channelId.startsWith('linkedin_');
        return false;
      });
      const requiresVideo = platformChannelsForValidation.some(channelId => {
        const chSpec = (CHANNEL_SPECS as any)[channelId];
        return chSpec && Array.isArray(chSpec.supportedMedia) && chSpec.supportedMedia.length === 1 && chSpec.supportedMedia[0] === 'video';
      });

      creatives.forEach((creative, idx) => {
        const label = creatives.length > 1 ? `${platform} #${idx + 1}` : platform;
        if (!creative.headlines.some(h => h.trim() !== '')) {
          validationErrors.push(t('campaigns.new.validation.headlineRequired', 'Pelo menos um título é obrigatório para {{platform}}').replace('{{platform}}', label));
        }
        if (!creative.descriptions.some(d => d.trim() !== '')) {
          validationErrors.push(t('campaigns.new.validation.descriptionRequired', 'Pelo menos uma descrição é obrigatória para {{platform}}').replace('{{platform}}', label));
        }
        // URL only required when the destination actually uses one. Meta Leads
        // with destinations like whatsapp/messenger/phone_call/instant_form/app
        // build the link internally (wa.me, m.me, tel:, lead_gen_form_id, etc)
        // so demanding a destinationUrl here would be a false-positive.
        const skipUrlRequirement = platform === 'meta'
          && campaignData.objective === 'leads'
          && campaignData.meta_lead_destination
          && campaignData.meta_lead_destination !== 'website';
        if (!skipUrlRequirement && !creative.destinationUrl.trim()) {
          validationErrors.push(t('campaigns.new.validation.urlRequired', 'URL de destino é obrigatória para {{platform}}').replace('{{platform}}', label));
        }
        if (requiresVideo && !creative.videoUrl.trim()) {
          validationErrors.push(t('campaigns.new.validation.videoRequiredForChannel', 'Vídeo é obrigatório para {{platform}} (canal exige vídeo)').replace('{{platform}}', label));
        }
      });
    }

    // Validate WhatsApp (o Google recusa o asset sem número, mensagem inicial ou descrição do CTA)
    if (campaignData.platforms.includes('google') && campaignData.googleAssets.whatsapp?.enabled) {
      const w = campaignData.googleAssets.whatsapp;
      const digits = (w.phone_number || '').replace(/\D/g, '');
      if (digits.length < 8) {
        validationErrors.push(t('campaigns.new.validation.whatsappPhone', 'Informe o número de WhatsApp com DDD para usar a extensão de mensagem.') as string);
      }
      if (!(w.starter_message || '').trim()) {
        validationErrors.push(t('campaigns.new.validation.whatsappStarter', 'A extensão de WhatsApp precisa da mensagem inicial que o cliente vai enviar.') as string);
      }
      if (!(w.cta_description || '').trim()) {
        validationErrors.push(t('campaigns.new.validation.whatsappCtaDesc', 'A extensão de WhatsApp precisa da descrição do botão (ex.: "Fale agora com nosso time").') as string);
      }
    }

    // Validate Google Ads structured snippets (only if Google is selected and user added any)
    if (campaignData.platforms.includes('google') && campaignData.googleAssets.structuredSnippets.length > 0) {
      campaignData.googleAssets.structuredSnippets.forEach((ss, idx) => {
        const validValues = ss.values.filter(v => v && v.trim() && v.length <= 25);
        if (validValues.length < 3) {
          validationErrors.push(
            t('campaigns.new.validation.snippetMin', 'Snippet "{{header}}" precisa de pelo menos 3 itens válidos (até 25 caracteres cada).')
              .replace('{{header}}', ss.header || `#${idx + 1}`)
          );
        }
        if (ss.values.length > 10) {
          validationErrors.push(
            t('campaigns.new.validation.snippetMax', 'Snippet "{{header}}" excede 10 itens.')
              .replace('{{header}}', ss.header || `#${idx + 1}`)
          );
        }
        const overLimit = ss.values.filter(v => v && v.length > 25);
        if (overLimit.length > 0) {
          validationErrors.push(
            t('campaigns.new.validation.snippetCharLimit', 'Snippet "{{header}}" tem {{n}} item(ns) acima de 25 caracteres.')
              .replace('{{header}}', ss.header || `#${idx + 1}`)
              .replace('{{n}}', String(overLimit.length))
          );
        }
      });
    }

    // Validate callouts (Google) — each up to 25 chars
    if (campaignData.platforms.includes('google') && campaignData.googleAssets.callouts.length > 0) {
      const overLimitCallouts = campaignData.googleAssets.callouts.filter(c => c && c.length > 25);
      if (overLimitCallouts.length > 0) {
        validationErrors.push(
          t('campaigns.new.validation.calloutCharLimit', '{{n}} callout(s) acima de 25 caracteres.')
            .replace('{{n}}', String(overLimitCallouts.length))
        );
      }
    }

    // Validate sitelinks (Google) — title up to 25, descs up to 35
    if (campaignData.platforms.includes('google') && campaignData.googleAssets.sitelinks.length > 0) {
      campaignData.googleAssets.sitelinks.forEach((sl, idx) => {
        if (sl.title && sl.title.length > 25) {
          validationErrors.push(
            t('campaigns.new.validation.sitelinkTitleLimit', 'Sitelink #{{n}}: título excede 25 caracteres.')
              .replace('{{n}}', String(idx + 1))
          );
        }
        if (sl.desc1 && sl.desc1.length > 35) {
          validationErrors.push(
            t('campaigns.new.validation.sitelinkDescLimit', 'Sitelink #{{n}}: descrição 1 excede 35 caracteres.')
              .replace('{{n}}', String(idx + 1))
          );
        }
        if (sl.desc2 && sl.desc2.length > 35) {
          validationErrors.push(
            t('campaigns.new.validation.sitelinkDescLimit', 'Sitelink #{{n}}: descrição 2 excede 35 caracteres.')
              .replace('{{n}}', String(idx + 1))
          );
        }
      });
    }

    if (validationErrors.length > 0) {
      validationErrors.forEach(err => toast.error(err, { duration: 5000 }));
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload any remaining local images to S3 (for all creatives, all platforms)
      const platformCreativesWithS3: Record<string, PlatformCreative[]> = {};
      for (const [platform, arr] of Object.entries(campaignData.platformCreatives)) {
        const processed: PlatformCreative[] = [];
        for (const creative of arr) {
          let current = creative;
          if (current.imageFile) {
            try {
              const s3Url = await uploadImageToS3(current.imageFile);
              current = { ...current, imageUrl: s3Url, imageFile: null };
            } catch (error) {
              console.error(`Error uploading image for platform ${platform}:`, error);
              toast.error(t('campaigns.new.imageUploadError', 'Error uploading image for {{platform}}', { platform }));
              setIsSubmitting(false);
              return;
            }
          }
          // Strip blob/data URLs that have no File to re-upload (stale references)
          if (current.imageUrl && (current.imageUrl.startsWith('blob:') || current.imageUrl.startsWith('data:'))) {
            current = { ...current, imageUrl: null, imageFile: null };
          }
          processed.push(current);
        }
        platformCreativesWithS3[platform] = processed;
      }

      // Convert platformCreatives to flat creatives array for backend (one entry per ad)
      // Pass the original ad id (if loaded from DB) so backend can upsert instead of duplicating.
      const creatives = Object.entries(platformCreativesWithS3).flatMap(([platform, arr]) =>
        arr.map(creative => {
          // Filter out blank headlines and align headlinePins to the kept indexes.
          const keptHeadlines: string[] = [];
          const keptPins: Array<1 | 2 | 3 | null> = [];
          (creative.headlines || []).forEach((h, i) => {
            if (h.trim() !== '') {
              keptHeadlines.push(h);
              if (platform === 'google') {
                keptPins.push((creative.headlinePins?.[i] as 1 | 2 | 3 | null) ?? null);
              }
            }
          });
          return {
            id: creative.id && !creative.id.startsWith('creative-') ? creative.id : undefined,
            platform,
            headlines: keptHeadlines,
            descriptions: creative.descriptions.filter(d => d.trim() !== ''),
            imageUrl: creative.imageUrl,
            videoUrl: creative.videoUrl,
            ctaText: creative.ctaText,
            destinationUrl: creative.destinationUrl,
            displayUrl: creative.displayUrl,
            keywords: creative.keywords,
            imagePrompt: creative.imagePrompt,
            ...(platform === 'google' ? { headlinePins: keptPins } : {}),
            // Google: id local do ad group escolhido pelo user no wizard.
            // Backend usa pra distribuir RSAs respeitando o limite de 3/ad group.
            ...(platform === 'google' && creative.adGroupId
              ? { adGroupId: creative.adGroupId }
              : {}),
            ...(platform === 'meta' && (creative.metaPageId || defaultMetaPageId)
              ? { metaPageId: creative.metaPageId || defaultMetaPageId }
              : {}),
            // Always send metaInstagramId on Meta ads (including empty string =
            // "Facebook-only, no IG") so the backend can distinguish "user
            // picked no-IG" from "user didn't change it".
            ...(platform === 'meta'
              ? { metaInstagramId: creative.metaInstagramId ?? '' }
              : {}),
            // Meta: chave do grupo de anúncios (ad set). Backend agrupa ads pela
            // mesma chave em settings.adsetGroup (default '1' = 1 grupo, como hoje).
            ...(platform === 'meta'
              ? { adsetGroup: creative.adsetGroup || '1' }
              : {}),
            ...(platform === 'linkedin' && (creative.linkedinOrgUrn || defaultLinkedinOrgUrn)
              ? { linkedinOrgUrn: creative.linkedinOrgUrn || defaultLinkedinOrgUrn }
              : {}),
          };
        })
      );

      // Build pixels settings — multi-pixel array per platform. Backend reads
      // the entry with `primary: true` for promoted_object/optimization. The
      // others fire client-side via the page's pixel snippets.
      const pixelsSettings: Record<string, Array<{ id: string; name: string; primary: boolean; event?: string }>> = {};
      Object.entries(selectedPixels).forEach(([platform, list]) => {
        if (!Array.isArray(list) || list.length === 0) return;
        // Ensure exactly one primary — first one if none flagged
        const hasPrimary = list.some(p => p.primary);
        const normalized = list.map((p, i) => ({
          id: p.id,
          name: p.name,
          primary: hasPrimary ? !!p.primary : i === 0,
          ...(p.event ? { event: p.event } : {})
        }));
        pixelsSettings[platform] = normalized;
      });

      const payload: any = {
        name: campaignData.name,
        description: campaignData.description,
        platform: campaignData.platforms[0] || 'meta', // For backwards compatibility
        platforms: campaignData.platforms,
        channels: campaignData.channels,
        type: campaignData.type || (() => {
          // Map channel to a valid campaign type
          const channelTypeMap: Record<string, string> = {
            google_search: 'search',
            google_display: 'display',
            youtube: 'video',
            google_shopping: 'shopping',
            facebook_feed: 'feed',
            facebook_stories: 'stories',
            instagram_feed: 'feed',
            instagram_stories: 'stories',
            instagram_reels: 'reels',
            linkedin_sponsored_content: 'sponsored_content',
            linkedin_inmail: 'sponsored_messaging',
            linkedin_text_ads: 'text_ads',
          };
          return channelTypeMap[campaignData.channels[0]] || 'feed';
        })(),
        objective: campaignData.objective,
        // Only set draft status for new campaigns; preserve existing status on edit
        ...(!isEditMode && { status: 'draft' }),
        budget_type: campaignData.budget_type,
        budget_amount: campaignData.budget_amount,
        currency: campaignData.currency,
        bid_strategy: campaignData.bid_strategy,
        max_cpc: campaignData.bid_strategy === 'manual_cpc' ? campaignData.max_cpc : null,
        target_cpa: campaignData.bid_strategy === 'target_cpa' ? campaignData.target_cpa : null,
        start_date: campaignData.start_date,
        end_date: campaignData.end_date || null,
        target_audience: campaignData.audience,
        ...(() => {
          const settingsObj: Record<string, any> = {};
          if (Object.keys(pixelsSettings).length > 0) settingsObj.pixels = pixelsSettings;
          // Meta CBO — orçamento na campanha distribuído pela Meta entre os ad sets.
          if (campaignData.platforms.includes('meta')) settingsObj.meta_cbo = campaignData.metaCbo === true;
          // AI Max (Pesquisa do Google): o push liga depois de criar a campanha.
          if (campaignData.channels.includes('google_search')) settingsObj.google_ai_max = campaignData.google_ai_max === true;
          // Estratégia de lance Google (RMF C.96/C.97/C.98). Settings são mesclados no
          // backend, então os campos são sempre enviados (null limpa o modo anterior).
          if (campaignData.platforms.includes('google')) {
            const mode = campaignData.bid_strategy === 'automatic' ? campaignData.google_bid_mode : null;
            const roasPct = Number(campaignData.google_target_roas_pct);
            if (mode === 'target_roas' && roasPct > 0) {
              settingsObj.google_bid_strategy = 'target_roas';
              settingsObj.target_roas = roasPct / 100;
            } else {
              settingsObj.google_bid_strategy = null;
            }
            if (mode === 'portfolio' && campaignData.google_portfolio_strategy) {
              settingsObj.google_portfolio_strategy = campaignData.google_portfolio_strategy;
              settingsObj.google_portfolio_strategy_name = campaignData.google_portfolio_strategy_name || null;
            } else {
              settingsObj.google_portfolio_strategy = null;
              settingsObj.google_portfolio_strategy_name = null;
            }
          }
          if (campaignData.ad_set_name) settingsObj.ad_set_name = campaignData.ad_set_name;
          if (campaignData.campaign_group_name) settingsObj.campaign_group_name = campaignData.campaign_group_name;
          if (campaignData.platforms.includes('meta') && campaignData.objective === 'leads' && campaignData.meta_lead_destination) {
            settingsObj.meta_lead_destination = campaignData.meta_lead_destination;
            // Filter config to the single field relevant to the chosen destination
            const cfg = campaignData.meta_lead_dest_config || {};
            const destCfg: Record<string, string> = {};
            if (campaignData.meta_lead_destination === 'instant_form' && cfg.leadform_id) destCfg.leadform_id = cfg.leadform_id;
            if (campaignData.meta_lead_destination === 'messenger' && cfg.welcome_message) destCfg.welcome_message = cfg.welcome_message;
            if (campaignData.meta_lead_destination === 'app') {
              if (cfg.app_id) destCfg.app_id = cfg.app_id;
              if (cfg.app_deep_link) destCfg.app_deep_link = cfg.app_deep_link;
            }
            if (campaignData.meta_lead_destination === 'whatsapp') {
              if (cfg.phone_number) destCfg.phone_number = cfg.phone_number;
              if (cfg.welcome_message) destCfg.welcome_message = cfg.welcome_message;
            }
            if (campaignData.meta_lead_destination === 'phone_call' && cfg.phone_number) destCfg.phone_number = cfg.phone_number;
            if (Object.keys(destCfg).length > 0) settingsObj.meta_lead_dest_config = destCfg;
          }
          // Google Ads asset extensions (sanitize empty entries)
          if (campaignData.platforms.includes('google')) {
            const a = campaignData.googleAssets;
            const cleanedAssets = {
              callouts: (a.callouts || []).map(c => c.trim()).filter(c => c.length > 0 && c.length <= 25),
              structuredSnippets: (a.structuredSnippets || [])
                .map(s => ({
                  header: s.header,
                  values: (s.values || []).map(v => v.trim()).filter(v => v.length > 0 && v.length <= 25),
                }))
                .filter(s => s.header && s.values.length >= 3),
              sitelinks: (a.sitelinks || [])
                .map(s => ({
                  title: (s.title || '').trim(),
                  url: (s.url || '').trim(),
                  desc1: (s.desc1 || '').trim(),
                  desc2: (s.desc2 || '').trim(),
                }))
                .filter(s => s.title && s.url),
              // WhatsApp: só vai pro backend quando ligado e com número
              whatsapp: (() => {
                const w = a.whatsapp;
                if (!w?.enabled || !(w.phone_number || '').replace(/\D/g, '')) return undefined;
                return {
                  enabled: true,
                  country_code: w.country_code || 'BR',
                  phone_number: (w.phone_number || '').trim(),
                  starter_message: (w.starter_message || '').trim().slice(0, 140),
                  cta: w.cta || 'CONTACT_US',
                  cta_description: (w.cta_description || '').trim().slice(0, 50),
                  use_as_destination: w.use_as_destination === true,
                };
              })(),
            };
            if (!cleanedAssets.whatsapp) delete (cleanedAssets as any).whatsapp;
            // Manda SEMPRE (inclusive vazio): o backend faz clean slate por
            // field_type e só assim remover uma extensão na edição chega ao
            // Google — omitir o objeto fazia o sync sair fora e a extensão
            // antiga continuava veiculando.
            settingsObj.google_assets = cleanedAssets;
            // Persiste a lista de ad groups locais junto da campanha. Ad groups
            // referenciam-se via id local; backend cria/mapeia pra Google.
            const cleanedAdGroups = (campaignData.googleAdGroups || [])
              .map(ag => ({ id: ag.id, name: (ag.name || '').trim(), externalId: ag.externalId }))
              .filter(ag => ag.id && ag.name);
            if (cleanedAdGroups.length > 0) {
              settingsObj.google_ad_groups = cleanedAdGroups;
            }
          }
          return Object.keys(settingsObj).length > 0 ? { settings: settingsObj } : {};
        })(),
        creatives,
        // Remarketing audiences: in edit mode always send (even empty) so backend can clear removed audiences
        audiences: isEditMode ? selectedAudiences : (selectedAudiences.length > 0 ? selectedAudiences : undefined),
      };

      if (isEditMode && campaignId) {
        // Progress toast pra quando o creative sync vai pra background (campanha com vídeo)
        const progressToastId = 'update-campaign-progress';
        const onProgress = (progress: number, status: string) => {
          const label = status === 'active' ? 'Processando vídeo' : status === 'waiting' ? 'Aguardando' : 'Sincronizando';
          toast.loading(`${label}... ${progress}%`, { id: progressToastId });
        };
        const response = await campaignService.updateCampaign(campaignId, payload, onProgress);
        toast.dismiss(progressToastId);

        // Se Meta dropou interesses deprecated, remove do state também e avisa user
        const metaErrs = response.syncResults?.meta?.adSetErrors || response.syncResults?.meta?.partialErrors || [];
        const allDropped: string[] = [];
        for (const e of metaErrs) {
          if (Array.isArray(e?.droppedInterests)) allDropped.push(...e.droppedInterests);
        }
        const uniqueDropped = Array.from(new Set(allDropped));
        if (uniqueDropped.length > 0) {
          setCampaignData(prev => ({
            ...prev,
            audience: {
              ...prev.audience,
              interests: (prev.audience.interests || []).filter(i => {
                const name = typeof i === 'string' ? i : i.name;
                return !uniqueDropped.includes(name);
              })
            }
          }));
          toast(
            t('campaigns.new.interestsDropped', 'Meta descontinuou estes interesses: {{names}}. Foram removidos do targeting — adicione substitutos se quiser.', {
              names: uniqueDropped.join(', ')
            }),
            { icon: '⚠️', duration: 8000 }
          );
        }

        if (response.syncResults) {
          const sr = response.syncResults;
          const platforms = Object.keys(sr);
          const failed = platforms.filter(p => !sr[p].success);
          if (failed.length === 0) {
            toast.success(t('campaigns.new.synced', 'Campaign saved and synced to platforms!'));
          } else if (failed.length < platforms.length) {
            toast.success(t('campaigns.new.updateSuccess', 'Campaign updated successfully!'));
            toast(t('campaigns.new.syncPartial', 'Sync failed on: {{platforms}}', { platforms: failed.join(', ') }), { icon: '⚠️' });
          } else {
            toast.success(t('campaigns.new.updateSuccess', 'Campaign updated successfully!'));
            toast.error(t('campaigns.new.syncFailed', 'Failed to sync to platforms'));
          }
        } else {
          toast.success(t('campaigns.new.updateSuccess', 'Campaign updated successfully!'));
        }
      } else {
        await campaignService.createCampaign(payload);
        toast.success(t('campaigns.new.createSuccess', 'Campaign created successfully!'));
      }
      navigate('/campaigns');
    } catch (error: any) {
      console.error('Error saving campaign:', error);

      // Extract detailed error message from backend response
      let errorMessage = isEditMode
        ? t('campaigns.new.updateError', 'Erro ao atualizar campanha.')
        : t('campaigns.new.createError', 'Erro ao criar campanha.');

      // Limite/assinatura: o interceptor global do api.ts já mostra o toast com CTA
      if (isHandledByInterceptor(error)) {
        return;
      }
      if (error.response?.data) {
        const data = error.response.data;
        if (data.errors && Array.isArray(data.errors)) {
          // Validation errors - show each field error
          const fieldErrors = data.errors.map((e: any) => `${e.field}: ${e.message}`).join(', ');
          errorMessage = `${data.message || errorMessage} (${fieldErrors})`;
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { duration: 6000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAvailableChannels = (): Channel[] => {
    const channels: Channel[] = [];
    if (campaignData.platforms.includes('google')) {
      channels.push(...GOOGLE_CHANNELS);
    }
    if (campaignData.platforms.includes('meta')) {
      channels.push(...META_CHANNELS);
    }
    if (campaignData.platforms.includes('linkedin')) {
      channels.push(...LINKEDIN_CHANNELS);
    }
    return channels;
  };

  const getCampaignTypes = () => {
    if (campaignData.platforms.includes('google')) return GOOGLE_CAMPAIGN_TYPES;
    return META_CAMPAIGN_TYPES;
  };

  const getObjectiveColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
      green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
    };
    return colors[color] || colors.blue;
  };

  const truncateUrl = (url: string) => {
    try {
      const u = new URL(url);
      const display = u.hostname + (u.pathname !== '/' ? u.pathname : '');
      return display.length > 35 ? display.substring(0, 35) + '...' : display;
    } catch {
      return url.length > 35 ? url.substring(0, 35) + '...' : url;
    }
  };

  // Render ad preview for a specific channel
  const renderAdPreview = (creative: Creative, channelId: string) => {
    const channel = [...GOOGLE_CHANNELS, ...META_CHANNELS, ...LINKEDIN_CHANNELS].find(c => c.id === channelId);
    if (!channel) return null;

    const headline = creative.headlines.find(h => h.trim() !== '') || t('campaigns.new.adTitle', 'Ad Title');
    const description = creative.descriptions.find(d => d.trim() !== '') || t('campaigns.new.adDescription', 'Ad description here...');

    // Resolve which page/account is posting this ad so the preview shows the
    // real brand name + profile picture instead of the "Your Brand" placeholder.
    const resolvePreviewIdentity = (): { name: string; avatarUrl?: string | null } => {
      const fallback = { name: t('campaigns.new.yourBrand', 'Your Brand') as string, avatarUrl: null as string | null };
      // Instagram Feed/Stories/Reels — use the IG account linked to the selected page
      if (channelId.startsWith('instagram_')) {
        const igId = creative.metaInstagramId && creative.metaInstagramId !== 'none' ? creative.metaInstagramId : null;
        const pageId = creative.metaPageId || defaultMetaPageId;
        const pageByIg = igId ? metaPages.find(p => p.instagram_actor_id === igId) : null;
        const pageByPage = pageId ? metaPages.find(p => p.page_id === pageId) : null;
        const src = pageByIg || pageByPage;
        if (src?.instagram_username) return { name: src.instagram_username, avatarUrl: src.instagram_picture || src.page_picture || null };
        if (src?.page_name) return { name: src.page_name, avatarUrl: src.page_picture || null };
        return fallback;
      }
      // Facebook Feed/Stories/Reels — use the selected page
      if (channelId.startsWith('facebook_')) {
        const pageId = creative.metaPageId || defaultMetaPageId;
        const page = pageId ? metaPages.find(p => p.page_id === pageId) : null;
        if (page?.page_name) return { name: page.page_name, avatarUrl: page.page_picture || null };
        return fallback;
      }
      // LinkedIn Sponsored / InMail — use the selected organization
      if (channelId.startsWith('linkedin_')) {
        const orgUrn = creative.linkedinOrgUrn || defaultLinkedinOrgUrn;
        const org = orgUrn ? linkedinOrgs.find(o => o.organization_urn === orgUrn) : null;
        if (org?.name) return { name: org.name, avatarUrl: org.logo_url || null };
        return { name: t('campaigns.new.yourCompany', 'Your Company') as string, avatarUrl: null };
      }
      return fallback;
    };
    const brandIdentity = resolvePreviewIdentity();
    const BrandAvatar: React.FC<{ size?: string; fallbackGradient?: string }> = ({ size = 'w-8 h-8', fallbackGradient = 'from-purple-500 to-pink-500' }) =>
      brandIdentity.avatarUrl
        ? <img src={brandIdentity.avatarUrl} alt={brandIdentity.name} className={`${size} rounded-full object-cover`} />
        : <div className={`${size} rounded-full bg-gradient-to-br ${fallbackGradient}`} />;
    // CTA auto-override: when this is a Meta Leads campaign with a non-website
    // destination, the CTA is dictated by Meta (WHATSAPP_MESSAGE / CALL_NOW /
    // MESSAGE_PAGE / SIGN_UP / INSTALL_APP). Mirror that on the preview so
    // what the user sees matches what gets pushed.
    const isMetaChannel = channelId.startsWith('facebook_') || channelId.startsWith('instagram_');
    const leadDest = campaignData.meta_lead_destination;
    let cta: string;
    if (isMetaChannel && campaignData.objective === 'leads' && leadDest && leadDest !== 'website') {
      const forced: Record<string, string> = {
        whatsapp: t('campaigns.cta.whatsappMessage', 'Enviar mensagem') as string,
        messenger: t('campaigns.cta.messagePage', 'Enviar mensagem') as string,
        phone_call: t('campaigns.cta.callNow', 'Ligar agora') as string,
        instant_form: t('campaigns.cta.signUp', 'Cadastre-se') as string,
        app: t('campaigns.cta.installApp', 'Instalar') as string,
      };
      cta = forced[leadDest] || t('campaigns.cta.learnMore', 'Saiba Mais') as string;
    } else {
      const ctaOption = CTA_OPTIONS.find(c => c.id === creative.ctaText);
      cta = ctaOption ? t(ctaOption.label) as string : t('campaigns.cta.learnMore', 'Learn More') as string;
    }

    // Google Search preview
    if (channelId === 'google_search') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 mb-1">{t('campaigns.new.ad', 'Ad')} - {truncateUrl(creative.destinationUrl || 'seusite.com.br')}</div>
          <div className="text-lg text-blue-600 dark:text-blue-400 font-medium hover:underline cursor-pointer mb-1">
            {headline}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{description}</div>
        </div>
      );
    }

    // Instagram/Facebook Feed preview
    if (['facebook_feed', 'instagram_feed'].includes(channelId)) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 max-w-full sm:max-w-[320px]">
          <div className="p-3 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700">
            <BrandAvatar />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">{brandIdentity.name}</div>
              <div className="text-xs text-gray-500">{t('campaigns.new.sponsored', 'Sponsored')}</div>
            </div>
          </div>
          <div className="aspect-square bg-gray-100 dark:bg-gray-700 relative">
            {creative.imageUrl ? (
              <img src={creative.imageUrl} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="p-3">
            <div className="font-medium text-gray-900 dark:text-white mb-1">{headline}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">{description}</div>
            <button className="w-full py-2 bg-primary-500 text-white rounded-lg font-medium text-sm">
              {cta}
            </button>
          </div>
        </div>
      );
    }

    // Stories preview
    if (['facebook_stories', 'instagram_stories'].includes(channelId)) {
      return (
        <div className="bg-gray-900 rounded-2xl overflow-hidden max-w-[200px] aspect-[9/16] relative">
          {creative.imageUrl ? (
            <img src={creative.imageUrl} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-purple-600 to-pink-600">
              <Film className="w-12 h-12 text-white/50" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60"></div>
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <BrandAvatar fallbackGradient="from-white/30 to-white/10" />
            <div className="text-white text-xs font-medium">{brandIdentity.name}</div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="text-white text-sm font-medium mb-2">{headline}</div>
            <button className="w-full py-2 bg-white text-gray-900 rounded-full text-xs font-medium">
              {cta}
            </button>
          </div>
        </div>
      );
    }

    // Reels preview
    if (channelId === 'instagram_reels') {
      return (
        <div className="bg-gray-900 rounded-2xl overflow-hidden max-w-[200px] aspect-[9/16] relative">
          {creative.imageUrl ? (
            <img src={creative.imageUrl} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-blue-600 to-purple-600">
              <Play className="w-16 h-16 text-white/50" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center gap-2 mb-2">
              <BrandAvatar fallbackGradient="from-white/30 to-white/10" />
              <div className="text-white text-xs font-medium">{brandIdentity.name}</div>
              <span className="text-white/60 text-xs">- {t('campaigns.new.sponsored', 'Sponsored')}</span>
            </div>
            <div className="text-white text-sm mb-3">{description.substring(0, 80)}...</div>
            <button className="w-full py-2 bg-white text-gray-900 rounded-lg text-sm font-medium">
              {cta}
            </button>
          </div>
        </div>
      );
    }

    // YouTube preview
    if (channelId === 'youtube') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 max-w-full sm:max-w-[400px]">
          <div className="aspect-video bg-gray-900 relative">
            {creative.imageUrl ? (
              <img src={creative.imageUrl} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Play className="w-16 h-16 text-white/50" />
              </div>
            )}
            <div className="absolute bottom-2 right-2 bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded">
              {t('campaigns.new.ad', 'Ad')} -0:15
            </div>
            <div className="absolute bottom-2 left-2 text-white text-xs bg-black/50 px-2 py-1 rounded">
              {t('campaigns.new.skipAd', 'Skip ad in 5s')}
            </div>
          </div>
          <div className="p-3">
            <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">{headline}</div>
            <div className="text-xs text-gray-500">{truncateUrl(creative.destinationUrl || 'seusite.com.br')}</div>
          </div>
        </div>
      );
    }

    // Display preview
    if (channelId === 'google_display') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 max-w-full sm:max-w-[300px]">
          <div className="aspect-[1.91/1] bg-gray-100 dark:bg-gray-700 relative">
            {creative.imageUrl ? (
              <img src={creative.imageUrl} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="p-3">
            <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">{headline}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">{description.substring(0, 60)}...</div>
            <button className="px-4 py-1.5 bg-primary-500 text-white rounded text-xs font-medium">
              {cta}
            </button>
          </div>
        </div>
      );
    }

    // Shopping preview
    if (channelId === 'google_shopping') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 w-[180px]">
          <div className="aspect-square bg-gray-100 dark:bg-gray-700 relative p-4">
            {creative.imageUrl ? (
              <img src={creative.imageUrl} alt="Preview" className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ShoppingBag className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="p-3">
            <div className="text-xs text-gray-500 mb-1">{t('campaigns.new.yourStore', 'Your Store')}</div>
            <div className="text-sm font-medium text-gray-900 dark:text-white mb-1 line-clamp-2">{headline}</div>
            <div className="text-lg font-bold text-primary-600">R$ 99,90</div>
            <div className="text-xs text-gray-500 mt-1">{t('campaigns.new.freeShipping', 'Free shipping')}</div>
          </div>
        </div>
      );
    }

    // LinkedIn Sponsored Content preview
    if (channelId === 'linkedin_sponsored_content') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 max-w-full sm:max-w-[400px]">
          <div className="p-3 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700">
            <BrandAvatar size="w-10 h-10" fallbackGradient="from-blue-600 to-blue-800" />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-white">{brandIdentity.name}</div>
              <div className="text-xs text-gray-500">{t('campaigns.new.sponsored', 'Sponsored')}</div>
            </div>
          </div>
          <div className="p-3">
            <div className="text-sm text-gray-700 dark:text-gray-300 mb-3">{description}</div>
          </div>
          <div className="aspect-[1.91/1] bg-gray-100 dark:bg-gray-700 relative">
            {creative.imageUrl ? (
              <img src={creative.imageUrl} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="p-3 border-t border-gray-100 dark:border-gray-700">
            <div className="font-medium text-gray-900 dark:text-white mb-1">{headline}</div>
            <div className="text-xs text-gray-500 mb-3">{truncateUrl(creative.destinationUrl || 'seusite.com.br')}</div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium">
              {cta}
            </button>
          </div>
        </div>
      );
    }

    // LinkedIn InMail preview
    if (channelId === 'linkedin_inmail') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 max-w-full sm:max-w-[350px]">
          <div className="p-4 bg-blue-600 text-white">
            <div className="text-xs opacity-80 mb-1">{t('campaigns.new.sponsoredMessage', 'Sponsored Message')}</div>
            <div className="font-medium">{headline}</div>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <BrandAvatar size="w-12 h-12" fallbackGradient="from-blue-600 to-blue-800" />
              <div>
                <div className="font-medium text-gray-900 dark:text-white">{brandIdentity.name}</div>
                <div className="text-xs text-gray-500">{t('campaigns.new.sentVia', 'Sent via LinkedIn')}</div>
              </div>
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300 mb-4">{description}</div>
            <button className="w-full py-2 bg-blue-600 text-white rounded-full text-sm font-medium">
              {cta}
            </button>
          </div>
        </div>
      );
    }

    // LinkedIn Text Ads preview
    if (channelId === 'linkedin_text_ads') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 max-w-full sm:max-w-[300px]">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded bg-gradient-to-br from-blue-600 to-blue-800 flex-shrink-0"></div>
            <div>
              <div className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer mb-1">
                {headline}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">{description}</div>
              <div className="text-xs text-gray-400 mt-1">{t('campaigns.new.ad', 'Ad')}</div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Platform Selection (Multi-select)
        const googleConnected = isPlatformConnected('google');
        const metaConnected = isPlatformConnected('meta');
        const linkedinConnected = isPlatformConnected('linkedin');
        const anyConnected = googleConnected || metaConnected || linkedinConnected;

        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('campaigns.new.whereToAdvertise', 'Where do you want to advertise?')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('campaigns.new.selectPlatforms', 'Select one or more connected ad platforms')}
              </p>
            </div>

            {loadingAccounts ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : !anyConnected ? (
              <div className="max-w-lg mx-auto text-center py-12">
                <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('campaigns.new.noAccountsConnected', 'No ad accounts connected')}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {t('campaigns.new.noAccountsDescription', 'To create campaigns, you need to connect at least one ad account (Google Ads, Meta Ads or LinkedIn Ads).')}
                </p>
                {connectedAccounts.some((a: any) => a.platform === 'openai_ads') && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 -mt-3">
                    {t('campaigns.new.openaiConnectedHint', 'Seu ChatGPT Ads está conectado: as campanhas dele são importadas automaticamente na tela de Campanhas. A criação direta por aqui chega em breve.')}
                  </p>
                )}
                <button
                  onClick={() => navigate('/settings/integrations')}
                  className="btn-primary"
                >
                  {t('campaigns.new.connectAccounts', 'Connect Accounts')}
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6 max-w-4xl mx-auto">
                  {/* Google Ads Card */}
                  <div
                    onClick={() => {
                      if (!googleConnected) {
                        navigate('/settings/integrations');
                        return;
                      }
                      togglePlatform('google');
                    }}
                    title={!googleConnected ? t('campaigns.notConnectedTitle', 'Conta não conectada - Clique para ir às configurações') : t('campaigns.clickToSelect', 'Clique para selecionar')}
                    className={`p-6 rounded-2xl border-2 transition-all text-left relative select-none ${
                      !googleConnected
                        ? 'border-gray-300 dark:border-gray-600 cursor-pointer bg-gray-100 dark:bg-gray-800/80'
                        : campaignData.platforms.includes('google')
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg cursor-pointer'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer'
                    }`}
                  >
                    {campaignData.platforms.includes('google') && googleConnected && (
                      <div className="absolute top-4 right-4 z-10">
                        <CheckCircle className="w-6 h-6 text-blue-500" />
                      </div>
                    )}
                    {!googleConnected && (
                      <div className="absolute top-4 right-4 z-10">
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-full border border-red-200 dark:border-red-800">
                          {t('campaigns.notConnected', 'Não conectado')}
                        </span>
                      </div>
                    )}
                    <div className={`flex flex-col items-center text-center gap-3 transition-all ${!googleConnected ? 'blur-[2px] grayscale opacity-50' : ''}`}>
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-sm ${googleConnected ? 'bg-white dark:bg-gray-800' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <GoogleAdsIcon className="w-10 h-10" />
                      </div>
                      <div>
                        <h3 className={`text-lg font-semibold mb-1 ${googleConnected ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Google Ads</h3>
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 mb-2 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200/60 dark:border-red-800/30">
                          <Youtube className="w-3 h-3 text-red-600" />
                          <span className="text-[10px] font-medium text-red-700 dark:text-red-400">
                            {t('campaigns.new.includesYoutube', 'Também publica em YouTube')}
                          </span>
                        </div>
                        <p className="text-xs mb-3 text-gray-500 dark:text-gray-400">
                          {t('campaigns.new.googleDescription', "World's largest search engine")}
                        </p>
                        <div className="flex flex-wrap justify-center gap-1">
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Search</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Display</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">YouTube</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Shopping</span>
                        </div>
                      </div>
                    </div>
                    {!googleConnected && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/5 dark:bg-black/20 rounded-2xl">
                        <span className="text-sm text-gray-700 dark:text-gray-200 font-medium flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg border border-gray-200 dark:border-gray-600">
                          <Link className="w-4 h-4" />
                          {t('campaigns.connectAccount', 'Conectar conta')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Meta Ads Card */}
                  <div
                    onClick={() => {
                      if (!metaConnected) {
                        navigate('/settings/integrations');
                        return;
                      }
                      togglePlatform('meta');
                    }}
                    title={!metaConnected ? t('campaigns.notConnectedTitle', 'Conta não conectada - Clique para ir às configurações') : t('campaigns.clickToSelect', 'Clique para selecionar')}
                    className={`p-6 rounded-2xl border-2 transition-all text-left relative select-none ${
                      !metaConnected
                        ? 'border-gray-300 dark:border-gray-600 cursor-pointer bg-gray-100 dark:bg-gray-800/80'
                        : campaignData.platforms.includes('meta')
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg cursor-pointer'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer'
                    }`}
                  >
                    {campaignData.platforms.includes('meta') && metaConnected && (
                      <div className="absolute top-4 right-4 z-10">
                        <CheckCircle className="w-6 h-6 text-blue-500" />
                      </div>
                    )}
                    {!metaConnected && (
                      <div className="absolute top-4 right-4 z-10">
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-full border border-red-200 dark:border-red-800">
                          {t('campaigns.notConnected', 'Não conectado')}
                        </span>
                      </div>
                    )}
                    <div className={`flex flex-col items-center text-center gap-3 transition-all ${!metaConnected ? 'blur-[2px] grayscale opacity-50' : ''}`}>
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-sm ${metaConnected ? 'bg-white dark:bg-gray-800' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <MetaAdsIcon className="w-10 h-10" />
                      </div>
                      <div>
                        <h3 className={`text-lg font-semibold mb-1 ${metaConnected ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Meta Ads</h3>
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 mb-2 rounded-full bg-pink-50 dark:bg-pink-900/20 border border-pink-200/60 dark:border-pink-800/30">
                          <InstagramIcon className="w-3 h-3 text-pink-600" />
                          <span className="text-[10px] font-medium text-pink-700 dark:text-pink-400">
                            {t('campaigns.new.includesInstagram', 'Também publica em Instagram')}
                          </span>
                        </div>
                        <p className="text-xs mb-3 text-gray-500 dark:text-gray-400">
                          {t('campaigns.new.metaDescription', 'Facebook and Instagram')}
                        </p>
                        <div className="flex flex-wrap justify-center gap-1">
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Feed</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Stories</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Reels</span>
                        </div>
                      </div>
                    </div>
                    {!metaConnected && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/5 dark:bg-black/20 rounded-2xl">
                        <span className="text-sm text-gray-700 dark:text-gray-200 font-medium flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg border border-gray-200 dark:border-gray-600">
                          <Link className="w-4 h-4" />
                          {t('campaigns.connectAccount', 'Conectar conta')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* LinkedIn Ads Card */}
                  <div
                    onClick={() => {
                      if (!linkedinConnected) {
                        navigate('/settings/integrations');
                        return;
                      }
                      togglePlatform('linkedin');
                    }}
                    title={!linkedinConnected ? t('campaigns.notConnectedTitle', 'Conta não conectada - Clique para ir às configurações') : t('campaigns.clickToSelect', 'Clique para selecionar')}
                    className={`p-6 rounded-2xl border-2 transition-all text-left relative select-none ${
                      !linkedinConnected
                        ? 'border-gray-300 dark:border-gray-600 cursor-pointer bg-gray-100 dark:bg-gray-800/80'
                        : campaignData.platforms.includes('linkedin')
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg cursor-pointer'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer'
                    }`}
                  >
                    {campaignData.platforms.includes('linkedin') && linkedinConnected && (
                      <div className="absolute top-4 right-4 z-10">
                        <CheckCircle className="w-6 h-6 text-blue-500" />
                      </div>
                    )}
                    {!linkedinConnected && (
                      <div className="absolute top-4 right-4 z-10">
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-full border border-red-200 dark:border-red-800">
                          {t('campaigns.notConnected', 'Não conectado')}
                        </span>
                      </div>
                    )}
                    <div className={`flex flex-col items-center text-center gap-3 transition-all ${!linkedinConnected ? 'blur-[2px] grayscale opacity-50' : ''}`}>
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-sm ${linkedinConnected ? 'bg-white dark:bg-gray-800' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        <LinkedInAdsIcon className="w-10 h-10" />
                      </div>
                      <div>
                        <h3 className={`text-lg font-semibold mb-1 ${linkedinConnected ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>LinkedIn Ads</h3>
                        <p className="text-xs mb-3 text-gray-500 dark:text-gray-400">
                          {t('campaigns.new.linkedinDescription', 'B2B professional network')}
                        </p>
                        <div className="flex flex-wrap justify-center gap-1">
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Sponsored</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">InMail</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Text Ads</span>
                        </div>
                      </div>
                    </div>
                    {!linkedinConnected && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/5 dark:bg-black/20 rounded-2xl">
                        <span className="text-sm text-gray-700 dark:text-gray-200 font-medium flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg border border-gray-200 dark:border-gray-600">
                          <Link className="w-4 h-4" />
                          {t('campaigns.connectAccount', 'Conectar conta')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Link to connect more accounts */}
                {(!googleConnected || !metaConnected || !linkedinConnected) && (
                  <div className="max-w-2xl mx-auto mt-4">
                    <button
                      onClick={() => navigate('/settings/integrations')}
                      className="w-full p-3 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg border border-dashed border-primary-300 dark:border-primary-700 transition-colors"
                    >
                      {t('campaigns.new.connectMoreAccounts', '+ Connect more ad accounts')}
                    </button>
                  </div>
                )}

                {campaignData.platforms.length > 0 && (
                  <div className="max-w-2xl mx-auto mt-6">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">
                          {t('campaigns.new.platformsSelected', { count: campaignData.platforms.length })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 1: // Channel Selection
        const availableChannels = getAvailableChannels();
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('campaigns.new.selectChannels', 'Select the channels')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('campaigns.new.whereAdsDisplayed', 'Choose where your ads will be displayed')}
              </p>
            </div>

            {campaignData.platforms.includes('google') && (
              <div className="max-w-3xl mx-auto mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <GoogleAdsIcon className="w-6 h-6" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Google Ads</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {GOOGLE_CHANNELS.map((channel) => {
                    const Icon = channel.icon;
                    const isSelected = campaignData.channels.includes(channel.id);
                    // Em breve: bloqueado em campanha nova; em edição, só dá pra desmarcar o que já estava.
                    const comingSoon = GOOGLE_COMING_SOON_CHANNELS.has(channel.id);
                    const isDisabled = comingSoon && !(isEditMode && isSelected);
                    return (
                      <button
                        key={channel.id}
                        type="button"
                        disabled={isDisabled}
                        aria-disabled={isDisabled}
                        title={isDisabled ? t('campaigns.rmf.channelComingSoonHint', 'Em breve: por enquanto o Google Ads funciona só com a Rede de Pesquisa.') : undefined}
                        onClick={() => { if (!isDisabled) toggleChannel(channel.id); }}
                        className={`relative p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                          isDisabled
                            ? 'border-gray-200 dark:border-gray-700 opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-800/40'
                            : isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        {comingSoon && (
                          <span className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                            {t('campaigns.rmf.comingSoon', 'Em breve')}
                          </span>
                        )}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-primary-100 dark:bg-primary-900/50' : 'bg-gray-100 dark:bg-gray-800'
                        }`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-primary-600' : 'text-gray-500'}`} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">{channel.name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t(channel.description)}</p>
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {campaignData.platforms.includes('meta') && (
              <div className="max-w-3xl mx-auto mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <MetaAdsIcon className="w-6 h-6" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Meta Ads</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {META_CHANNELS.map((channel) => {
                    const Icon = channel.icon;
                    const isSelected = campaignData.channels.includes(channel.id);
                    return (
                      <button
                        key={channel.id}
                        onClick={() => toggleChannel(channel.id)}
                        className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-primary-100 dark:bg-primary-900/50' : 'bg-gray-100 dark:bg-gray-800'
                        }`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-primary-600' : 'text-gray-500'}`} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">{channel.name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t(channel.description)}</p>
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {campaignData.platforms.includes('linkedin') && (
              <div className="max-w-3xl mx-auto mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <LinkedInAdsIcon className="w-6 h-6" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">LinkedIn Ads</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {LINKEDIN_CHANNELS.map((channel) => {
                    const Icon = channel.icon;
                    const isSelected = campaignData.channels.includes(channel.id);
                    return (
                      <button
                        key={channel.id}
                        onClick={() => toggleChannel(channel.id)}
                        className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-primary-100 dark:bg-primary-900/50' : 'bg-gray-100 dark:bg-gray-800'
                        }`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-primary-600' : 'text-gray-500'}`} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">{channel.name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t(channel.description)}</p>
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {campaignData.channels.length > 0 && (
              <div className="max-w-3xl mx-auto mt-6">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">
                      {t('campaigns.new.channelsSelected', { count: campaignData.channels.length })}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 2: // Objective
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('campaigns.new.selectObjective', 'What is the campaign objective?')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('campaigns.new.whatToAchieve', 'This helps optimize your ad delivery')}
              </p>
            </div>

            {isObjectiveLocked && (
              <div className="max-w-2xl mx-auto mb-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
                O objetivo não pode ser alterado porque a campanha já foi sincronizada em <strong>{pushedPlatforms.join(', ')}</strong>. Os painéis de anúncios não permitem alterar o objetivo de uma campanha existente. Para usar outro objetivo, crie uma nova campanha.
              </div>
            )}
            <div className="space-y-3 max-w-2xl mx-auto">
              {CAMPAIGN_OBJECTIVES.map((obj) => {
                const Icon = obj.icon;
                const disabled = isObjectiveLocked && obj.id !== campaignData.objective;
                return (
                  <button
                    key={obj.id}
                    disabled={disabled}
                    onClick={() => {
                      if (isObjectiveLocked) return;
                      setCampaignData(prev => ({ ...prev, objective: obj.id }));
                    }}
                    className={`w-full p-5 rounded-xl border-2 transition-all text-left ${
                      disabled ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700' :
                      campaignData.objective === obj.id
                        ? `${getObjectiveColor(obj.color)} shadow-md`
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        campaignData.objective === obj.id
                          ? ''
                          : 'bg-gray-100 dark:bg-gray-800'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{t(obj.name)}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t(obj.description)}</p>
                      </div>
                      {campaignData.objective === obj.id && (
                        <CheckCircle className="w-5 h-5" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 3: // Details
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('campaigns.new.campaignDetails', 'Campaign Details')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('campaigns.new.enterNameDescription', 'Enter a name and description for your campaign')}
              </p>
            </div>

            <div className="max-w-xl mx-auto space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('campaigns.campaignName', 'Campaign Name')} *
                </label>
                <input
                  type="text"
                  value={campaignData.name}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('campaigns.new.campaignNamePlaceholder', 'Ex: Summer Sales 2024')}
                  className="input text-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('campaigns.new.nameHint', 'Use a descriptive name to easily identify your campaign')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.description', 'Description')} ({t('common.optional', 'optional')})
                </label>
                <textarea
                  value={campaignData.description}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('campaigns.new.descriptionPlaceholder', 'Describe your campaign objective...')}
                  rows={4}
                  className="input"
                />
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">{t('campaigns.new.aiTip', 'AI Tip')}</p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      {t('campaigns.new.namingTip', 'Names like "Brand + Goal + Period" help with organization, e.g. "DevSkin - Leads - January 2024"')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Platform Hierarchy Info */}
              {campaignData.platforms.includes('meta') && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      {t('campaigns.hierarchy.metaTitle', 'Estrutura Meta Ads')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-400 mb-4 flex-wrap">
                    <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 rounded font-medium">Campanha</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 rounded font-medium">Conjunto de An&uacute;ncios</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 rounded font-medium">An&uacute;ncios</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                      {t('campaigns.hierarchy.adSetName', 'Nome do Conjunto de An\u00FAncios')}
                    </label>
                    <input
                      type="text"
                      value={campaignData.ad_set_name}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, ad_set_name: e.target.value }))}
                      placeholder={campaignData.name ? `${campaignData.name} - Conjunto de An\u00FAncios` : 'Ex: Minha Campanha - Conjunto de An\u00FAncios'}
                      className="input"
                    />
                  </div>

                  {/* Meta Leads destination — only when objective is "leads".
                      Maps to Meta Marketing API destination_type on the ad set:
                      ON_AD=Instant Form, MESSENGER, APP, WHATSAPP, PHONE_CALL. */}
                  {campaignData.objective === 'leads' && (
                    <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
                      <label className="block text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                        {t('campaigns.new.metaLeadDestination', 'Onde os leads serão capturados')}
                      </label>
                      <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                        {t('campaigns.new.metaLeadDestinationHint', 'Escolha como você quer que o cliente entre em contato. Cada opção muda o formato do anúncio.')}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {[
                          { id: 'website', icon: Globe, label: t('campaigns.new.leadDest.website', 'Site'), desc: t('campaigns.new.leadDest.websiteDesc', 'Landing page própria — URL vem do step de criativos') },
                          { id: 'instant_form', icon: FileText, label: t('campaigns.new.leadDest.instantForm', 'Formulário instantâneo'), desc: t('campaigns.new.leadDest.instantFormDesc', 'Formulário dentro do Meta') },
                          { id: 'messenger', icon: MessageSquare, label: t('campaigns.new.leadDest.messenger', 'Messenger'), desc: t('campaigns.new.leadDest.messengerDesc', 'Conversa direto no app') },
                          { id: 'app', icon: Monitor, label: t('campaigns.new.leadDest.app', 'Aplicativo'), desc: t('campaigns.new.leadDest.appDesc', 'Para apps mobile') },
                          { id: 'whatsapp', icon: MessageSquare, label: t('campaigns.new.leadDest.whatsapp', 'WhatsApp'), desc: t('campaigns.new.leadDest.whatsappDesc', 'Manda pra conversa no WhatsApp') },
                          { id: 'phone_call', icon: MousePointer, label: t('campaigns.new.leadDest.phoneCall', 'Chamadas'), desc: t('campaigns.new.leadDest.phoneCallDesc', 'Telefone direto') }
                        ].map(opt => {
                          const Icon = opt.icon;
                          const isSelected = campaignData.meta_lead_destination === opt.id;
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => setCampaignData(prev => ({ ...prev, meta_lead_destination: opt.id as any }))}
                              className={`p-3 rounded-lg border-2 transition-all text-left flex items-start gap-3 ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'
                              }`}
                            >
                              <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                              }`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900 dark:text-white">{opt.label}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.desc}</p>
                              </div>
                              {isSelected && <Check className="w-4 h-4 text-primary-600 flex-shrink-0 mt-1" />}
                            </button>
                          );
                        })}
                      </div>

                      <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                        {t('campaigns.new.leadDestNextStep', 'Configure o número/formulário/mensagem no próximo step (Criativos).')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {campaignData.platforms.includes('linkedin') && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      {t('campaigns.hierarchy.linkedinTitle', 'Estrutura LinkedIn Ads')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-400 mb-4 flex-wrap">
                    <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 rounded font-medium">Grupo de Campanhas</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 rounded font-medium">Campanha</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 rounded font-medium">An&uacute;ncios</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                      {t('campaigns.hierarchy.campaignGroupName', 'Nome do Grupo de Campanhas')}
                    </label>
                    <input
                      type="text"
                      value={campaignData.campaign_group_name}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, campaign_group_name: e.target.value }))}
                      placeholder={campaignData.name ? `${campaignData.name} - Grupo` : 'Ex: Minha Campanha - Grupo'}
                      className="input"
                    />
                  </div>
                </div>
              )}

              {campaignData.platforms.includes('google') && !campaignData.platforms.includes('meta') && !campaignData.platforms.includes('linkedin') && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      {t('campaigns.hierarchy.googleTitle', 'Estrutura Google Ads')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-400">
                    <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 rounded font-medium">Campanha</span>
                    <ChevronRight className="w-4 h-4" />
                    <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 rounded font-medium">An&uacute;ncios</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 4: // Budget
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('campaigns.new.budgetPeriod', 'Budget and Period')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('campaigns.new.configureInvestment', 'Configure your investment and campaign duration')}
              </p>
            </div>

            <div className="max-w-xl mx-auto space-y-6">
              {/* Budget Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('campaigns.new.budgetType', 'Budget Type')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setCampaignData(prev => ({ ...prev, budget_type: 'daily' }))}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      campaignData.budget_type === 'daily'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-900 dark:text-white">{t('campaigns.new.daily', 'Daily')}</div>
                    <div className="text-sm text-gray-500">{t('campaigns.new.dailyDesc', 'Spends X per day')}</div>
                  </button>
                  <button
                    onClick={() => setCampaignData(prev => ({ ...prev, budget_type: 'lifetime' }))}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      campaignData.budget_type === 'lifetime'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-900 dark:text-white">{t('campaigns.new.lifetime', 'Total')}</div>
                    <div className="text-sm text-gray-500">{t('campaigns.new.lifetimeDesc', 'Spends X over the entire period')}</div>
                  </button>
                </div>
              </div>

              {/* Budget Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('campaigns.new.amount', 'Budget Amount')} ({campaignData.budget_type === 'daily' ? t('campaigns.new.perDay', '/day') : t('campaigns.new.total', 'total')})
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R$</span>
                  <input
                    type="number"
                    value={campaignData.budget_amount}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, budget_amount: parseFloat(e.target.value) || 0 }))}
                    className="input pl-12 text-2xl font-bold"
                    min="1"
                    step="10"
                  />
                </div>
                {campaignData.budget_type === 'daily' && (() => {
                  if (campaignData.start_date && campaignData.end_date) {
                    const start = new Date(campaignData.start_date + 'T00:00:00');
                    const end = new Date(campaignData.end_date + 'T00:00:00');
                    const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
                    const total = campaignData.budget_amount * days;
                    return (
                      <p className="text-sm text-gray-500 mt-2">
                        {t('campaigns.new.periodEstimate', 'Estimate for the period')} ({t('campaigns.new.periodDays', '{{count}} days', { count: days })}): <span className="font-medium">R$ {total.toLocaleString('pt-BR')}</span>
                      </p>
                    );
                  }
                  return (
                    <p className="text-sm text-gray-500 mt-2">
                      {t('campaigns.new.monthlyEstimate', 'Monthly estimate')}: <span className="font-medium">R$ {(campaignData.budget_amount * 30).toLocaleString('pt-BR')}</span>
                    </p>
                  );
                })()}
              </div>

              {/* Meta CBO — Campaign Budget Optimization (só quando Meta selecionada) */}
              {campaignData.platforms.includes('meta') && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={campaignData.metaCbo === true}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, metaCbo: e.target.checked }))}
                      className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('campaigns.new.metaCbo', 'Orçamento otimizado na campanha (CBO) — a Meta distribui entre os grupos de anúncios')}
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('campaigns.new.metaCboHelp', 'Quando ativo, o orçamento fica no nível da campanha e a Meta otimiza a distribuição automaticamente entre os grupos de anúncios (ad sets).')}
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {/* Ad Schedule — dia da semana + horário (Google AD_SCHEDULE, Meta adset_schedule, LinkedIn dayparts) */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <AdScheduleInput
                  value={(campaignData.audience as any).adSchedule || DEFAULT_AD_SCHEDULE}
                  onChange={(adSchedule) =>
                    setCampaignData(prev => ({
                      ...prev,
                      audience: { ...prev.audience, adSchedule } as any,
                    }))
                  }
                  platforms={campaignData.platforms}
                />
              </div>

              {/* Bid Strategy */}
              {campaignData.channels.includes('google_search') && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!campaignData.google_ai_max}
                  onClick={() => setCampaignData(prev => ({ ...prev, google_ai_max: !prev.google_ai_max }))}
                  className={`w-full text-left flex items-start gap-3 p-4 rounded-xl border-2 transition-colors ${campaignData.google_ai_max ? 'border-violet-500 bg-violet-50/60 dark:bg-violet-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                >
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${campaignData.google_ai_max ? 'bg-violet-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}><Sparkles className="w-5 h-5" /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">{t('campaigns.aiMax.title', 'AI Max para Pesquisa')}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('campaigns.aiMax.desc', 'A IA do Google amplia as buscas atendidas, personaliza os textos e escolhe a melhor página de destino. Bom para escalar campanhas que já convertem.')}</span>
                  </span>
                  <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${campaignData.google_ai_max ? 'bg-violet-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                    <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform ${campaignData.google_ai_max ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </span>
                </button>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('campaigns.new.bidStrategy', 'Bid Strategy')}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {(() => {
                    const hasGoogle = campaignData.platforms.includes('google');
                    // Escolha exibida: modos Google (ROAS alvo / portfólio) gravam bid_strategy='automatic'.
                    const currentChoice = campaignData.bid_strategy === 'automatic' && hasGoogle && campaignData.google_bid_mode
                      ? campaignData.google_bid_mode
                      : campaignData.bid_strategy;
                    type BidChoice = 'automatic' | 'manual_cpc' | 'target_cpa' | 'target_roas' | 'portfolio';
                    const options: Array<{ value: BidChoice; label: string; desc: string; recommended?: boolean }> = [
                      {
                        value: 'automatic',
                        label: hasGoogle
                          ? t('campaigns.rmf.bidMaxConversions', 'Maximizar conversões (automático)')
                          : t('campaigns.new.automatic', 'Automatic'),
                        desc: t('campaigns.new.automaticDesc', 'Google/Meta optimizes bids automatically'),
                        recommended: true,
                      },
                      {
                        value: 'manual_cpc',
                        label: t('campaigns.new.manualCpc', 'Manual CPC'),
                        desc: t('campaigns.new.manualCpcDesc', 'Set a maximum cost per click'),
                      },
                      {
                        value: 'target_cpa',
                        label: t('campaigns.new.targetCpa', 'Target CPA'),
                        desc: t('campaigns.new.targetCpaDesc', 'Set a target cost per acquisition'),
                      },
                      ...(hasGoogle ? [
                        {
                          value: 'target_roas' as BidChoice,
                          label: t('campaigns.rmf.bidTargetRoas', 'ROAS alvo'),
                          desc: t('campaigns.rmf.bidTargetRoasDesc', 'Google Ads: maximiza o valor das conversões buscando o retorno sobre o investimento que você define.'),
                        },
                        {
                          value: 'portfolio' as BidChoice,
                          label: t('campaigns.rmf.bidPortfolio', 'Usar estratégia de portfólio (compartilhada)'),
                          desc: t('campaigns.rmf.bidPortfolioDesc', 'Google Ads: usa uma estratégia de lance compartilhada entre campanhas da conta.'),
                        },
                      ] : []),
                    ];
                    return options.map((strategy) => (
                    <button
                      key={strategy.value}
                      type="button"
                      onClick={() => setCampaignData(prev => {
                        const isGoogleMode = strategy.value === 'target_roas' || strategy.value === 'portfolio';
                        const bid: CampaignData['bid_strategy'] = isGoogleMode ? 'automatic' : strategy.value as CampaignData['bid_strategy'];
                        return {
                          ...prev,
                          bid_strategy: bid,
                          google_bid_mode: isGoogleMode ? strategy.value as 'target_roas' | 'portfolio' : null,
                          max_cpc: bid !== 'manual_cpc' ? undefined : prev.max_cpc,
                          target_cpa: bid !== 'target_cpa' ? undefined : prev.target_cpa,
                        };
                      })}
                      className={`relative p-4 pt-6 rounded-xl border-2 transition-all text-left ${
                        currentChoice === strategy.value
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {strategy.recommended && (
                        <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-sm uppercase tracking-wider">
                          ★ {t('common.recommended', 'Recomendado')}
                        </span>
                      )}
                      <div className="font-semibold text-gray-900 dark:text-white text-sm">
                        {strategy.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{strategy.desc}</div>
                    </button>
                  ));
                  })()}
                </div>

                {/* ROAS alvo (Google) — usuário digita %, salva razão (400% → 4) */}
                {campaignData.platforms.includes('google') && campaignData.bid_strategy === 'automatic' && campaignData.google_bid_mode === 'target_roas' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('campaigns.rmf.targetRoasPct', 'ROAS alvo (%)')}
                    </label>
                    <div className="relative max-w-xs">
                      <input
                        type="number"
                        value={campaignData.google_target_roas_pct || ''}
                        onChange={(e) => setCampaignData(prev => ({ ...prev, google_target_roas_pct: parseFloat(e.target.value) || undefined }))}
                        className="input pr-10"
                        min="1"
                        step="10"
                        placeholder="400"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('campaigns.rmf.targetRoasHint', 'Ex.: 400% = R$ 4 em valor de conversão para cada R$ 1 investido.')}
                    </p>
                  </div>
                )}

                {/* Estratégia de portfólio (Google) */}
                {campaignData.platforms.includes('google') && campaignData.bid_strategy === 'automatic' && campaignData.google_bid_mode === 'portfolio' && (
                  <GooglePortfolioStrategyPicker
                    value={campaignData.google_portfolio_strategy}
                    currency={campaignData.currency}
                    onChange={(rn, name) => setCampaignData(prev => ({ ...prev, google_portfolio_strategy: rn, google_portfolio_strategy_name: name }))}
                  />
                )}

                {/* Max CPC Input */}
                {campaignData.bid_strategy === 'manual_cpc' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('campaigns.new.maxCpc', 'Max CPC (R$)')}
                    </label>
                    <div className="relative max-w-xs">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R$</span>
                      <input
                        type="number"
                        value={campaignData.max_cpc || ''}
                        onChange={(e) => setCampaignData(prev => ({ ...prev, max_cpc: parseFloat(e.target.value) || undefined }))}
                        className="input pl-12"
                        min="0.01"
                        step="0.10"
                        placeholder="2.50"
                      />
                    </div>
                  </div>
                )}

                {/* Target CPA Input */}
                {campaignData.bid_strategy === 'target_cpa' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('campaigns.new.targetCpaAmount', 'Target CPA (R$)')}
                    </label>
                    <div className="relative max-w-xs">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R$</span>
                      <input
                        type="number"
                        value={campaignData.target_cpa || ''}
                        onChange={(e) => setCampaignData(prev => ({ ...prev, target_cpa: parseFloat(e.target.value) || undefined }))}
                        className="input pl-12"
                        min="0.01"
                        step="1"
                        placeholder="25.00"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('campaigns.new.startDate', 'Start Date')}
                  </label>
                  <input
                    type="date"
                    value={campaignData.start_date}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('campaigns.new.endDate', 'End Date (optional)')}
                  </label>
                  <input
                    type="date"
                    value={campaignData.end_date}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="input"
                    min={campaignData.start_date}
                  />
                </div>
              </div>

              {!campaignData.end_date && (
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  {t('campaigns.new.noEndDateHint', 'Without an end date, the campaign runs continuously until you pause it')}
                </p>
              )}
            </div>
          </div>
        );

      case 5: // Pixel Selection
        const platformPixelMap: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; pixels: PlatformPixel[] }> = {};
        // Include all selected platforms, even with 0 pixels (so user can create one)
        if (campaignData.platforms.includes('meta')) {
          platformPixelMap.meta = { label: 'Meta Pixel', icon: MetaAdsIcon, pixels: availablePixels.meta };
        }
        if (campaignData.platforms.includes('google')) {
          platformPixelMap.google = { label: 'Google Ads Conversion', icon: GoogleAdsIcon, pixels: availablePixels.google };
        }
        if (campaignData.platforms.includes('linkedin')) {
          platformPixelMap.linkedin = { label: 'LinkedIn Insight Tag', icon: LinkedInAdsIcon, pixels: availablePixels.linkedin };
        }
        const hasAnyPixels = Object.values(platformPixelMap).some(p => p.pixels.length > 0);

        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('campaigns.new.pixelTitle', 'Conversion Tracking')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('campaigns.new.pixelDescription', 'Select the pixels/conversions to track results for this campaign')}
              </p>
            </div>

            {loadingPixels ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : Object.keys(platformPixelMap).length === 0 ? (
              <div className="max-w-lg mx-auto">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Crosshair className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {t('campaigns.new.noPixelsFound', 'No pixels found')}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                      {t('campaigns.new.noPixelsDescription', 'Conecte suas contas de anúncio nas Configurações para ver e criar pixels.')}
                    </p>
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-xs text-yellow-700 dark:text-yellow-300">
                        {t('campaigns.new.pixelOptional', 'This step is optional. You can continue without selecting a pixel.')}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-6">
                {!hasAnyPixels && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 text-sm text-yellow-700 dark:text-yellow-300">
                    {t('campaigns.new.pixelOptional', 'This step is optional. You can continue without selecting a pixel.')}
                  </div>
                )}
                {Object.entries(platformPixelMap).map(([platform, { label, icon: PlatformIcon, pixels }]) => (
                  <Card key={platform}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                        <div className="flex items-center gap-3">
                          <PlatformIcon className="w-6 h-6" />
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{label}</h3>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setCreatePixelPlatform(platform as 'meta' | 'google' | 'linkedin'); setNewPixelName(''); setNewPixelCategory('DEFAULT'); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          {t('campaigns.new.createPixel', 'Criar pixel')}
                        </button>
                      </div>
                      <div className="space-y-2">
                        {(selectedPixels[platform] || []).length === 0 && (
                          <div className="p-3 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 text-center text-xs text-gray-500 dark:text-gray-400">
                            {t('campaigns.new.noPixelSelected', 'Nenhum pixel selecionado — selecione 1 ou mais abaixo')}
                          </div>
                        )}

                        {pixels.length === 0 ? (
                          <div className="p-3 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-center text-xs text-gray-500 dark:text-gray-400">
                            {t('campaigns.new.noPixelsOnPlatform', 'Nenhum pixel nesta plataforma. Clique em "Criar pixel" acima pra adicionar.')}
                          </div>
                        ) : pixels.map((pixel) => {
                          const selectedList = selectedPixels[platform] || [];
                          const selectedEntry = selectedList.find(p => p.id === pixel.id);
                          const isSelected = !!selectedEntry;
                          // Datasets that are not web pixels (WhatsApp, server, offline)
                          // can't optimize web ads — disable selection with explanation.
                          const isWebPixel = (pixel as any).isWebPixel !== false;
                          const isUnavailable = pixel.isUnavailable || (platform === 'meta' && !isWebPixel);
                          const togglePixel = () => {
                            if (isUnavailable) return;
                            setSelectedPixels(prev => {
                              const list = prev[platform] || [];
                              if (list.some(p => p.id === pixel.id)) {
                                // Removing — if removed primary, promote the next remaining to primary
                                const remaining = list.filter(p => p.id !== pixel.id);
                                if (selectedEntry?.primary && remaining.length > 0) {
                                  remaining[0] = { ...remaining[0], primary: true };
                                }
                                return { ...prev, [platform]: remaining };
                              }
                              const next: SelectedPixel = {
                                id: pixel.id,
                                name: pixel.name,
                                primary: list.length === 0,
                                event: platform === 'meta' ? 'LEAD' : undefined
                              };
                              return { ...prev, [platform]: [...list, next] };
                            });
                          };
                          const setPrimary = () => {
                            setSelectedPixels(prev => ({
                              ...prev,
                              [platform]: (prev[platform] || []).map(p => ({ ...p, primary: p.id === pixel.id }))
                            }));
                          };
                          const setEvent = (newEvent: string) => {
                            setSelectedPixels(prev => ({
                              ...prev,
                              [platform]: (prev[platform] || []).map(p => p.id === pixel.id ? { ...p, event: newEvent } : p)
                            }));
                          };
                          return (
                            <div
                              key={pixel.id}
                              className={`group flex items-stretch rounded-lg border-2 transition-all overflow-hidden ${
                                isSelected
                                  ? selectedEntry?.primary
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-500/20'
                                    : 'border-primary-300 bg-primary-50/50 dark:bg-primary-900/10'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                              } ${isUnavailable ? 'opacity-50' : ''}`}
                            >
                              <button
                                type="button"
                                onClick={togglePixel}
                                disabled={isUnavailable}
                                className="flex-1 text-left p-4 min-w-0 disabled:cursor-not-allowed"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                      {pixel.name}
                                      {isSelected && selectedEntry?.primary && (
                                        <span className="ml-2 text-[10px] uppercase font-bold bg-primary-500 text-white px-1.5 py-0.5 rounded">
                                          Primary
                                        </span>
                                      )}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                                      <span className="text-xs text-gray-500">ID: {pixel.id}</span>
                                      {(pixel as any).type && (pixel as any).type !== 'PIXEL' && (
                                        <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 rounded-full text-amber-700 dark:text-amber-300" title="Datasets não-web (WhatsApp, server, offline) não otimizam campanhas web">
                                          {(pixel as any).type}
                                        </span>
                                      )}
                                      {pixel.category && (
                                        <span className="text-xs px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 rounded-full text-blue-600 dark:text-blue-400">
                                          {pixel.category}
                                        </span>
                                      )}
                                      {pixel.lastFired && (
                                        <span className="text-xs text-green-600 dark:text-green-400">
                                          {t('campaigns.new.lastFired', 'Last fired')}: {new Date(pixel.lastFired).toLocaleDateString()}
                                        </span>
                                      )}
                                      {pixel.isUnavailable && (
                                        <span className="text-xs text-red-500">
                                          {t('campaigns.new.pixelUnavailable', 'Unavailable')}
                                        </span>
                                      )}
                                      {platform === 'meta' && !isWebPixel && (
                                        <span className="text-xs text-amber-600">
                                          {t('campaigns.new.notWebPixel', 'Não é pixel web — não otimiza campanha')}
                                        </span>
                                      )}
                                    </div>
                                    {/* Per-pixel event picker (Meta only) — selecting which custom_event_type this pixel optimizes for */}
                                    {isSelected && platform === 'meta' && isWebPixel && (
                                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Evento Meta:</label>
                                        <select
                                          value={selectedEntry?.event || 'LEAD'}
                                          onChange={(e) => { e.stopPropagation(); setEvent(e.target.value); }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-xs border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded px-2 py-1"
                                        >
                                          <option value="LEAD">LEAD</option>
                                          <option value="COMPLETE_REGISTRATION">COMPLETE_REGISTRATION</option>
                                          <option value="CONTACT">CONTACT</option>
                                          <option value="SCHEDULE">SCHEDULE</option>
                                          <option value="PURCHASE">PURCHASE</option>
                                          <option value="INITIATE_CHECKOUT">INITIATE_CHECKOUT</option>
                                          <option value="ADD_TO_CART">ADD_TO_CART</option>
                                          <option value="VIEW_CONTENT">VIEW_CONTENT</option>
                                          <option value="SEARCH">SEARCH</option>
                                          <option value="SUBSCRIBE">SUBSCRIBE</option>
                                          <option value="START_TRIAL">START_TRIAL</option>
                                          <option value="SUBMIT_APPLICATION">SUBMIT_APPLICATION</option>
                                        </select>
                                        {selectedList.length > 1 && !selectedEntry?.primary && (
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setPrimary(); }}
                                            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                                          >
                                            Tornar primary
                                          </button>
                                        )}
                                      </div>
                                    )}
                                    {/* Set primary toggle for non-Meta platforms */}
                                    {isSelected && platform !== 'meta' && selectedList.length > 1 && !selectedEntry?.primary && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setPrimary(); }}
                                        className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium"
                                      >
                                        Tornar primary
                                      </button>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                                  )}
                                </div>
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleViewPixelSnippet(platform as 'meta' | 'google' | 'linkedin', { id: pixel.id, name: pixel.name }); }}
                                className="flex items-center justify-center px-3 border-l border-gray-200 dark:border-gray-700 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                                title={t('campaigns.new.viewPixelCode', 'Ver código de instalação')}
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleDeletePixel(platform as 'meta' | 'google' | 'linkedin', { id: pixel.id, name: pixel.name }); }}
                                className="flex items-center justify-center px-3 border-l border-gray-200 dark:border-gray-700 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                title={t('campaigns.new.deletePixel', 'Remover pixel')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Offline Events - Meta only */}
                {campaignData.platforms.includes('meta') && selectedPixels.meta && availablePixels.meta.length > 0 && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-2">
                        <MetaAdsIcon className="w-6 h-6" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('campaigns.new.offlineEvents', 'Offline Events (optional)')}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {t('campaigns.new.offlineEventsDescription', 'Use the same pixel to also track offline conversions (phone calls, in-store visits, etc.)')}
                      </p>
                      <div className="space-y-2">
                        {(selectedPixels.meta_offline || []).length === 0 && (
                          <div className="p-3 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 text-center text-xs text-gray-500 dark:text-gray-400">
                            {t('campaigns.new.noOfflineEvents', 'Sem rastreamento de eventos offline (opcional)')}
                          </div>
                        )}
                        {availablePixels.meta.map((pixel) => {
                          const offlineList = selectedPixels.meta_offline || [];
                          const isSelected = offlineList.some(p => p.id === pixel.id);
                          const toggleOffline = () => {
                            setSelectedPixels(prev => {
                              const list = prev.meta_offline || [];
                              if (list.some(p => p.id === pixel.id)) {
                                return { ...prev, meta_offline: list.filter(p => p.id !== pixel.id) };
                              }
                              return { ...prev, meta_offline: [...list, { id: pixel.id, name: pixel.name, primary: list.length === 0 }] };
                            });
                          };
                          return (
                            <button
                              type="button"
                              key={`offline-${pixel.id}`}
                              onClick={toggleOffline}
                              className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white text-sm">{pixel.name}</p>
                                  <span className="text-xs text-gray-500">ID: {pixel.id}</span>
                                </div>
                                {isSelected && <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      {t('campaigns.new.pixelInfo', 'Selecting a pixel allows the platform to track conversions and optimize ad delivery for your campaign goals.')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 6: // Audience
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('campaigns.new.audience', 'Target Audience')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('campaigns.new.defineAudience', 'Define who will see your ads')}
              </p>
            </div>

            <div className="max-w-xl mx-auto space-y-6">
              {/* Age Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('campaigns.new.ageRange', 'Age Range')}
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block">{t('common.min', 'Min')}</label>
                    <select
                      value={campaignData.audience.age_min}
                      onChange={(e) => setCampaignData(prev => ({
                        ...prev,
                        audience: { ...prev.audience, age_min: parseInt(e.target.value) }
                      }))}
                      className="input"
                    >
                      {[13, 18, 21, 25, 30, 35, 40, 45, 50, 55, 60].map(age => (
                        <option key={age} value={age}>{age} {t('campaigns.new.years', 'years')}</option>
                      ))}
                    </select>
                  </div>
                  <span className="text-gray-400 mt-5">{t('campaigns.new.to', 'to')}</span>
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block">{t('common.max', 'Max')}</label>
                    <select
                      value={campaignData.audience.age_max}
                      onChange={(e) => setCampaignData(prev => ({
                        ...prev,
                        audience: { ...prev.audience, age_max: parseInt(e.target.value) }
                      }))}
                      className="input"
                    >
                      {[25, 30, 35, 40, 45, 50, 55, 60, 65].map(age => (
                        <option key={age} value={age}>{age} {t('campaigns.new.years', 'years')}</option>
                      ))}
                      <option value={65}>65+ {t('campaigns.new.years', 'years')}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('campaigns.new.gender', 'Gender')}
                </label>
                <div className="flex gap-3">
                  {[
                    { id: 'all', label: t('campaigns.new.allGenders', 'All') },
                    { id: 'male', label: t('campaigns.new.male', 'Male') },
                    { id: 'female', label: t('campaigns.new.female', 'Female') },
                  ].map(gender => (
                    <button
                      key={gender.id}
                      onClick={() => setCampaignData(prev => ({
                        ...prev,
                        audience: { ...prev.audience, genders: [gender.id] }
                      }))}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                        campaignData.audience.genders.includes(gender.id)
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      {gender.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Devices — seleção de dispositivos onde veicular + bid adjustment Google */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('campaigns.devices.title', 'Dispositivos')}
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {t('campaigns.devices.subtitle', 'Onde exibir os anúncios')}
                  </span>
                </label>
                <DeviceTargetingInput
                  value={campaignData.audience.devices || DEFAULT_DEVICES}
                  onChange={(devices) =>
                    setCampaignData(prev => ({
                      ...prev,
                      audience: { ...prev.audience, devices }
                    }))
                  }
                  platforms={campaignData.platforms}
                />
              </div>

              {/* Locations with Radius */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <MapPin className="inline w-4 h-4 mr-1" />
                  {t('campaigns.new.locations', 'Onde exibir os anúncios')}
                </label>

                {/* Selected locations as cards with radius */}
                {(campaignData.audience.locations?.length || 0) > 0 && (
                  <div className="space-y-2 mb-3">
                    {campaignData.audience.locations.map((loc, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 group hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                          {loc.targetType === 'Country' ? (
                            <Globe className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          ) : (
                            <MapPin className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {loc.name}
                            </span>
                            {loc.targetType && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium uppercase tracking-wider">
                                {loc.targetType}
                              </span>
                            )}
                            {loc.geoTargetId && (
                              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                ID: {loc.geoTargetId}
                              </span>
                            )}
                          </div>
                          {loc.canonicalName && loc.canonicalName !== loc.name && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                              {loc.canonicalName}
                            </p>
                          )}
                          {loc.targetType !== 'Country' && (
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-xs text-gray-500 whitespace-nowrap w-8">
                                {loc.radius === 0 ? '∞' : `${loc.radius}km`}
                              </span>
                              <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={loc.radius}
                                onChange={(e) => {
                                  const newRadius = parseInt(e.target.value);
                                  setCampaignData(prev => ({
                                    ...prev,
                                    audience: {
                                      ...prev.audience,
                                      locations: prev.audience.locations.map((l, i) =>
                                        i === idx ? { ...l, radius: newRadius } : l
                                      )
                                    }
                                  }));
                                }}
                                className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full appearance-none cursor-pointer accent-primary-500"
                              />
                              <span className="text-xs text-gray-500 w-16 text-right">
                                {loc.radius === 0 ? t('campaigns.new.entireArea', 'Entire area') : `${loc.radius} km`}
                              </span>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCampaignData(prev => ({
                            ...prev,
                            audience: { ...prev.audience, locations: prev.audience.locations.filter((_, i) => i !== idx) }
                          }))}
                          className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Location autocomplete search */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={locationSearchQuery}
                      onChange={(e) => {
                        setLocationSearchQuery(e.target.value);
                        handleLocationSearch(e.target.value);
                      }}
                      onFocus={() => locationSuggestions.length > 0 && setShowLocationDropdown(true)}
                      onBlur={() => setTimeout(() => setShowLocationDropdown(false), 200)}
                      placeholder={t('campaigns.new.locationsPlaceholder', 'Search city, state or country...')}
                      className="input pl-9"
                    />
                  </div>
                  {isSearchingLocations && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  )}
                  {/* Dropdown results */}
                  {showLocationDropdown && locationSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {locationSuggestions.map((suggestion) => {
                        const isCountry = suggestion.targetType === 'Country';
                        return (
                          <button
                            key={suggestion.id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectLocation(suggestion)}
                            className="w-full text-left px-4 py-3 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                {isCountry ? (
                                  <Globe className="w-4 h-4 text-gray-500" />
                                ) : (
                                  <MapPin className="w-4 h-4 text-gray-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 dark:text-white text-sm">{suggestion.name}</p>
                                {suggestion.canonicalName && suggestion.canonicalName !== suggestion.name && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{suggestion.canonicalName}</p>
                                )}
                              </div>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 uppercase tracking-wider flex-shrink-0">
                                {suggestion.targetType}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Location Map */}
                <LocationMap
                  locations={campaignData.audience.locations}
                  onLocationCoordsResolved={(idx, lat, lng) => {
                    setCampaignData(prev => ({
                      ...prev,
                      audience: {
                        ...prev.audience,
                        locations: prev.audience.locations.map((l, i) =>
                          i === idx ? { ...l, lat, lng } : l
                        )
                      }
                    }));
                  }}
                />

                {/* Meta audience reach estimate — only when Meta is selected */}
                {campaignData.platforms.includes('meta') && (campaignData.audience.locations?.length || 0) > 0 && (
                  <div className="mt-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500 text-white flex items-center justify-center shrink-0">
                        <Users className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                          {t('campaigns.new.metaAudienceSizeLabel', 'Alcance estimado (Meta)')}
                        </div>
                        {estimatingMetaAudience ? (
                          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('campaigns.new.metaAudienceEstimating', 'Calculando com dados do Meta...')}
                          </div>
                        ) : metaAudienceEstimate && metaAudienceEstimate.lower != null && metaAudienceEstimate.upper != null ? (
                          <>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
                              {Number(metaAudienceEstimate.lower).toLocaleString('pt-BR')} – {Number(metaAudienceEstimate.upper).toLocaleString('pt-BR')}
                              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
                                {t('campaigns.new.metaAudienceUsers', 'pessoas')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {t('campaigns.new.metaAudienceHint', 'Estimativa mensal ativa baseada nas configurações de localização, idade e gênero.')}
                            </p>
                          </>
                        ) : metaAudienceEstimate?.reason === 'meta_not_connected' ? (
                          <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
                            {t('campaigns.new.metaAudienceNotConnected', 'Conecte sua conta do Meta Ads para ver o alcance estimado.')}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            {t('campaigns.new.metaAudienceUnavailable', 'Sem estimativa disponível. Ajuste a localização ou verifique sua conexão com o Meta.')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Idiomas do público (Google Ads, RMF C.30) */}
              {campaignData.platforms.includes('google') && (
                <GoogleLanguagesInput
                  value={campaignData.audience.languages || []}
                  onChange={(languages) =>
                    setCampaignData(prev => ({
                      ...prev,
                      audience: { ...prev.audience, languages }
                    }))
                  }
                />
              )}

              {/* Meta Interests — mostra quando Meta foi selecionada OU quando nenhuma plataforma
                   foi escolhida ainda (user pode estar preenchendo o audience antes do passo de plataforma).
                   Se a campanha é só Google ou só LinkedIn, esconde (não se aplica). */}
              {(campaignData.platforms.length === 0 || campaignData.platforms.includes('meta')) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('campaigns.new.metaInterests', 'Meta — Interesses / Comportamentos / Demografia')} ({t('common.optional', 'optional')})
                  </label>
                  {campaignData.platforms.length === 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                      {t('campaigns.new.metaInterestsHint', 'Este targeting só é aplicado se Meta estiver selecionada como plataforma.')}
                    </p>
                  )}
                  <MetaInterestsInput
                    values={campaignData.audience.interests}
                    onChange={(interests) => {
                      setCampaignData(prev => ({
                        ...prev,
                        audience: { ...prev.audience, interests }
                      }));
                    }}
                    placeholder={t('campaigns.new.metaInterestsPlaceholder', 'Buscar no catálogo oficial da Meta...')}
                  />
                </div>
              )}

              {/* LinkedIn B2B Targeting — mostra quando LinkedIn foi selecionada OU nenhuma plataforma */}
              {(campaignData.platforms.length === 0 || campaignData.platforms.includes('linkedin')) && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('campaigns.new.linkedinTargeting', 'LinkedIn — Targeting B2B')} ({t('common.optional', 'optional')})
                  </label>
                  {campaignData.platforms.length === 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                      {t('campaigns.new.linkedinTargetingHint', 'Este targeting só é aplicado se LinkedIn estiver selecionado como plataforma.')}
                    </p>
                  )}
                  <LinkedInTargetingInput
                    value={campaignData.audience.linkedin || {}}
                    onChange={(linkedin) => {
                      setCampaignData(prev => ({
                        ...prev,
                        audience: { ...prev.audience, linkedin }
                      }));
                    }}
                  />
                </div>
              )}

              {/* Remarketing Audiences */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Target className="inline w-4 h-4 mr-1" />
                    {t('campaigns.new.remarketingAudiences', 'Audiências de Remarketing')}
                    {campaignData.objective === 'remarketing'
                      ? <span className="text-red-500 ml-1">*</span>
                      : ` (${t('common.optional', 'optional')})`
                    }
                  </label>
                  <button
                    type="button"
                    onClick={openCreateAudienceModal}
                    className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    {t('audiences.create', 'Nova Audiência')}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  {t('campaigns.new.remarketingDesc', 'Selecione audiências existentes para incluir ou excluir desta campanha')}
                </p>

                {/* Warning for remarketing without audience */}
                {campaignData.objective === 'remarketing' && selectedAudiences.length === 0 && !loadingAudiences && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        {t('campaigns.new.remarketingRequiresAudience', 'Campanhas de remarketing requerem pelo menos uma audiência selecionada.')}
                      </p>
                    </div>
                  </div>
                )}

                {loadingAudiences ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Selected audiences for campaign */}
                    {selectedAudiences.length > 0 && (
                      <div className="space-y-2 mb-4">
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {t('campaigns.new.selectedAudiences', 'Audiências selecionadas para esta campanha:')}
                        </p>
                        {selectedAudiences.map((selected, idx) => {
                          const audience = availableAudiences.find(a => a.id === selected.audienceId);
                          if (!audience) return null;
                          return (
                            <div
                              key={idx}
                              className={`flex items-center justify-between p-3 rounded-lg border ${
                                selected.type === 'include'
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`p-1.5 rounded ${selected.type === 'include' ? 'bg-green-100 dark:bg-green-800 text-green-600' : 'bg-red-100 dark:bg-red-800 text-red-600'}`}>
                                  {selected.type === 'include' ? <Target className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white text-sm">{audience.name}</p>
                                  <p className="text-xs text-gray-500">{audience.size?.toLocaleString() || 0} usuários</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <select
                                  value={selected.type}
                                  onChange={(e) => {
                                    const newSelected = [...selectedAudiences];
                                    newSelected[idx].type = e.target.value as 'include' | 'exclude';
                                    setSelectedAudiences(newSelected);
                                  }}
                                  className="text-xs border rounded px-2 py-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                                >
                                  <option value="include">{t('campaigns.new.include', 'Incluir')}</option>
                                  <option value="exclude">{t('campaigns.new.exclude', 'Excluir')}</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => setSelectedAudiences(prev => prev.filter((_, i) => i !== idx))}
                                  className="p-1 text-gray-400 hover:text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Available audiences list with CRUD */}
                    {availableAudiences.length === 0 ? (
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                        <Users className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {t('campaigns.new.noAudiences', 'Nenhuma audiência disponível')}
                        </p>
                        <button
                          type="button"
                          onClick={openCreateAudienceModal}
                          className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          {t('audiences.createFirst', 'Criar primeira audiência')}
                        </button>
                      </div>
                    ) : (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
                        {availableAudiences.map(audience => {
                          const isSelected = selectedAudiences.some(s => s.audienceId === audience.id);
                          const getTypeIcon = (type: string) => {
                            if (type === 'website_visitors') return <Globe className="w-4 h-4" />;
                            if (type === 'leads') return <UserPlus className="w-4 h-4" />;
                            if (type === 'customer_list') return <Upload className="w-4 h-4" />;
                            if (type === 'converters') return <Target className="w-4 h-4" />;
                            return <Users className="w-4 h-4" />;
                          };
                          const getTypeLabel = (type: string) => {
                            const labels: Record<string, string> = {
                              website_visitors: t('audiences.types.websiteVisitors', 'Visitantes'),
                              customer_list: t('audiences.types.customerList', 'Lista'),
                              leads: t('audiences.types.leads', 'Leads'),
                              converters: t('audiences.types.converters', 'Convertidos'),
                            };
                            return labels[type] || type;
                          };
                          return (
                            <div
                              key={audience.id}
                              className={`flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                                isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                  {getTypeIcon(audience.type)}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white text-sm">{audience.name}</p>
                                  <p className="text-xs text-gray-500">
                                    {getTypeLabel(audience.type)} • {audience.size?.toLocaleString() || 0} usuários
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {!isSelected && (
                                  <button
                                    type="button"
                                    onClick={() => setSelectedAudiences(prev => [...prev, {
                                      audienceId: audience.id,
                                      type: 'include',
                                      bidAdjustment: 1.0
                                    }])}
                                    className="px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-800/40"
                                  >
                                    + {t('common.add', 'Adicionar')}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => openEditAudienceModal(audience)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                  title={t('common.edit', 'Editar')}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleteAudienceId(audience.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-500"
                                  title={t('common.delete', 'Excluir')}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Delete Audience Confirmation */}
              {deleteAudienceId && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md mx-4 shadow-xl">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {t('audiences.confirmDelete', 'Confirmar exclusão')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {t('audiences.confirmDeleteDesc', 'Tem certeza que deseja excluir esta audiência? Esta ação não pode ser desfeita.')}
                    </p>
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setDeleteAudienceId(null)}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        {t('common.cancel', 'Cancelar')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAudience(deleteAudienceId)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        {t('common.delete', 'Excluir')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 7: // Creatives - Platform-specific forms
        // Get active platform creative for preview
        const previewSourceCreative = activePlatformTab ? getActiveCreative(activePlatformTab) : undefined;
        const activeCreativeForPreview = previewSourceCreative
          ? {
              ...createEmptyCreative(),
              headlines: previewSourceCreative.headlines,
              descriptions: previewSourceCreative.descriptions,
              imageUrl: previewSourceCreative.imageUrl,
              videoUrl: previewSourceCreative.videoUrl,
              ctaText: previewSourceCreative.ctaText,
              destinationUrl: previewSourceCreative.destinationUrl,
              metaPageId: previewSourceCreative.metaPageId,
              metaInstagramId: previewSourceCreative.metaInstagramId,
              linkedinOrgUrn: previewSourceCreative.linkedinOrgUrn,
            }
          : createEmptyCreative();

        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('campaigns.new.creatives', 'Creatives and Ads')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('campaigns.new.createAdsPerPlatform', 'Create specific ad content for each platform')}
              </p>
            </div>

            {/* Platform Tabs */}
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 border-b border-gray-200 dark:border-gray-700">
                {campaignData.platforms.map((platform) => {
                  const spec = PLATFORM_SPECS[platform];
                  if (!spec) return null;
                  const PlatformIcon = spec.icon;
                  const creatives = campaignData.platformCreatives[platform] || [];
                  const isComplete = creatives.length > 0 && creatives.every(creative =>
                    creative.headlines.some(h => h.trim() !== '') &&
                    creative.descriptions.some(d => d.trim() !== '') &&
                    creative.destinationUrl.trim() !== ''
                  );

                  return (
                    <button
                      key={platform}
                      onClick={() => setActivePlatformTab(platform)}
                      className={`flex items-center gap-3 px-5 py-3 rounded-t-lg border-b-2 transition-all whitespace-nowrap ${
                        platform === activePlatformTab
                          ? 'border-primary-500 bg-white dark:bg-gray-800 text-primary-700 dark:text-primary-300 shadow-sm'
                          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      <PlatformIcon className="w-5 h-5" />
                      <span className="font-medium">{spec.name}</span>
                      {creatives.length > 1 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          platform === activePlatformTab
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {creatives.length}
                        </span>
                      )}
                      {isComplete && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Platform Info Banner — limits reflect the selected channels */}
              {activePlatformTab && PLATFORM_SPECS[activePlatformTab] && (() => {
                const plat = activePlatformTab;
                const ps = PLATFORM_SPECS[plat];
                const chans = (campaignData.channels || []).filter((c) => {
                  const chSpec = (CHANNEL_SPECS as any)[c];
                  if (!chSpec) return false;
                  if (plat === 'google') return c.startsWith('google_') || c === 'youtube';
                  if (plat === 'meta') return c.startsWith('facebook_') || c.startsWith('instagram_');
                  if (plat === 'linkedin') return c.startsWith('linkedin_');
                  return false;
                });
                const hLimit = (() => {
                  const ls = chans.map((c) => (CHANNEL_SPECS as any)[c]?.headlines?.charLimit).filter((n): n is number => typeof n === 'number' && n > 0);
                  return ls.length > 0 ? Math.max(ps.headlines.charLimit, ...ls) : ps.headlines.charLimit;
                })();
                const dLimit = (() => {
                  const ls = chans.map((c) => (CHANNEL_SPECS as any)[c]?.descriptions?.charLimit).filter((n): n is number => typeof n === 'number' && n > 0);
                  return ls.length > 0 ? Math.max(ps.descriptions.charLimit, ...ls) : ps.descriptions.charLimit;
                })();
                return (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-700 dark:text-blue-300">
                        <p className="font-medium mb-1">{ps.name} {t('campaigns.new.specifications', 'Specifications')}:</p>
                        <ul className="space-y-1 text-blue-600 dark:text-blue-400">
                          <li>• {t('campaigns.new.headlines', 'Headlines')}: {ps.headlines.min}-{ps.headlines.max} ({t('campaigns.new.maxChars', 'max')} {hLimit} {t('campaigns.new.chars', 'chars')})</li>
                          <li>• {t(ps.descriptions.label)}: {ps.descriptions.min}-{ps.descriptions.max} ({t('campaigns.new.maxChars', 'max')} {dLimit} {t('campaigns.new.chars', 'chars')})</li>
                          <li>• {t('campaigns.new.imageSize', 'Image')}: {ps.image.recommendedSize} {ps.image.required ? `(${t('common.required', 'required')})` : `(${t('common.optional', 'optional')})`}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
                {/* Platform Creative Form */}
                <div className="space-y-6">
                  <Card>
                    <CardContent className="pt-6">
                      {activePlatformTab ? (
                        renderPlatformCreativeForm(activePlatformTab)
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>{t('campaigns.new.selectPlatformFirst', 'Select a platform to configure')}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Preview */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900 dark:text-white">{t('campaigns.new.preview', 'Preview')}</h3>
                    <select
                      value={previewChannel || ''}
                      onChange={(e) => setPreviewChannel(e.target.value)}
                      className="input py-1 px-3 text-sm w-auto"
                    >
                      {campaignData.channels
                        .filter(ch => {
                          // Only show channels from active platform
                          const channel = [...GOOGLE_CHANNELS, ...META_CHANNELS, ...LINKEDIN_CHANNELS].find(c => c.id === ch);
                          return channel?.platform === activePlatformTab;
                        })
                        .map(ch => {
                          const channel = [...GOOGLE_CHANNELS, ...META_CHANNELS, ...LINKEDIN_CHANNELS].find(c => c.id === ch);
                          return (
                            <option key={ch} value={ch}>{channel?.name}</option>
                          );
                        })}
                    </select>
                  </div>

                  <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6 min-h-[400px] flex items-center justify-center">
                    {previewChannel ? (
                      renderAdPreview(activeCreativeForPreview, previewChannel)
                    ) : (
                      <div className="text-center text-gray-500">
                        <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>{t('campaigns.new.selectChannel', 'Select a channel to preview')}</p>
                      </div>
                    )}
                  </div>

                  {/* Platform completion status */}
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('campaigns.new.platformStatus', 'Platform Status')}
                    </h4>
                    <div className="space-y-2">
                      {campaignData.platforms.map(platform => {
                        const spec = PLATFORM_SPECS[platform];
                        if (!spec) return null;
                        const creatives = campaignData.platformCreatives[platform] || [];
                        const isComplete = creatives.length > 0 && creatives.every(creative =>
                          creative.headlines.some(h => h.trim() !== '') &&
                          creative.descriptions.some(d => d.trim() !== '') &&
                          creative.destinationUrl.trim() !== ''
                        );
                        const PlatformIcon = spec.icon;

                        return (
                          <div
                            key={platform}
                            onClick={() => setActivePlatformTab(platform)}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                              platform === activePlatformTab
                                ? 'bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500'
                                : 'bg-gray-50 dark:bg-gray-800/50 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            <PlatformIcon className="w-5 h-5" />
                            <span className="flex-1 font-medium text-gray-900 dark:text-white">{spec.name}</span>
                            {isComplete ? (
                              <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                                <CheckCircle className="w-4 h-4" />
                                {t('common.complete', 'Complete')}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 text-sm">
                                <AlertCircle className="w-4 h-4" />
                                {t('common.incomplete', 'Incomplete')}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Channels for active platform */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('campaigns.new.channelsForPlatform', 'Channels for')} {activePlatformTab && PLATFORM_SPECS[activePlatformTab]?.name}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {campaignData.channels
                        .filter(ch => {
                          const channel = [...GOOGLE_CHANNELS, ...META_CHANNELS, ...LINKEDIN_CHANNELS].find(c => c.id === ch);
                          return channel?.platform === activePlatformTab;
                        })
                        .map(ch => {
                          const channel = [...GOOGLE_CHANNELS, ...META_CHANNELS, ...LINKEDIN_CHANNELS].find(c => c.id === ch);
                          const Icon = channel?.icon || Image;
                          return (
                            <button
                              key={ch}
                              onClick={() => setPreviewChannel(ch)}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                                ch === previewChannel
                                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                              }`}
                            >
                              <Icon className="w-3 h-3" />
                              {channel?.name}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 8: // Review
        const selectedObjective = CAMPAIGN_OBJECTIVES.find(o => o.id === campaignData.objective);

        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {isEditMode ? t('campaigns.new.editTitle', 'Edit Campaign') : t('campaigns.new.review', 'Review Campaign')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t('campaigns.new.reviewBeforeCreating', 'Check the details before creating')}
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-6">
              <Card>
                <CardContent className="pt-6 space-y-6">
                  {/* Header with platforms */}
                  <div className="flex items-center gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex -space-x-2">
                      {campaignData.platforms.includes('google') && (
                        <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-xl flex items-center justify-center border-2 border-white dark:border-gray-800 shadow">
                          <GoogleAdsIcon className="w-7 h-7" />
                        </div>
                      )}
                      {campaignData.platforms.includes('meta') && (
                        <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-xl flex items-center justify-center border-2 border-white dark:border-gray-800 shadow">
                          <MetaAdsIcon className="w-7 h-7" />
                        </div>
                      )}
                      {campaignData.platforms.includes('linkedin') && (
                        <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-xl flex items-center justify-center border-2 border-white dark:border-gray-800 shadow">
                          <LinkedInAdsIcon className="w-7 h-7" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">{campaignData.name}</h3>
                      <p className="text-sm text-gray-500">
                        {campaignData.platforms.map(p => p === 'google' ? 'Google Ads' : p === 'meta' ? 'Meta Ads' : 'LinkedIn Ads').join(' + ')}
                      </p>
                    </div>
                  </div>

                  {/* Channels */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">{t('campaigns.steps.channels', 'Channels')}</p>
                    <div className="flex flex-wrap gap-2">
                      {campaignData.channels.map(ch => {
                        const channel = [...GOOGLE_CHANNELS, ...META_CHANNELS, ...LINKEDIN_CHANNELS].find(c => c.id === ch);
                        const Icon = channel?.icon || Image;
                        return (
                          <span key={ch} className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <Icon className="w-3 h-3" />
                            {channel?.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{t('campaigns.objective', 'Objective')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedObjective ? t(selectedObjective.name) : ''}</p>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{t('campaigns.budget', 'Budget')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        R$ {campaignData.budget_amount.toLocaleString('pt-BR')} / {campaignData.budget_type === 'daily' ? t('campaigns.new.perDay', 'day') : t('campaigns.new.total', 'total')}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{t('campaigns.new.period', 'Period')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {new Date(campaignData.start_date).toLocaleDateString()}
                        {campaignData.end_date ? ` - ${new Date(campaignData.end_date).toLocaleDateString()}` : ` - ${t('campaigns.new.continuous', 'Continuous')}`}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{t('campaigns.new.bidStrategy', 'Bid Strategy')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {(() => {
                          const hasGoogle = campaignData.platforms.includes('google');
                          const mode = hasGoogle && campaignData.bid_strategy === 'automatic' ? campaignData.google_bid_mode : null;
                          if (mode === 'target_roas') {
                            return `${t('campaigns.rmf.bidTargetRoas', 'ROAS alvo')}: ${campaignData.google_target_roas_pct ? `${campaignData.google_target_roas_pct}%` : '—'}`;
                          }
                          if (mode === 'portfolio') {
                            return `${t('campaigns.rmf.bidPortfolioShort', 'Estratégia de portfólio')}: ${campaignData.google_portfolio_strategy_name || campaignData.google_portfolio_strategy || '—'}`;
                          }
                          if (campaignData.bid_strategy === 'manual_cpc') {
                            return `${t('campaigns.new.manualCpc', 'Manual CPC')}${campaignData.max_cpc ? ` · R$ ${campaignData.max_cpc.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}`;
                          }
                          if (campaignData.bid_strategy === 'target_cpa') {
                            return `${t('campaigns.new.targetCpa', 'Target CPA')}${campaignData.target_cpa ? ` · R$ ${campaignData.target_cpa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}`;
                          }
                          return hasGoogle
                            ? t('campaigns.rmf.bidMaxConversions', 'Maximizar conversões (automático)')
                            : t('campaigns.new.automatic', 'Automatic');
                        })()}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{t('campaigns.steps.creatives', 'Creatives')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {t('campaigns.new.adsCount', '{{count}} ad(s)', { count: campaignData.creatives.length })}
                      </p>
                    </div>
                  </div>

                  {/* Platform Hierarchy Names */}
                  {(campaignData.ad_set_name || campaignData.campaign_group_name) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {campaignData.ad_set_name && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                          <p className="text-xs text-gray-500 mb-1">{t('campaigns.hierarchy.adSetName', 'Nome do Conjunto de An\u00FAncios')}</p>
                          <p className="font-medium text-gray-900 dark:text-white">{campaignData.ad_set_name}</p>
                        </div>
                      )}
                      {campaignData.campaign_group_name && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                          <p className="text-xs text-gray-500 mb-1">{t('campaigns.hierarchy.campaignGroupName', 'Nome do Grupo de Campanhas')}</p>
                          <p className="font-medium text-gray-900 dark:text-white">{campaignData.campaign_group_name}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pixel / Conversion Tracking Summary — list every selected pixel,
                      mark the primary with a star. */}
                  {Object.values(selectedPixels).some(list => Array.isArray(list) && list.length > 0) && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-2">{t('campaigns.new.pixelTitle', 'Conversion Tracking')}</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(selectedPixels).flatMap(([platform, list]) => {
                          if (!Array.isArray(list) || list.length === 0) return [];
                          const platformLabel = platform === 'google' ? 'Google' : platform === 'meta' ? 'Meta' : platform === 'meta_offline' ? 'Meta Offline' : 'LinkedIn';
                          return list.map(pixel => (
                            <span key={`${platform}-${pixel.id}`} className="px-3 py-1 bg-white dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                              <Crosshair className="w-3 h-3" />
                              {platformLabel}: {pixel.name}
                              {pixel.event && <span className="text-xs text-blue-600 dark:text-blue-400">[{pixel.event}]</span>}
                              {pixel.primary && <span className="text-xs font-bold text-amber-600">★</span>}
                            </span>
                          ));
                        })}
                      </div>
                    </div>
                  )}

                  {/* Audience Summary */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <p className="text-xs text-gray-500 mb-2">{t('campaigns.new.audience', 'Target Audience')}</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-white dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300">
                        {campaignData.audience.age_min}-{campaignData.audience.age_max} {t('campaigns.new.years', 'years')}
                      </span>
                      <span className="px-3 py-1 bg-white dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300">
                        {campaignData.audience.genders[0] === 'all' ? t('campaigns.new.allGenders', 'All genders') : campaignData.audience.genders[0] === 'male' ? t('campaigns.new.male', 'Male') : t('campaigns.new.female', 'Female')}
                      </span>
                      {(campaignData.audience.locations || []).map((loc, idx) => (
                        <span key={idx} className="px-3 py-1 bg-white dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300">
                          {loc.canonicalName || loc.name}{loc.radius > 0 ? ` (${loc.radius}km)` : ''}
                          {loc.geoTargetId && <span className="text-xs text-gray-400 ml-1">#{loc.geoTargetId}</span>}
                        </span>
                      ))}
                    </div>
                    {campaignData.platforms.includes('google') && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-2">{t('campaigns.rmf.languagesTitle', 'Idiomas do público')}</p>
                        <div className="flex flex-wrap gap-2">
                          {(campaignData.audience.languages || []).length > 0
                            ? (campaignData.audience.languages || []).map(id => (
                                <span key={id} className="px-3 py-1 bg-white dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300">
                                  {googleLanguageLabel(id)}
                                </span>
                              ))
                            : (
                                <span className="px-3 py-1 bg-white dark:bg-gray-700 rounded-full text-sm text-gray-500">
                                  {t('campaigns.rmf.allLanguages', 'Todos os idiomas')}
                                </span>
                              )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Creatives Summary - Per Platform */}
                  <div>
                    <p className="text-xs text-gray-500 mb-3">{t('campaigns.new.createdAds', 'Created Ads')}</p>
                    <div className="space-y-4">
                      {Object.entries(campaignData.platformCreatives).map(([platform, creativesArr]) => {
                        const spec = PLATFORM_SPECS[platform];
                        if (!spec || !creativesArr || creativesArr.length === 0) return null;
                        const PlatformIcon = spec.icon;

                        return (
                          <div key={platform} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                              <PlatformIcon className="w-5 h-5" />
                              <span className="font-medium text-gray-900 dark:text-white">{spec.name}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                · {creativesArr.length} {creativesArr.length === 1 ? t('campaigns.new.adLabel', 'Anúncio').toLowerCase() : t('campaigns.new.adsLabel', 'anúncios')}
                              </span>
                            </div>
                            <div className="space-y-3">
                              {creativesArr.map((creative, cIdx) => (
                                <div key={cIdx} className="flex items-start gap-3 p-2 rounded-lg bg-white dark:bg-gray-900/30">
                                  {creativesArr.length > 1 && (
                                    <span className="text-xs text-gray-400 font-medium mt-1 flex-shrink-0">#{cIdx + 1}</span>
                                  )}
                                  <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                                    {creative.imageUrl ? (
                                      <img src={creative.imageUrl} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Image className="w-6 h-6 text-gray-400" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                      {creative.headlines.find(h => h.trim() !== '') || t('campaigns.new.noHeadline', 'No headline')}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate mt-1">
                                      {creative.descriptions.find(d => d.trim() !== '') || t('common.noDescription', 'No description')}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs text-primary-500">
                                        {(() => {
                                          const label = spec.ctas.find(c => c.value === creative.ctaText)?.label;
                                          return label ? t(label) : creative.ctaText;
                                        })()}
                                      </span>
                                      {creative.destinationUrl && (
                                        <span className="text-xs text-gray-400 truncate max-w-[150px]">
                                          {creative.destinationUrl}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {campaignData.description && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('campaigns.new.campaignDescription', 'Campaign Description')}</p>
                      <p className="text-gray-700 dark:text-gray-300">{campaignData.description}</p>
                    </div>
                  )}

                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      <strong>{t('campaigns.new.note', 'Note')}:</strong> {t('campaigns.new.draftNote', 'The campaign will be created as a draft. You can review and activate it later.')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-500">{t('campaigns.new.loadingCampaign', 'Loading campaign...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 min-w-0 max-w-full overflow-x-clip">
      {/* Header — uma linha só no celular: voltar (ícone) | título | IA | fechar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2.5 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={handleBack}
              aria-label={t('common.back', 'Back')}
              className="flex items-center gap-2 p-1.5 -ml-1.5 sm:p-0 sm:ml-0 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{t('common.back', 'Back')}</span>
            </button>

            <h1 className="flex-1 sm:flex-none min-w-0 truncate text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              {isEditMode ? t('campaigns.new.editTitle', 'Edit Campaign') : t('campaigns.new.title', 'New Campaign')}
            </h1>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {!isEditMode && currentStep === 0 && (
                <button
                  onClick={() => setShowAIModal(true)}
                  title={t('campaigns.createWithAI', 'Create with AI')}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm whitespace-nowrap bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all"
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="sm:hidden">{t('campaigns.createWithAIShort', 'Com IA')}</span>
                  <span className="hidden sm:inline">{t('campaigns.createWithAI', 'Create with AI')}</span>
                </button>
              )}
              <button
                onClick={() => navigate('/campaigns')}
                aria-label={t('common.close', 'Fechar')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps (Creation) / Section Tabs (Edit) */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {isEditMode ? (
            // Edit mode: horizontal scrollable tabs, click any to jump
            <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-hide">
              {STEPS.map((step, index) => (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => setCurrentStep(index)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    index === currentStep
                      ? 'bg-primary-500 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                    index === currentStep ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'
                  }`}>{index + 1}</span>
                  <span>{t(step.title)}</span>
                </button>
              ))}
            </div>
          ) : (
            // Creation mode: keep the linear progress indicator
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => (
                <React.Fragment key={step.id}>
                  <button
                    type="button"
                    onClick={() => { if (index <= currentStep) setCurrentStep(index); }}
                    disabled={index > currentStep}
                    className={`flex items-center gap-2 ${index <= currentStep ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed opacity-60'} transition-opacity`}
                  >
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-medium transition-all text-xs sm:text-sm ${
                      index < currentStep
                        ? 'bg-green-500 text-white'
                        : index === currentStep
                        ? 'bg-primary-500 text-white ring-4 ring-primary-100 dark:ring-primary-900/30'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}>
                      {index < currentStep ? (
                        <Check className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div className="hidden lg:block">
                      <p className={`text-xs font-medium whitespace-nowrap ${
                        index <= currentStep ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {t(step.title)}
                      </p>
                    </div>
                  </button>
                  {index < STEPS.length - 1 && (
                    <div className={`flex-1 h-1 mx-1 sm:mx-2 md:mx-4 rounded-full ${
                      index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-8 pb-28">
        {renderStepContent()}
      </div>

      {/* Footer Navigation — barra fixa embaixo; no celular os botões dividem a largura
          (lg:left-72 = não fica atrás da barra lateral no desktop) */}
      <div
        className="fixed bottom-0 left-0 right-0 lg:left-72 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-30 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 sm:py-4 flex items-center justify-between gap-2">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 sm:px-6 py-2.5 text-sm sm:text-base whitespace-nowrap rounded-lg border border-gray-300 dark:border-gray-600 sm:border-transparent sm:dark:border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4 sm:hidden" />
            {t('campaigns.new.back', 'Back')}
          </button>

          <div className="flex-[2] sm:flex-none flex items-center gap-2 sm:gap-4 min-w-0 [&>button]:flex-1 sm:[&>button]:flex-none [&>button]:whitespace-nowrap [&>button]:py-2.5 sm:[&>button]:py-2">
            <span className="hidden sm:inline text-sm text-gray-500">
              {isEditMode
                ? t('campaigns.new.sectionOf', 'Section {{current}} of {{total}}', { current: currentStep + 1, total: STEPS.length })
                : t('campaigns.new.stepOf', 'Step {{current}} of {{total}}', { current: currentStep + 1, total: STEPS.length })}
            </span>

            {isEditMode ? (
              <>
                {currentStep < STEPS.length - 1 && (
                  <Button
                    variant="secondary"
                    onClick={handleNext}
                    disabled={!canProceed()}
                    aria-label={t('campaigns.new.next', 'Continue')}
                    rightIcon={<ArrowRight className="w-4 h-4" />}
                  >
                    <span className="hidden sm:inline">{t('campaigns.new.next', 'Continue')}</span>
                  </Button>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  leftIcon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                >
                  <span className="sm:hidden">{isSubmitting ? t('campaigns.new.updating', 'Saving...') : t('common.save', 'Salvar')}</span>
                  <span className="hidden sm:inline">{isSubmitting ? t('campaigns.new.updating', 'Saving...') : t('campaigns.new.update', 'Save Changes')}</span>
                </Button>
              </>
            ) : currentStep === STEPS.length - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                leftIcon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              >
                {isSubmitting ? t('campaigns.new.creating', 'Creating...') : t('campaigns.new.create', 'Create Campaign')}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                rightIcon={<ArrowRight className="w-4 h-4" />}
              >
                {t('campaigns.new.next', 'Continue')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* AI Campaign Generation Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAIModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('campaigns.ai.title', 'Create Campaign with AI')}
                </h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('campaigns.ai.description', 'Describe your goal and AI will create an optimized campaign for you')}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Platform Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('campaigns.ai.selectPlatforms', 'Which platforms do you want to advertise on?')}
                </label>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setCampaignData(prev => ({
                        ...prev,
                        platforms: prev.platforms.includes('google')
                          ? prev.platforms.filter(p => p !== 'google')
                          : [...prev.platforms, 'google']
                      }));
                    }}
                    disabled={!isPlatformConnected('google')}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      !isPlatformConnected('google')
                        ? 'border-gray-200 dark:border-gray-700 opacity-40 cursor-not-allowed'
                        : campaignData.platforms.includes('google')
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                    }`}
                    title={!isPlatformConnected('google') ? t('campaigns.notConnected', 'Account not connected') : ''}
                  >
                    <GoogleAdsIcon className="w-6 h-6 mx-auto mb-1" />
                    <span className="text-xs font-medium">Google</span>
                    {campaignData.platforms.includes('google') && isPlatformConnected('google') && (
                      <CheckCircle className="w-4 h-4 text-blue-500 mx-auto mt-1" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCampaignData(prev => ({
                        ...prev,
                        platforms: prev.platforms.includes('meta')
                          ? prev.platforms.filter(p => p !== 'meta')
                          : [...prev.platforms, 'meta']
                      }));
                    }}
                    disabled={!isPlatformConnected('meta')}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      !isPlatformConnected('meta')
                        ? 'border-gray-200 dark:border-gray-700 opacity-40 cursor-not-allowed'
                        : campaignData.platforms.includes('meta')
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                    }`}
                    title={!isPlatformConnected('meta') ? t('campaigns.notConnected', 'Account not connected') : ''}
                  >
                    <MetaAdsIcon className="w-6 h-6 mx-auto mb-1" />
                    <span className="text-xs font-medium">Meta</span>
                    {campaignData.platforms.includes('meta') && isPlatformConnected('meta') && (
                      <CheckCircle className="w-4 h-4 text-blue-500 mx-auto mt-1" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCampaignData(prev => ({
                        ...prev,
                        platforms: prev.platforms.includes('linkedin')
                          ? prev.platforms.filter(p => p !== 'linkedin')
                          : [...prev.platforms, 'linkedin']
                      }));
                    }}
                    disabled={!isPlatformConnected('linkedin')}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      !isPlatformConnected('linkedin')
                        ? 'border-gray-200 dark:border-gray-700 opacity-40 cursor-not-allowed'
                        : campaignData.platforms.includes('linkedin')
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                    }`}
                    title={!isPlatformConnected('linkedin') ? t('campaigns.notConnected', 'Account not connected') : ''}
                  >
                    <LinkedInAdsIcon className="w-6 h-6 mx-auto mb-1" />
                    <span className="text-xs font-medium">LinkedIn</span>
                    {campaignData.platforms.includes('linkedin') && isPlatformConnected('linkedin') && (
                      <CheckCircle className="w-4 h-4 text-blue-500 mx-auto mt-1" />
                    )}
                  </button>
                </div>
                {campaignData.platforms.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    {t('campaigns.ai.selectAtLeastOne', 'Select at least one platform')}
                  </p>
                )}
              </div>

              {/* Brand context banner */}
              {campaignBrandProfileLoaded && (
                campaignBrandProfile?.business_name ? (
                  <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-purple-50/70 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
                      <Building2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-100">
                        {t('social.brandContext.title', 'Gerando com base em')}{' '}
                        <strong className="font-semibold">{campaignBrandProfile.business_name}</strong>
                        {campaignBrandProfile.brand_voice && (
                          <span className="text-gray-500 dark:text-gray-400"> · {campaignBrandProfile.brand_voice}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {aiIgnorePattern
                          ? t('social.brandContext.ignoringPattern', 'Ignorando o padrão dos posts anteriores neste post.')
                          : t('social.brandContext.followingPatternCampaign', 'A IA vai considerar seu perfil e padrões anteriores ao montar a campanha.')}
                      </p>
                    </div>
                    <RouterLink
                      to="/settings/business-profile"
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40 transition"
                    >
                      <SettingsIcon className="w-3.5 h-3.5" />
                      {t('social.brandContext.edit', 'Editar')}
                    </RouterLink>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50/70 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center text-white shrink-0">
                      <Building2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-100">
                        {t('social.brandContext.missing', 'Conte à IA sobre o seu negócio para gerar posts melhores.')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {t('social.brandContext.missingHintCampaign', 'Sem o perfil, a campanha será gerada só com o prompt atual.')}
                      </p>
                    </div>
                    <RouterLink
                      to="/settings/business-profile"
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition"
                    >
                      <SettingsIcon className="w-3.5 h-3.5" />
                      {t('social.brandContext.configure', 'Configurar')}
                    </RouterLink>
                  </div>
                )
              )}

              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('campaigns.ai.whatGoal', 'What do you want to achieve?')}
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAIPrompt(e.target.value)}
                  placeholder={t('campaigns.ai.placeholder', 'E.g.: I want to increase sales for my clothing e-commerce targeting women aged 25-35 interested in sustainable fashion. My budget is $100/day.')}
                  className="input min-h-[100px]"
                />
              </div>

              {/* Variation controls — only when a brand profile exists */}
              {campaignBrandProfile?.business_name && (
                <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-white/60 dark:bg-gray-900/40 p-3 space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aiIgnorePattern}
                      onChange={(e) => setAiIgnorePattern(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {t('social.variation.ignore', 'Fugir do padrão dos posts anteriores')}
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('social.variation.ignoreHintCampaign', 'Ignorar histórico de posts/campanhas nesta geração.')}
                      </p>
                    </div>
                  </label>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t('social.variation.direction', 'Direção desta variação (opcional)')}
                    </label>
                    <input
                      type="text"
                      value={aiVariationDirection}
                      onChange={(e) => setAiVariationDirection(e.target.value)}
                      placeholder={t('social.variation.directionPlaceholderCampaign', 'Ex.: foco em remarketing; mais agressivo; tom mais formal dessa vez') as string}
                      className="w-full px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      maxLength={400}
                    />
                  </div>
                </div>
              )}

              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">
                  <strong>{t('campaigns.ai.tips', 'Tips for better results:')}</strong>
                </p>
                <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                  <li>• {t('campaigns.ai.tip1', 'Mention your product/service')}</li>
                  <li>• {t('campaigns.ai.tip2', 'Describe your target audience')}</li>
                  <li>• {t('campaigns.ai.tip3', 'Specify your goal (sales, leads, traffic)')}</li>
                </ul>
              </div>
            </div>

            <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => setShowAIModal(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                className="flex-1"
                onClick={handleGenerateWithAI}
                disabled={isGeneratingAI || !aiPrompt.trim() || campaignData.platforms.length === 0}
                leftIcon={isGeneratingAI ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              >
                {isGeneratingAI ? t('common.generating', 'Generating...') : t('campaigns.ai.generate', 'Generate Campaign')}
              </Button>
            </div>
            <AiCostBadge feature="ai-generation" action="campaign-generate" className="mt-2" />
          </div>
        </div>
      )}

      {/* Creative Library Modal */}
      <CreativeLibraryModal
        isOpen={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        onSelect={handleLoadLibraryToPlatform}
        platforms={activePlatformTab ? [activePlatformTab] : campaignData.platforms}
        currentPlatform={activePlatformTab || undefined}
      />

      {/* Save Creative to Library Modal */}
      {saveModalPlatform && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !savingCreative && setSaveModalPlatform(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Save className="w-5 h-5" />
                {t('campaigns.new.saveToLibraryTitle', 'Salvar na Biblioteca')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('campaigns.new.saveToLibrarySubtitle', 'Reutilize esse creative em campanhas futuras.')}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('campaigns.new.creativeName', 'Nome do creative')} *
                </label>
                <input
                  type="text"
                  value={saveModalName}
                  onChange={(e) => setSaveModalName(e.target.value.substring(0, 100))}
                  placeholder={t('campaigns.new.creativeNamePlaceholder', 'Ex: Black Friday Lead Gen 2026')}
                  maxLength={100}
                  className="input"
                  autoFocus
                  disabled={savingCreative}
                />
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    {t('campaigns.new.creativeNameHelp', 'Use um nome descritivo pra encontrar depois.')}
                  </p>
                  <span className="text-xs text-gray-400">{saveModalName.length}/100</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('campaigns.new.creativeTags', 'Tags (opcional)')}
                </label>
                <CommaSeparatedInput
                  values={saveModalTags}
                  onChange={(tags) => setSaveModalTags(tags)}
                  placeholder={t('campaigns.new.creativeTagsPlaceholder', 'lead, blackfriday, b2b...')}
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('campaigns.new.creativeTagsHelp', 'Separe com vírgula. Útil pra filtrar a biblioteca.')}
                </p>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg flex items-center gap-2">
                <Info className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  {t('campaigns.new.savingForPlatform', 'Salvando como creative de')} <strong className="capitalize">{saveModalPlatform}</strong>
                </span>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveModalPlatform(null)}
                disabled={savingCreative}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg disabled:opacity-50"
              >
                {t('common.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                onClick={handleConfirmSaveToLibrary}
                disabled={!saveModalName.trim() || savingCreative}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
              >
                {savingCreative ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {savingCreative ? t('campaigns.new.saving', 'Salvando...') : t('campaigns.new.saveToLibrary', 'Salvar na Biblioteca')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audience Create/Edit Modal */}
      {isAudienceModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 sm:px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingAudience ? t('audiences.edit', 'Editar Audiência') : t('audiences.createTitle', 'Nova Audiência')}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setIsAudienceModalOpen(false);
                  resetAudienceForm();
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('audiences.name', 'Nome')} *
                </label>
                <input
                  type="text"
                  value={audienceFormData.name}
                  onChange={(e) => setAudienceFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('audiences.namePlaceholder', 'Ex: Visitantes Black Friday')}
                  className="input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('audiences.description', 'Descrição')}
                </label>
                <textarea
                  value={audienceFormData.description}
                  onChange={(e) => setAudienceFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('audiences.descriptionPlaceholder', 'Descreva o propósito desta audiência...')}
                  className="input"
                  rows={2}
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('audiences.type', 'Tipo')} *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { value: 'website_visitors', source: 'pixel_events', label: t('audiences.types.pixelTracking', 'Audiência por Pixel'), icon: <Crosshair className="w-5 h-5" />, desc: t('audiences.typesDesc.pixelTracking', 'Segmente pelo fluxo do site via pixel tracking') },
                    { value: 'website_visitors', source: 'landing_page', label: t('audiences.types.websiteVisitors', 'Visitantes do Site'), icon: <Globe className="w-5 h-5" />, desc: t('audiences.typesDesc.websiteVisitors', 'Pessoas que visitaram suas landing pages') },
                    { value: 'leads', source: 'landing_page', label: t('audiences.types.leads', 'Leads'), icon: <UserPlus className="w-5 h-5" />, desc: t('audiences.typesDesc.leads', 'Leads capturados em formulários') },
                    { value: 'converters', source: 'landing_page', label: t('audiences.types.converters', 'Convertidos'), icon: <Target className="w-5 h-5" />, desc: t('audiences.typesDesc.converters', 'Pessoas que já converteram') },
                    { value: 'customer_list', source: 'landing_page', label: t('audiences.types.customerList', 'Lista de Clientes'), icon: <Upload className="w-5 h-5" />, desc: t('audiences.typesDesc.customerList', 'Upload de lista de emails/telefones') }
                  ].map((type) => {
                    const isSelected = audienceFormData.type === type.value && audienceFormData.source === type.source;
                    return (
                    <button
                      key={`${type.value}-${type.source}`}
                      type="button"
                      onClick={() => setAudienceFormData(prev => ({
                        ...prev,
                        type: type.value as Audience['type'],
                        source: type.source as 'landing_page' | 'pixel_events',
                        pixel_event_types: type.source !== 'pixel_events' ? [] : prev.pixel_event_types
                      }))}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        isSelected
                          ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={isSelected ? 'text-primary-600' : 'text-gray-500'}>
                          {type.icon}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white text-sm">{type.label}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{type.desc}</p>
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* Pixel Event Types - shown when source is pixel_events */}
              {audienceFormData.source === 'pixel_events' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <Crosshair className="inline w-4 h-4 mr-1" />
                    {t('audiences.pixelEventTypes', 'Eventos do Pixel')} *
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {t('audiences.pixelEventTypesDesc', 'Selecione quais ações do site definem esta audiência')}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {[
                      { value: 'page_view', label: t('audiences.events.pageView', 'Visualização de Página') },
                      { value: 'scroll_depth', label: t('audiences.events.scrollDepth', 'Profundidade de Scroll') },
                      { value: 'time_on_page', label: t('audiences.events.timeOnPage', 'Tempo na Página') },
                      { value: 'click', label: t('audiences.events.click', 'Cliques') },
                      { value: 'form_start', label: t('audiences.events.formStart', 'Início de Formulário') },
                      { value: 'form_submit', label: t('audiences.events.formSubmit', 'Envio de Formulário') },
                      { value: 'button_click', label: t('audiences.events.buttonClick', 'Clique em Botão') },
                      { value: 'link_click', label: t('audiences.events.linkClick', 'Clique em Link') },
                      { value: 'video_play', label: t('audiences.events.videoPlay', 'Play em Vídeo') },
                      { value: 'video_complete', label: t('audiences.events.videoComplete', 'Vídeo Completo') },
                      { value: 'conversion', label: t('audiences.events.conversion', 'Conversão') },
                      { value: 'add_to_cart', label: t('audiences.events.addToCart', 'Adição ao Carrinho') },
                    ].map((event) => (
                      <label
                        key={event.value}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                          audienceFormData.pixel_event_types.includes(event.value)
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={audienceFormData.pixel_event_types.includes(event.value)}
                          onChange={() => {
                            setAudienceFormData(prev => ({
                              ...prev,
                              pixel_event_types: prev.pixel_event_types.includes(event.value)
                                ? prev.pixel_event_types.filter(e => e !== event.value)
                                : [...prev.pixel_event_types, event.value]
                            }));
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-xs text-gray-700 dark:text-gray-300">{event.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Landing Pages / External Sites Selection */}
              {(audienceFormData.type === 'website_visitors' || audienceFormData.type === 'leads' || audienceFormData.type === 'converters') && (() => {
                const showExternalSites = audienceFormData.type === 'website_visitors';
                const filteredPages = showExternalSites
                  ? landingPagesForAudience
                  : landingPagesForAudience.filter(lp => !lp.is_external);

                return (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {showExternalSites
                      ? t('audiences.selectSources', 'Selecione as Landing Pages / Sites')
                      : t('audiences.selectLandingPages', 'Selecione as Landing Pages')} *
                  </label>
                  {filteredPages.length === 0 ? (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center space-y-2">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        {showExternalSites
                          ? t('audiences.noSources', 'Nenhuma landing page publicada ou site externo cadastrado.')
                          : t('audiences.noLandingPages', 'Nenhuma landing page publicada. Publique uma landing page primeiro.')}
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                      {filteredPages.map((lp) => (
                        <label
                          key={lp.id}
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                            audienceFormData.landing_page_ids.includes(lp.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={audienceFormData.landing_page_ids.includes(lp.id)}
                            onChange={() => toggleAudienceLandingPage(lp.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{lp.name}</p>
                              {lp.is_external && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 text-[10px] font-medium rounded">
                                  <ExternalLink className="w-3 h-3" />
                                  Externo
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{lp.published_url}</p>
                          </div>
                          <div className="text-right text-xs">
                            <p className="text-gray-600 dark:text-gray-400">{lp.views} visitas</p>
                            <p className="text-green-600">{lp.conversions} conversões</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Inline external website registration for website_visitors */}
                  {showExternalSites && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-2">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        <Link className="w-3 h-3" />
                        {t('audiences.addExternalSite', 'Cadastrar Site Externo')}
                      </p>
                      <div className="flex flex-wrap sm:flex-nowrap gap-2">
                        <Input
                          placeholder={t('audiences.externalName', 'Nome do site')}
                          value={externalSiteForm.name}
                          onChange={(e) => setExternalSiteForm(prev => ({ ...prev, name: e.target.value }))}
                          className="text-sm"
                        />
                        <Input
                          placeholder={t('audiences.externalUrl', 'https://meusite.com.br')}
                          value={externalSiteForm.url}
                          onChange={(e) => setExternalSiteForm(prev => ({ ...prev, url: e.target.value }))}
                          className="text-sm"
                        />
                        <Button
                          size="sm"
                          onClick={handleAddExternalSiteInline}
                          isLoading={isAddingExternalSite}
                          leftIcon={<Plus className="w-3 h-3" />}
                        >
                          {t('common.add', 'Adicionar')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}

              {/* CSV Upload for Customer List */}
              {audienceFormData.type === 'customer_list' && !editingAudience && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('audiences.uploadCSV', 'Upload de Planilha (CSV)')} *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                    <input
                      type="file"
                      accept=".csv,.txt"
                      onChange={handleCsvFileChange}
                      className="hidden"
                      id="csv-upload-campaign"
                    />
                    <label htmlFor="csv-upload-campaign" className="cursor-pointer">
                      <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                      {csvFile ? (
                        <p className="text-sm font-medium text-primary-600">{csvFile.name}</p>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('audiences.clickToUpload', 'Clique para selecionar arquivo')}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">CSV ou TXT</p>
                        </>
                      )}
                    </label>
                  </div>

                  {/* CSV Format Help */}
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-gray-700 dark:text-gray-300">
                        {t('audiences.csvFormat', 'Formato esperado:')}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          const csvContent = `email;phone;first_name;last_name;country
joao@exemplo.com;11999998888;João;Silva;BR
maria@exemplo.com;21988887777;Maria;Santos;BR
pedro@exemplo.com;31977776666;Pedro;Oliveira;BR
ana@exemplo.com;41966665555;Ana;Costa;BR
carlos@exemplo.com;51955554444;Carlos;Souza;BR`;
                          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = 'exemplo_audiencia.csv';
                          link.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                      >
                        <FileText className="w-3 h-3" />
                        {t('audiences.downloadExample', 'Baixar exemplo')}
                      </button>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                      {t('audiences.csvColumns', 'Colunas: email, phone, first_name, last_name, country')}
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                      {t('audiences.csvExample', 'Ex: email;telefone;nome;sobrenome')}
                    </p>
                  </div>

                  {/* CSV Preview */}
                  {csvPreview.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('audiences.preview', 'Preview')} ({csvPreview.length} {t('audiences.rows', 'linhas')}):
                      </p>
                      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              {csvPreview[0]?.email !== undefined && <th className="px-3 py-2 text-left">Email</th>}
                              {csvPreview[0]?.phone !== undefined && <th className="px-3 py-2 text-left">Telefone</th>}
                              {csvPreview[0]?.first_name !== undefined && <th className="px-3 py-2 text-left">Nome</th>}
                              {csvPreview[0]?.last_name !== undefined && <th className="px-3 py-2 text-left">Sobrenome</th>}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {csvPreview.map((row, idx) => (
                              <tr key={idx}>
                                {row.email !== undefined && <td className="px-3 py-2">{row.email}</td>}
                                {row.phone !== undefined && <td className="px-3 py-2">{row.phone}</td>}
                                {row.first_name !== undefined && <td className="px-3 py-2">{row.first_name}</td>}
                                {row.last_name !== undefined && <td className="px-3 py-2">{row.last_name}</td>}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Lookback Days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('audiences.lookbackDays', 'Período de Lookback')}
                </label>
                <select
                  value={audienceFormData.lookback_days}
                  onChange={(e) => setAudienceFormData(prev => ({ ...prev, lookback_days: parseInt(e.target.value) }))}
                  className="input"
                >
                  <option value={7}>{t('audiences.days7', 'Últimos 7 dias')}</option>
                  <option value={14}>{t('audiences.days14', 'Últimos 14 dias')}</option>
                  <option value={30}>{t('audiences.days30', 'Últimos 30 dias')}</option>
                  <option value={60}>{t('audiences.days60', 'Últimos 60 dias')}</option>
                  <option value={90}>{t('audiences.days90', 'Últimos 90 dias')}</option>
                  <option value={180}>{t('audiences.days180', 'Últimos 180 dias')}</option>
                </select>
              </div>

              {/* Dynamic / Static Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('audiences.updateMode', 'Modo de Atualização')}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setAudienceFormData(prev => ({ ...prev, is_dynamic: true }))}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      audienceFormData.is_dynamic
                        ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <RefreshCw className={`w-4 h-4 ${audienceFormData.is_dynamic ? 'text-primary-600' : 'text-gray-500'}`} />
                      <span className="font-medium text-gray-900 dark:text-white text-sm">
                        {t('audiences.dynamic', 'Dinâmica')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('audiences.dynamicDesc', 'Atualiza automaticamente com novos dados')}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAudienceFormData(prev => ({ ...prev, is_dynamic: false }))}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      !audienceFormData.is_dynamic
                        ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Save className={`w-4 h-4 ${!audienceFormData.is_dynamic ? 'text-primary-600' : 'text-gray-500'}`} />
                      <span className="font-medium text-gray-900 dark:text-white text-sm">
                        {t('audiences.static', 'Estática')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('audiences.staticDesc', 'Snapshot fixo, não atualiza automaticamente')}
                    </p>
                  </button>
                </div>
              </div>

              {/* Estimated Size */}
              {estimatedAudienceSize !== null && audienceFormData.type !== 'customer_list' && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900 dark:text-blue-100">
                      {t('audiences.estimatedSize', 'Tamanho Estimado:')} {estimatedAudienceSize.toLocaleString()} {t('audiences.users', 'usuários')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-3 sm:px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsAudienceModalOpen(false);
                  resetAudienceForm();
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                {t('common.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                onClick={handleSaveAudience}
                disabled={audienceSubmitting}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {audienceSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingAudience ? t('common.save', 'Salvar') : t('audiences.create', 'Criar Audiência')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Pixel Install Snippet Modal */}
      {snippetModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSnippetModal(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('campaigns.new.installPixelTitle', 'Código de instalação')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {snippetModal.pixelName} <span className="capitalize">({snippetModal.platform})</span> · ID: {snippetModal.pixelId}
              </p>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              {loadingSnippet ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                </div>
              ) : snippetData ? (
                <>
                  {/* Instructions */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-300 flex items-start gap-2">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{snippetData.instructions}</span>
                  </div>

                  {/* Note about landing pages */}
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-sm text-green-800 dark:text-green-300 flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      {t('campaigns.new.pixelLandingNote', 'Se você usar nossas landing pages, basta selecionar este pixel nelas — o código será injetado automaticamente, sem precisar copiar.')}
                    </span>
                  </div>

                  {/* Main HTML snippet */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-semibold text-gray-900 dark:text-white">
                        {snippetData.conversionSnippet
                          ? t('campaigns.new.globalSiteTag', 'Tag global do site (em todas as páginas)')
                          : t('campaigns.new.pixelMainSnippet', 'Código principal (em todas as páginas)')}
                      </label>
                      <button
                        type="button"
                        onClick={() => handleCopySnippet(snippetData.html, t('campaigns.new.snippetMainLabel', 'Código principal'))}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg"
                      >
                        <Save className="w-3.5 h-3.5" />
                        {t('common.copy', 'Copiar')}
                      </button>
                    </div>
                    <pre className="text-xs bg-gray-900 dark:bg-gray-950 text-gray-100 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all">
{snippetData.html}
                    </pre>
                  </div>

                  {/* Conversion event snippet (Google) */}
                  {snippetData.conversionSnippet && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-semibold text-gray-900 dark:text-white">
                          {t('campaigns.new.eventSnippet', 'Snippet de evento (página de conversão)')}
                        </label>
                        <button
                          type="button"
                          onClick={() => handleCopySnippet(snippetData.conversionSnippet!, t('campaigns.new.snippetEventLabel', 'Snippet de evento'))}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg"
                        >
                          <Save className="w-3.5 h-3.5" />
                          {t('common.copy', 'Copiar')}
                        </button>
                      </div>
                      <pre className="text-xs bg-gray-900 dark:bg-gray-950 text-gray-100 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all">
{snippetData.conversionSnippet}
                      </pre>
                    </div>
                  )}

                  {/* Noscript fallback */}
                  {snippetData.noscript && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-semibold text-gray-900 dark:text-white">
                          {t('campaigns.new.noscriptFallback', 'Fallback <noscript> (após o <body>)')}
                        </label>
                        <button
                          type="button"
                          onClick={() => handleCopySnippet(snippetData.noscript!, t('campaigns.new.snippetNoscriptLabel', 'Noscript'))}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg"
                        >
                          <Save className="w-3.5 h-3.5" />
                          {t('common.copy', 'Copiar')}
                        </button>
                      </div>
                      <pre className="text-xs bg-gray-900 dark:bg-gray-950 text-gray-100 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all">
{snippetData.noscript}
                      </pre>
                    </div>
                  )}

                  {/* Docs link */}
                  {snippetData.docsUrl && (
                    <a
                      href={snippetData.docsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      {t('campaigns.new.officialDocs', 'Documentação oficial')}
                    </a>
                  )}
                </>
              ) : null}
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                type="button"
                onClick={() => setSnippetModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                {t('common.close', 'Fechar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Pixel Modal */}
      {createPixelPlatform && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !creatingPixel && setCreatePixelPlatform(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                {t('campaigns.new.createPixelTitle', 'Criar pixel no {{platform}}', { platform: createPixelPlatform === 'meta' ? 'Meta' : createPixelPlatform === 'google' ? 'Google Ads' : 'LinkedIn' })}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('campaigns.new.createPixelDescription', 'O pixel será criado na plataforma e ficará disponível pra usar em campanhas e landing pages.')}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('campaigns.new.pixelName', 'Nome do pixel')} *
                </label>
                <input
                  type="text"
                  value={newPixelName}
                  onChange={(e) => setNewPixelName(e.target.value)}
                  placeholder={t('campaigns.new.pixelNamePlaceholder', 'Ex: Pixel Principal, Conversão Leads...')}
                  maxLength={100}
                  className="input"
                  autoFocus
                  disabled={creatingPixel}
                />
              </div>
              {createPixelPlatform !== 'meta' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('campaigns.new.pixelCategory', 'Tipo de conversão')}
                  </label>
                  <select
                    value={newPixelCategory}
                    onChange={(e) => setNewPixelCategory(e.target.value)}
                    className="input"
                    disabled={creatingPixel}
                  >
                    <option value="DEFAULT">{t('campaigns.new.pixelCat.default', 'Padrão')}</option>
                    <option value="LEAD">{t('campaigns.new.pixelCat.lead', 'Lead')}</option>
                    <option value="PURCHASE">{t('campaigns.new.pixelCat.purchase', 'Compra (Purchase)')}</option>
                    <option value="SIGNUP">{t('campaigns.new.pixelCat.signup', 'Cadastro (Sign-up)')}</option>
                    <option value="DOWNLOAD">{t('campaigns.new.pixelCat.download', 'Download')}</option>
                    <option value="ADD_TO_CART">{t('campaigns.new.pixelCat.addToCart', 'Adicionar ao Carrinho')}</option>
                    {createPixelPlatform === 'google' && (
                      <>
                        <option value="CONTACT">{t('campaigns.new.pixelCat.contact', 'Contato')}</option>
                        <option value="BOOK_APPOINTMENT">{t('campaigns.new.pixelCat.bookAppt', 'Agendamento')}</option>
                        <option value="PAGE_VIEW">{t('campaigns.new.pixelCat.pageView', 'Visualização de Página')}</option>
                        <option value="SUBMIT_LEAD_FORM">{t('campaigns.new.pixelCat.submitLead', 'Enviar formulário de Lead')}</option>
                      </>
                    )}
                  </select>
                </div>
              )}
              <div className="text-xs text-gray-500 dark:text-gray-400 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="font-medium mb-1">{t('campaigns.new.pixelCreateNote', '⚠️ Atenção:')}</p>
                <p>{t('campaigns.new.pixelCreateWarning', 'Criar o pixel apenas registra o rastreador na plataforma. Pra começar a coletar eventos, instale o código em suas páginas — se usar nossas landing pages, basta selecionar este pixel nelas.')}</p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreatePixelPlatform(null)}
                disabled={creatingPixel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg disabled:opacity-50"
              >
                {t('common.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                onClick={handleCreatePixel}
                disabled={!newPixelName.trim() || creatingPixel}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {creatingPixel && <Loader2 className="w-4 h-4 animate-spin" />}
                {creatingPixel ? t('campaigns.new.creating', 'Criando...') : t('campaigns.new.createPixel', 'Criar pixel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Editor Modal for campaign creatives */}
      {editingImagePlatform && (
        <ImageEditorModal
          isOpen={!!editingImagePlatform}
          onClose={() => setEditingImagePlatform(null)}
          imageUrl={getActiveCreative(editingImagePlatform)?.imageUrl || undefined}
          onSave={(newUrl) => {
            updatePlatformCreative(editingImagePlatform, { imageUrl: newUrl, imageFile: null, isGeneratingImage: false });
            setEditingImagePlatform(null);
          }}
        />
      )}
    </div>
  );
};

export default NewCampaignPage;
